import { validateWorkspaceInput } from "../workspace-input.js";
import type {
  RemovedWorkspaceMachine,
  Workspace,
  WorkspaceSettings,
  ExternalWorkspacePayload,
  Channel,
  Message,
  Attachment,
  Task,
  TaskPriority,
  TaskSearchResult,
  PageSearchResult,
  TaskState,
  TaskLabel,
  Cycle,
  Page,
  PageAgentRun,
  Module,
  TaskView,
  TaskActivity,
  Membership,
  TypingEvent,
  ReactionWire,
  WorkspaceMember,
  WorkSession,
  ExternalParticipant,
  FormerParticipant,
  ScheduledMessage,
  PromptTemplate,
  ChannelTaskActivityConfig,
  SlackIdentityOrg,
  SlackImportProgress,
  SlackSyncAddResponse,
  SlackSyncChannelsResponse,
  SlackSyncImportHistoryResponse,
  SlackSyncMemberChannel,
  SlackSyncRemoveChannelsResponse,
  SlackSyncStatusResponse,
  SignedCardAction,
} from "./types.js";
// CYBORG-1231: structured payload on cybo_moved / cybo_handed_off activity rows (absent on old rows).
export interface CyboTransferDetail {
  cyboIds: string[];
  cyboNames: string[];
  count: number;
  actorName: string;
  machineName: string;
  fromUserName?: string;
  /** cybo_moved sent to the agent's owner, not the machine's host. */
  forOwner?: boolean;
  /** forOwner rows: who the agent belongs to now. */
  newOwnerName?: string;
}

/** system_notice's detail: only the page the notice opens, when the server named one. */
export interface NoticeDetail {
  url?: string;
}

/** One task on a task-assignment Alerts row, read by the relay for the row's recipient. */
export interface TaskAlertCard {
  id: string;
  title: string;
  dueAt: number | null;
  priority: string | null;
  channelId: string | null; // null: a DM (fromDm) or no channel
  fromDm: boolean;
}

import { SentReadStamps } from "./sent-read-stamps.js";
import { reportUiFailure, shouldReport } from "../report-failure.js";
import {
  CONNECTION_LOST_ERROR,
  fetchOrTransportError,
  isTransportFailure,
  TransportError,
} from "./transport-failure.js";
import type { DaemonLivenessRow } from "./daemon-liveness-merge.js";
import { taskIdsDigest } from "./task-ids-digest.js";
import { anySignal, timeoutSignal } from "./abort-signal.js";
import type {
  ImportedSkill,
  MarketplaceSkill,
  MarketplaceSort,
  MarketplaceSourceStatus,
  Skill,
  SkillAssignment,
  SkillFileUpload,
  SkillIcon,
  SkillImportIdentifiers,
} from "$lib/plugins/agents/types.js";
import type { WorkspaceLogsPage } from "$lib/logs/workspace-log-history.js";
import type {
  LogsGroupEventsRequest,
  LogsGroupEventsResponse,
  LogsRunDetail,
  LogsRunDetailRequest,
  LogsSessionDetail,
  LogsSessionDetailRequest,
  LogsSearch,
  LogsSubjectRef,
  LogsTab,
  LogsTimelinePage,
  LogsTimelineRequest,
} from "$lib/logs/timeline/wire.js";
import type { RefreshOutcome } from "./auth-refresh.js";
// TYPE-ONLY, so no runtime edge is added between core/ and state/ (state/client.ts
// already holds the singleton this class instantiates). The Connect payload is one
// shape shared by the wire and the store; declaring it twice is how the two drift.
import type { ConnectSnapshot, ExternalInvitePreview } from "../state/connect.svelte.js";
import { CLOUD_RELAY_HTTP_BASE } from "./relay-config.js";
// Disconnect instrumentation (measurement only). Pure helpers — they never touch
// the socket, so nothing in the reconnect path can be changed by importing them.
import {
  DriftTracker,
  SUSPENSION_TICK_MS,
  SuspensionMeter,
  buildReconnectReport,
  reconnectReportDelayMs,
  type ReconnectReportBody,
} from "./reconnect-telemetry.js";
import { wsUrlToHttpBase } from "./auth-refresh.js";
import { guessContentType } from "../media/guess-content-type.js";
import type { BadgeAck, HiddenUnreadByWorkspace } from "./hidden-unread.js";
import type {
  AccountHandoffCandidates,
  Handoff,
  HandoffCandidates,
  WorkspaceHandoffs,
} from "../components/agents/handoff/types.js";
// The wire→model narrowing gate for a task's state group. Lives with the board's
// column vocabulary (tasks/board.ts) so the client and the board can never
// disagree about which five values are a real column.
import { isStateGroupKey } from "../tasks/board.js";
import {
  isTracedFrame,
  newTraceparent,
  traceIdOf,
  withTraceId,
  withTraceparent,
} from "./traceparent.js";
import { withPromptId } from "./prompt-id.js";
import { normalizeFrameMessageText } from "./message-text.js";
import { OUTSIDER_REACHABLE_RPCS } from "./workspace-list.js";
// "detail" (the search bar, CYBORG-1240) also matches page bodies and returns the card fields
// (snippet, project, owner, updatedAt); "titles" is the composer picker's title lookup.
export interface PageSearchOptions {
  mode: "titles" | "detail";
  cursor?: string;
  limit?: number;
}

// Per-section message search (CYBORG-search-ui): extra cyborg:search fields that narrow to one
// place + the filter toggles. The relay reader lands separately; an older relay ignores them.
export interface MessageSearchFilters {
  fromMe?: boolean;
  mentions?: boolean; // the relay's name for "Mentions me" (CyborgSearchSchema)
  fromIds?: string[]; // "from:@person": only rows these people sent
  mentionIds?: string[]; // "mentions:@person": only rows mentioning these people (Bursera)
  hasLink?: boolean;
  hasFile?: boolean; // "has:file": the message carries an attachment (Bursera)
}
// One place (dmPeerId covers a human DM or an agent conversation) or none: filters alone narrow
// the whole-workspace search.
export type MessageSearchScope =
  | (MessageSearchFilters & { channelId: string; dmPeerId?: never })
  | (MessageSearchFilters & { dmPeerId: string; channelId?: never })
  | (MessageSearchFilters & { channelId?: never; dmPeerId?: never });

export interface MessageTombstone {
  id: string;
  channelId: string | null;
  fromId: string;
  toId: string | null;
  parentId: string | null;
}

export interface MessageChanges {
  cursor: string;
  reset?: boolean;
  upserts: Message[];
  tombstones: MessageTombstone[];
}

export interface ThreadSummary {
  root: Message | null;
  replyCount: number;
  lastReplyAt: number;
  participants: string[];
  unreadReplies: number;
  unreadMentions: number;
}

export interface ThreadCounts {
  totalUnreadThreads: number;
  totalUnreadMentions: number;
}

// The relay's thread-state event (thread_updated / thread_read_changed and the
// mark_thread_read answer): absolute per-root unread for the viewer, their canonical
// workspace totals, and the server `at` that orders it against its siblings.
export interface ThreadStateEvent {
  toUserId: string;
  workspaceId?: string;
  rootId: string;
  unread_replies: number;
  unread_mentions: number;
  last_viewed?: number;
  at?: number;
  counts?: ThreadCounts;
  previous_unread_replies?: number;
  previous_unread_mentions?: number;
}

// The relay's `mapTask` readback (relay-standalone). Legacy fields come back
// camelCase, but the Tasks Redesign (Plane-shaped) projection rides along in
// snake_case (project_id / parent_id / state_id / sequence_id / cycle_id /
// label_ids / module_ids). `mapRawTask` normalizes those onto the camelCase
// `Task` model so callers never see two casings. All Plane fields are optional —
// an old relay omits them entirely.
type RawTask = Task & {
  project_id?: string | null;
  parent_id?: string | null;
  state_id?: string | null;
  // The row's `task_states.group`, denormalized by the relay beside `state_id`.
  // Typed as a plain string because it is UNVALIDATED wire data — mapRawTask
  // narrows it to the five real groups (isStateGroupKey) before it reaches Task.
  state_group?: string | null;
  sequence_id?: number | null;
  cycle_id?: string | null;
  label_ids?: string[];
  module_ids?: string[];
};

// One fetch_tasks answer. `asOf` (relay time the read started) and `ids` (a delta's
// visible id set) are null from a relay that predates CYBORG-1176.
export interface TaskPage {
  tasks: Task[];
  nextCursor: string | null;
  asOf: number | null;
  ids: string[] | null;
  // CYBORG-1198: the relay matched the sent idsDigest, so `ids` was left out.
  idsUnchanged: boolean;
}

function mapRawTask(t: RawTask): Task {
  // Pull `schedule` out of `rest` so it is NOT spread blindly: a partial broadcast
  // (reorder/recurrence-spawn `{ id }`, or any path that omits the field) must not
  // carry an explicit `schedule: undefined`/`null` that the app merge would use to
  // blank an existing cadence chip. We only emit `schedule` when the relay actually
  // sent one (defense in depth alongside the server denormalize on the edit paths).
  const {
    project_id,
    parent_id,
    state_id,
    state_group,
    sequence_id,
    cycle_id,
    label_ids,
    module_ids,
    schedule,
    ...rest
  } = t;
  const mapped: Task = {
    ...rest,
    // Prefer the snake_case readback; fall back to any camelCase the relay
    // already supplied so this stays robust to a future relay that emits both.
    projectId: project_id ?? rest.projectId ?? null,
    parentId: parent_id ?? rest.parentId ?? null,
    stateId: state_id ?? rest.stateId ?? null,
    // Narrow rather than trust: a relay that predates the field sends nothing
    // (null — the board then falls back to "unstarted"), and a relay that sends
    // an unrecognized value must not be able to invent a sixth board column.
    stateGroup: isStateGroupKey(state_group) ? state_group : (rest.stateGroup ?? null),
    sequenceId: sequence_id ?? rest.sequenceId ?? null,
    cycleId: cycle_id ?? rest.cycleId ?? null,
    labelIds: label_ids ?? rest.labelIds ?? [],
    moduleIds: module_ids ?? rest.moduleIds ?? [],
  };
  // Per-task scheduling — set `schedule` whenever the relay actually sent the field
  // (including an explicit `null`, which signals a detached schedule and must clear
  // the chip). Only an OMITTED field (`undefined`, a partial broadcast that carried
  // no schedule) is dropped, so the merge in app.svelte.ts can't blank a live chip.
  if (schedule !== undefined) mapped.schedule = schedule;
  return mapped;
}

// CYBORG-989 — the one answer a workspace open needs. Discriminated on `supported`
// and `unchanged` rather than a bag of optionals, so a caller cannot read
// `channels` off an answer that never carried one.
export type BootstrapWorkspaceResponse =
  // This relay does not serve bootstrap (older relay, flag off, or the caller is an
  // external in this workspace) — use the per-RPC path.
  | { supported: false }
  // The client's version is current: nothing workspace-wide changed, so only the
  // per-user half is returned and the relay ran no workspace-wide query at all.
  | ({ supported: true; unchanged: true; version: number } & BootstrapUserState)
  | ({
      supported: true;
      unchanged: false;
      version: number;
      channels: Channel[];
      members: WorkspaceMember[];
      external: ExternalParticipant[];
      workSessions: WorkSession[];
    } & BootstrapUserState);

// The half of a bootstrap answer that is per-USER, and so comes back on BOTH the
// changed and the unchanged branch.
export interface BootstrapUserState {
  unread: {
    counts: Record<string, number>;
    mentionCounts?: Record<string, number>;
    reads: Record<string, number>;
    dmCounts: Record<string, number>;
    dmReads: Record<string, number>;
    // peer id (or group_dm channel id) → epoch ms of that conversation's newest
    // message. Server-seeded recency for DM ordering, so a fresh device or a
    // message that landed while the app was closed is not read as "never".
    // Additive + backward-compatible: older relays omit it.
    dmLastMessageAt?: Record<string, number>;
  };
  prefs: Record<string, string>;
  // DMs I hid from my list (peer → hidden-at ms), on both answers; older relays omit it.
  dmHiddenAt?: Record<string, number>;
  drafts: { scope: string; text: string; updatedAt: number }[];
  // Scopes cleared on the server (#610 tombstones); older relays omit it.
  draftTombstones?: { scope: string; updatedAt: number }[];
  archivedChannelIds?: string[];
}

// Who a token acts as, resolved by the relay in the list query (pg-sync McpTokenActsAs).
// `deleted`: the agent is gone, or the person is no longer a member; the key cannot act.
export interface McpTokenActsAs {
  kind: "cybo" | "user";
  id: string;
  name: string | null;
  /** A person's photo URL; for an agent, its stored avatar (image URL or emoji). */
  avatarUrl: string | null;
  deleted: boolean;
}

/** The OAuth app holding a key (Connected apps); absent on a pasted key. */
export interface McpTokenOAuthApp {
  clientId: string;
  kind: "dcr" | "cimd";
  name: string | null;
  logoUri: string | null;
}

export interface McpToken {
  id: string;
  name: string;
  identityType: string;
  identityId: string;
  /** Absent from a relay older than the field. */
  actsAs?: McpTokenActsAs;
  scopes: string[];
  enabled: boolean;
  lastUsedAt: number | null;
  createdAt: number;
  /**
   * WHO minted the token. Present only on the superadmin listing
   * (listMcpTokensForWorkspace) — the owner-scoped one omits it because every
   * row is the caller's own. The server has always sent it; the settings table
   * simply never rendered it, which is why a superadmin saw everyone's tokens
   * with no way to tell whose was whose.
   */
  ownerName?: string | null;
  /** Present only on a key an app got through MCP OAuth (GB-06). */
  oauthApp?: McpTokenOAuthApp;
}

// ─── MCP OAuth consent (GB-06 API, GB-07 screen) ─────────────────────

/** What the consent screen may show about a pending request; never the code or the state. */
export interface McpConsentRequest {
  requestId: string;
  client: {
    id: string;
    kind: "dcr" | "cimd";
    name: string | null;
    uri: string | null;
    logoUri: string | null;
    verified: boolean;
  };
  redirectHost: string;
  scopes: string[];
  expiresAt: number;
}

export type McpConsentActAs = { type: "user" } | { type: "cybo"; cyboId: string };

/** A consent API answer that was not 2xx; `code` is the relay's error enum (or http_<status>). */
export class McpConsentError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    message?: string,
    options?: { cause?: unknown },
  ) {
    super(message ?? code, options);
    this.name = "McpConsentError";
  }
}

// ─── Webhooks (inbound, GitHub-style config) ─────────────────────────

export interface Webhook {
  id: string;
  channelId: string;
  workspaceId: string;
  name: string;
  // Whether a signing secret is set — the value itself is never returned.
  hasSecret: boolean;
  contentType: string;
  // "release" (only release events) | "all" (everything) | "select" (allowlist).
  eventMode: string;
  events: string[];
  // Event -> the `action`s it passes (pull_request -> ["opened"]); an unlisted event passes all.
  eventActions?: Record<string, string[]>;
  active: boolean;
  createdBy: string;
  // The cybo each delivery fires, or null for card-only (the default).
  triggerCyboId: string | null;
  // The agent posts appear as, or null for the creator ("Posts appear as"). Optional for older relays.
  actorCyboId?: string | null;
  // The instructions each fired task carries; only the creator may change them.
  promptTemplate?: string | null;
  // Server-computed: what the fired agent may do at its current autonomy, and why.
  agentAccess?: { access: "can_change_code" | "read_only"; reason: string } | null;
  lastDeliveryAt: number | null;
  createdAt: number;
}

export interface WebhookConfig {
  webhooks: Webhook[];
  // Event -> every `action` the relay accepts for it; {} from a relay older than W4.
  actionCatalog: Record<string, string[]>;
}

// W7 source/rule model for one channel (`cyborg:webhook_sources` / `cyborg:webhook_rules`, the
// CLI's `webhook:sources` / `webhook:rules`). A migrated channel webhook is a source whose
// `legacyWebhookId` names it.
export interface WebhookSource {
  id: string;
  legacyWebhookId: string | null;
  name: string;
  preset: string;
  active: boolean;
  paused: boolean;
  defaultBehavior: string;
}

export interface WebhookRule {
  id: string;
  sourceId: string;
  position: number;
  enabled: boolean;
  filter: Record<string, unknown>;
  continueEvaluating?: boolean;
  implied?: boolean;
}

export interface WebhookRuleAction {
  id: string;
  ruleId: string;
  kind: "post_channel" | "wake_agent" | "create_task" | "move_task";
  position?: number;
  config?: Record<string, unknown>;
}

export interface WebhookRulePolicy {
  ruleId: string;
  name: string;
  mode: "observe" | "active";
  paused: boolean;
  ownerUserId?: string;
  dailyCap?: number;
}

export interface WebhookRulesModel {
  rules: WebhookRule[];
  actions: WebhookRuleAction[];
  policies: WebhookRulePolicy[];
}

// Every source of a channel with all their rules (`cyborg:webhook_rules` without a sourceId).
export interface WebhookChannelRules extends WebhookRulesModel {
  sources: WebhookSource[];
}

// W10a outbound subscriptions (#5392): `cyborg:outbound_subscription_*`, the CLI's
// `webhook:outgoing:*`. The relay lists only the caller's own subscriptions.
export interface OutboundSubscriptionInput {
  name: string;
  url: string;
  events: string[];
  filter: Record<string, unknown>;
  active: boolean;
  // Legacy X-Cyborg7-* signatures alongside Standard Webhooks; only the owner turns them off (D13).
  legacySignatures: boolean;
}

export interface OutboundSubscription extends OutboundSubscriptionInput {
  id: string;
  lastSuccessAt: string | null;
  lastError: string | null;
}

// W9 Activity (`cyborg:webhook_activity`, the CLI's `webhook:activity`): metadata only.
export type WebhookActivityOutcome = "pending" | "succeeded" | "failed" | "ignored";

export interface WebhookActivityAction {
  ruleId: string;
  actionId: string;
  kind?: string;
  mode?: "observe" | "active";
  outcome: "pending" | "observed" | "succeeded" | "failed" | "ignored";
  canRetry?: boolean;
  attempts: number;
  reason?: string;
}

export interface WebhookActivityRun {
  id: string;
  operation: "receipt" | "replay";
  executionMode?: "none" | "observe" | "active";
  canRetry?: boolean;
  outcome: WebhookActivityOutcome;
  reason?: string;
  matchedRules: string[];
  actions: WebhookActivityAction[];
  attempts: number;
}

export interface WebhookActivityItem {
  sourceId?: string;
  eventId: string;
  channelId?: string;
  receivedAt: string;
  type: string;
  action?: string | null;
  outcome?: WebhookActivityOutcome;
  reason?: string;
  pending: boolean;
  // What the server lets this person ask for (D24); absent on an older relay.
  canRetry?: boolean;
  retryRunId?: string;
  canReplay?: boolean;
  runs: WebhookActivityRun[];
}

export interface WebhookActivityQuery {
  // Omitted: every channel this person may administer (the server decides).
  channelId?: string;
  sourceId?: string;
  outcome?: WebhookActivityOutcome;
  cursor?: string;
  limit?: number;
}

export interface WebhookActivityPage {
  activity: WebhookActivityItem[];
  // Over everything retained in the query's scope, not just this page.
  counts: Record<WebhookActivityOutcome, number> & { total: number };
  nextCursor?: string;
}

export interface WebhookDelivery {
  id: string;
  webhookId: string;
  channelId: string;
  event: string | null;
  action: string | null;
  requestHeaders: Record<string, string> | null;
  requestBody: string | null;
  responseStatus: number;
  responseBody: string | null;
  ok: boolean;
  redeliveredFrom: string | null;
  createdAt: number;
}

// W8's effect-free preview (`cyborg:webhook_action_plan`, knowledge/156). Never executes.
export interface WebhookActionPlanResult {
  dryRun: true;
  executable: false;
  mode: "observe";
  paused: boolean;
  ownerUserId: string;
  runAsUserId: string;
  dailyCap: number;
  payloadTrust: "untrusted";
  longTermMemoryWrites: false;
  actions: Array<{ id: string; kind: WebhookRuleAction["kind"]; idempotencyKey: string }>;
  rendered: Array<{ actionId: string; text: string }>;
  /** Task actions as W8 would run them; `title` only for a create with a sample. */
  tasks?: Array<{
    actionId: string;
    operation: "create_or_move" | "move";
    projectId: string;
    status: string;
    title?: string;
  }>;
}

// One tool of the workspace MCP server, GENERATED server-side from the real tool
// registry (mcp/tool-catalog.ts) — the settings page renders THIS, never a
// hand-kept list (CYBORG-882). `scope` is null for a tool that needs neither
// read nor write.
export interface McpToolInfo {
  name: string;
  scope: "read" | "write" | null;
  description: string;
}

// The "Test connection" verdict for a pasted token. The relay re-runs the /mcp
// endpoint's own auth sequence and names WHICH outcome it hit; the settings page
// turns the status into the sentence the user reads. The submitted value is
// hashed relay-side and never comes back in this payload.
export interface McpTokenTest {
  status:
    | "empty"
    | "not_a_cyborg7_token"
    | "unknown_token"
    | "disabled_token"
    | "expired_token"
    | "owner_suspended"
    | "owner_revoked"
    | "external_agents_disabled"
    | "identity_not_member"
    | "ok";
  // Present on "ok" only.
  tokenName?: string;
  identityType?: "cybo" | "user";
  identityName?: string | null;
  scopes?: string[];
}

/** A row of a profile's Access tab: a key acting as that agent or person, and who made it. */
export interface McpIdentityToken extends McpToken {
  ownerId: string;
  /** The maker's name, never their email; null when they have none. */
  ownerName: string | null;
  ownerAvatarUrl: string | null;
  /** Epoch ms; null when it never expires. Absent from a relay older than the field. */
  expiresAt?: number | null;
}

// ─── Agent triggers (outgoing webhooks owned by an agent, GB-03/GB-04; knowledge/147) ───

export interface AgentTriggerFilters {
  mention: boolean;
  dm: boolean;
  threadFollowup: boolean;
  fromAgents: boolean;
}

export interface AgentTrigger {
  id: string;
  workspaceId: string;
  cyboId: string | null;
  name: string;
  url: string;
  filters?: AgentTriggerFilters;
  /** `••••last4` of the Authorization value, never the value; null when none is set. */
  authHeader?: string | null;
  rateLimitPerMinute?: number;
  isActive: boolean;
  failureCount: number;
  lastSuccessAt: number | null;
  lastFailureAt: number | null;
  createdAt: number;
}

export interface AgentTriggerPatch {
  url?: string;
  name?: string;
  filters?: Partial<AgentTriggerFilters>;
  /** A string sets it; null removes it (update only). */
  authHeader?: string | null;
  rateLimitPerMinute?: number;
  isActive?: boolean;
}

/** The relay's answer to a trigger change: ok, or a typed refusal it explains. */
export interface AgentTriggerMutation {
  ok: boolean;
  id: string | null;
  webhook?: AgentTrigger | null;
  /** The signing secret, returned once on create. */
  secret?: string;
  code?: string;
  error?: string;
}

export interface AgentTriggerTestResult {
  ok: boolean;
  sent: boolean;
  responseStatus?: number | null;
  errorCode?: string | null;
  code?: string;
  error?: string | null;
}

export interface AgentTriggerDelivery {
  id: string;
  outboxId: string | null;
  /** mention / dm / thread_followup, or test. */
  reason: string | null;
  /** success / failure / rate_limited / dropped / sending / interrupted. */
  status: string;
  responseStatus: number | null;
  errorCode: string | null;
  attempt: number | null;
  coalesced?: number | null;
  createdAt: number;
}

/** knowledge/157: how far an external agent's setup got (cyborg:external_agent_status). */
export interface ExternalAgentStatus {
  cyboId: string;
  name: string;
  handle: string;
  avatar: string | null;
  channels: Array<{ id: string; name: string; isPrivate: boolean }>;
  mcpEnabled: boolean;
  mcpUrl?: string;
  connection: { connected: boolean; grants: number; oauthGrants: number; lastUsedAt?: number };
  trigger?: {
    id: string;
    active: boolean;
    filters: Record<string, boolean>;
    rateLimitPerMinute: number;
    hasAuthHeader: boolean;
    lastSuccessAt?: number;
    lastFailureAt?: number;
  };
  connectMessage: string;
}

export interface ExternalAgentTestCheck {
  id: "called" | "read" | "replied" | "single";
  state: "pass" | "fail" | "waiting";
  label: string;
  detail?: string;
  fix?: string;
}

export interface ExternalAgentTestResult {
  messageId: string;
  channelId?: string;
  startedAt?: number;
  checks: ExternalAgentTestCheck[];
  done: boolean;
}

export interface McpListResponse {
  enabled: boolean;
  tokens: McpToken[];
  tools: McpToolInfo[];
  connectionUrl: string | null;
  /** The list is every token in the workspace (superadmin), not only the caller's own. */
  viewerIsSuperadmin?: boolean;
  /** MCP OAuth is on for this relay (CYBORG7_MCP_OAUTH); absent from an older relay. */
  oauthEnabled?: boolean;
  identities: {
    self: { id: string; name: string } | null;
    /** `external`: an agent whose replies come from outside Cyborg (knowledge/157). */
    cybos: Array<{ id: string; name: string; external?: boolean }>;
    /** The agent the caller's New agent setup is connecting: preselected, never auto-approved. */
    hintCyboId?: string;
  };
}

// The serialized daemon-access-request row the UI consumes (#705 REQUEST →
// NOTIFY → APPROVE). createdAt/resolvedAt are epoch-ms numbers (the server
// serializes Date columns to numbers on the wire). Mirrors the server's
// DaemonAccessRequestPayload (cyborg-messages.ts).
// An agent transfer request (cybo-transfer.ts), as the relay serializes it. Mirrors the server's
// CyboTransferRequestPayload; `machines` and `memoryPending` come only on the recipient's pending view.
export interface CyboTransferMachineView {
  id: string;
  label: string;
  online: boolean;
  providerAvailable: boolean | null;
}
export interface CyboTransferRequestView {
  id: string;
  workspaceId: string;
  cyboId: string;
  cyboName: string;
  fromUserId: string;
  fromUserName: string | null;
  toUserId: string;
  toUserName: string | null;
  fromDaemonId: string | null;
  fromMachineName: string | null;
  toDaemonId: string | null;
  toMachineName: string | null;
  status: "pending" | "accepted" | "declined" | "cancelled" | "expired" | "failed";
  failureCode: string | null;
  resolvedBy: string | null;
  resolvedAt: number | null;
  createdAt: number;
  expiresAt: number;
  machines?: CyboTransferMachineView[];
  memoryPending?: boolean;
}
export interface CyboTransferCandidateView {
  userId: string;
  name: string;
  imageUrl: string | null;
  machineCount: number;
  onlineCount: number;
  /** The refusal code this person would get; absent when they can receive the agent. */
  refusal?: string;
}
export interface CyboTransferPreviewView {
  cyboId: string;
  cyboName: string;
  fromMachine: { id: string; label: string; online: boolean } | null;
  scheduleCount: number;
  openSessionCount: number;
  memoryUploaded: boolean;
  memoryPending: boolean;
  expiresInMs: number;
  candidates: CyboTransferCandidateView[];
  recipientMachines?: CyboTransferMachineView[];
  pending: CyboTransferRequestView | null;
}

export interface DaemonAccessRequestView {
  id: string;
  workspaceId: string;
  daemonId: string;
  requesterId: string;
  requesterName: string | null;
  scopes: Array<"chat" | "spawn" | "terminal" | "admin">;
  status: "pending" | "approved" | "denied";
  resolvedBy: string | null;
  resolvedAt: number | null;
  createdAt: number;
}

// The worker-invite row as the WIRE serves it (#3425's WorkerInvitePayload):
// timestamps, no status field, and never the plaintext code (hash-only at rest —
// the code travels solely on the mint response, beside this row).
export interface WorkerInviteWire {
  id: string;
  workspaceId: string;
  invitedBy: string;
  label: string | null;
  createdAt: number;
  expiresAt: number;
  cancelledAt: number | null;
  redeemedAt: number | null;
  redeemedDaemonId: string | null;
  firstConnectedAt: number | null;
  // Server-lane contract: the role applied at redeem. Absent = an older relay
  // that never stores it (the dialog then applies it on connect).
  memberDefaultRole?: "viewer" | "operator" | null;
  // Today's serializer sends NO status; honor it if a future one does.
  status?: string;
}

// The client-canonical invite row. `code`/`setupCommand` are present ONLY on
// the row built from the mint response — never re-fetchable. `status` is
// derived from the wire's timestamps by normalizeWorkerInvite below.
export interface WorkerInviteView {
  id: string;
  workspaceId: string;
  code?: string;
  setupCommand?: string;
  label?: string | null;
  createdBy: string;
  createdAt: number;
  expiresAt?: number;
  status: "pending" | "connected" | "canceled";
  // Redeemed-but-not-yet-connected nuance for the pending rows.
  redeemedAt?: number | null;
  daemonId: string | null;
  connectedAt: number | null;
  // undefined = the relay didn't send it (see WorkerInviteWire).
  memberDefaultRole?: "viewer" | "operator" | null;
}

export function normalizeWorkerInvite(p: WorkerInviteWire): WorkerInviteView {
  // An explicit wire status wins (future serializer); today's sends none, so
  // derive it: cancelled beats everything; only a REAL first connect counts as
  // connected — a redeemed-but-silent machine stays pending (the row shows the
  // "redeemed, waiting to connect" nuance instead of vanishing or completing).
  let status: WorkerInviteView["status"] = "pending";
  if (p.status === "pending" || p.status === "connected" || p.status === "canceled") {
    status = p.status;
  } else if (p.cancelledAt != null) {
    status = "canceled";
  } else if (p.firstConnectedAt != null) {
    status = "connected";
  }
  return {
    id: p.id,
    workspaceId: p.workspaceId,
    label: p.label,
    createdBy: p.invitedBy,
    createdAt: p.createdAt,
    expiresAt: p.expiresAt,
    status,
    redeemedAt: p.redeemedAt,
    daemonId: p.redeemedDaemonId,
    connectedAt: p.firstConnectedAt,
    ...(p.memberDefaultRole !== undefined ? { memberDefaultRole: p.memberDefaultRole } : {}),
  };
}

// The serialized page-access-request row the UI consumes (REQUEST → NOTIFY →
// APPROVE, structural clone of DaemonAccessRequestView above minus scopes — a
// page grant is per-user, all-or-nothing). createdAt/resolvedAt are epoch-ms
// numbers. Mirrors the server's PageAccessRequestPayload (cyborg-messages.ts).
export interface PageAccessRequestView {
  id: string;
  workspaceId: string;
  pageId: string;
  requesterId: string;
  requesterName: string | null;
  status: "pending" | "approved" | "denied";
  resolvedBy: string | null;
  resolvedAt: number | null;
  createdAt: number;
}

// ─── Corporate-email domain onboarding (feat/onboarding-company-email) ───

// The join policy for a claimed domain: `request` (default — a match detects the
// team, the user requests to join, an admin approves), `open` (auto-join on a
// match), `invite_only` (a match never auto-routes). Mirrors the server's
// JoinPolicySchema. NOTE the prototype settings UI uses "invite" as its label
// value — map "invite" ↔ "invite_only" at the component boundary.
export type JoinPolicy = "invite_only" | "request" | "open";

// The serialized workspace_email_domains row the UI consumes (the claimed
// company domain + its join policy). createdAt/verifiedAt are epoch-ms numbers;
// verifiedAt is RESERVED for a future DNS step and is always null in v1. Mirrors
// the server's WorkspaceEmailDomainPayload (cyborg-messages.ts).
export interface WorkspaceEmailDomainView {
  id: string;
  workspaceId: string;
  domain: string;
  joinPolicy: JoinPolicy;
  claimedBy: string | null;
  verifiedAt: number | null;
  createdAt: number;
}

// The serialized workspace_join_requests row the UI consumes (a pending/decided
// request to join a team by email domain). createdAt/decidedAt are epoch-ms
// numbers. Mirrors the server's WorkspaceJoinRequestPayload (cyborg-messages.ts).
export interface WorkspaceJoinRequestView {
  id: string;
  workspaceId: string;
  userId: string;
  email: string | null;
  status: "pending" | "approved" | "rejected";
  createdAt: number;
  decidedAt: number | null;
  decidedBy: string | null;
}

// A member's request that an owner/admin invite `email` (CYBORG-1087). Mirrors the
// server's WorkspaceInviteRequestPayload (cyborg-messages.ts); times are epoch ms.
export interface WorkspaceInviteRequestView {
  id: string;
  workspaceId: string;
  email: string;
  note: string | null;
  requestedBy: string | null;
  requestedByName: string | null;
  status: "pending" | "approved" | "declined";
  createdAt: number;
  decidedAt: number | null;
  decidedBy: string | null;
}

// The "Your team is already here" hint the signup REST response
// (/api/auth/register/verify) carries when a brand-new account's email domain
// already belongs to a registered workspace. Public team info only. The UI shows
// TeamFoundCard and offers request-to-join (cyborg:request_workspace_join with
// targetWorkspaceId = workspaceId). Mirrors the server's PendingDomainJoin
// (routes/auth.ts). Absent on a normal signup.
export interface PendingDomainJoin {
  workspaceId: string;
  name: string;
  avatarUrl: string | null;
  memberCount: number;
  members: { name: string; avatarUrl: string | null }[];
  /** How @domain signups join — drives "Join" (open) vs "Request to join". */
  joinPolicy: "request" | "open";
  domain: string;
}

export interface SlackEventMap {
  channel_message: Message;
  dm: Message;
  // Author-only ephemeral notice about a cybo @-mention that couldn't invoke
  // (not a channel member / no runnable daemon / spawn failed). Rendered as a
  // LOCAL system note in the channel — never persisted.
  cybo_mention_notice: {
    toUserId: string;
    workspaceId: string;
    channelId: string;
    text: string;
  };
  // Author-only signal that a channel @-mention is HELD behind the cybo's running
  // turn in that channel (the daemon's mention queue) rather than being answered
  // now. Drives the "Queued for @x" pill on the sender's own message bubble;
  // `queued: false` is the clear, sent when the hold ends (released to the real
  // session, or expired). Ephemeral — never persisted, so a reload shows no badge.
  cybo_mention_queued: {
    toUserId: string;
    workspaceId: string;
    channelId: string;
    messageId: string;
    cyboId: string;
    queued: boolean;
    // CYBORG-1354 share holds. Plain string: a newer relay may add reasons; unknown = busy-queue.
    reason?: string;
    ownerName?: string | null;
    /** the held thing is a task/page write: the pill says "write", not "share" */
    write?: boolean;
    /** the asker is the one approver: their card's message id and its token-locked buttons */
    holdId?: string;
    actions?: SignedCardAction[];
  };
  // /catchup digest — personal ephemeral result, scoped to the caller (toUserId).
  // Rendered client-side (a sheet), NEVER persisted. ok=false → `text` is a
  // human reason ("all caught up" / an error); unreadCount=0 when caught up.
  catchup_result: {
    requestId?: string;
    toUserId: string;
    workspaceId: string;
    channelId: string;
    channelName: string;
    ok: boolean;
    text: string;
    unreadCount: number;
  };
  typing: TypingEvent;
  reaction: ReactionWire;
  delete_message: { messageId: string; workspaceId: string };
  // `source` rides along when the edit is a system line changing state (a continued hop pause).
  edit_message: { messageId: string; workspaceId: string; text: string; source?: string };
  pin_message: {
    messageId: string;
    channelId: string | null;
    workspaceId: string;
    pinnedAt: number | null;
    pinnedBy: string | null;
  };
  read: {
    workspaceId: string;
    channelId?: string;
    dmPeerId?: string;
    lastReadAt: number;
  };
  activity_new: {
    id: string;
    workspaceId: string;
    eventType: string;
    // 'message' | 'task' | 'daemon' | 'page' (free text). Lets the live handler
    // route a task/page notification into the feed by source kind, not just by
    // eventType.
    sourceType?: string;
    sourceId: string;
    channelId: string | null;
    // DM-scoped row: the peer the server's DM-open clear keys on.
    dmPeerId?: string | null;
    previewText: string | null;
    actorId: string | null;
    actorName: string | null;
    createdAt: number;
    // Page-mention tap target (CYBORG-304): the page's project id, carried on
    // this live push only (activity_events has no project_id column). Absent
    // for every other event type.
    projectId?: string | null;
    // CYBORG-1231: cybo_moved / cybo_handed_off; system_notice carries {url}.
    detail?: CyboTransferDetail | NoticeDetail;
    // agent_dm_received: the DM message to land on (falls back to sourceId).
    messageId?: string | null;
    // An agent row raised as its cybo: the session that acted, which keys the viewer's alias.
    actorSessionId?: string | null;
    // task_assigned / task_status_changed: the row's tasks, as the recipient may see them.
    tasks?: TaskAlertCard[];
    // A notice reworded as settled (a missed-run row's "caught up"): the row is read now.
    isRead?: boolean;
    // Server-computed cross-workspace unread total (dock badge); applied even when this
    // frame is for a workspace other than the open one.
    totalUnread?: number;
    hiddenUnreadByWorkspace?: HiddenUnreadByWorkspace;
  };
  // Bare cross-workspace unread total for a row-less change to the badge (a human DM writes a
  // dm_received row but sends no activity_new). Server-computed after the row insert.
  activity_badge: { totalUnread: number; hiddenUnreadByWorkspace?: HiddenUnreadByWorkspace };
  // Live push (#705 REQUEST → NOTIFY → APPROVE): a daemon-access request was
  // created (→ owner) or resolved (→ requester). The payload IS the serialized
  // request row the inbox renders. Pushed per-user, not workspace-wide.
  daemon_access_request_changed: { request: DaemonAccessRequestView };
  // Live push to both people whenever an agent transfer request is created or settles.
  cybo_transfer_request_changed: { request: CyboTransferRequestView };
  // Live push (Worker tier): a worker invite was minted, canceled, redeemed, or
  // its machine first connected. The payload is the RAW wire row (WITHOUT
  // `code` — that only ever travels in the mint response); consumers derive
  // status via normalizeWorkerInvite.
  worker_invite_changed: { invite: WorkerInviteWire };
  // Live broadcast: who can use a machine changed (member default or a
  // per-person grant). Re-read the machine list + access rows.
  // Live push: my membership in a workspace became active (invite accepted elsewhere): re-read the list.
  // `externalWorkspace`: a host seated me as an external instead (server feedback-channel.ts).
  workspace_membership_changed: {
    workspaceId: string;
    externalWorkspace?: ExternalWorkspacePayload;
  };
  workspace_machines_changed: { workspaceId: string; daemonId: string };
  external_agent_status_changed: { workspaceId: string; cyboId: string };
  // Live broadcast (slack-sync.ts slackImportFrame): where one channel's Slack history
  // import is. Channel-scoped: a private channel's reaches only its members.
  slack_import_progress: SlackImportProgress;
  // Live push, structural clone of daemon_access_request_changed above: a
  // page-access request was created (→ owner) or resolved (→ the requester it
  // belongs to). Access is per-user, so a resolve only ever affects that one
  // requester.
  page_access_request_changed: { request: PageAccessRequestView };
  // Live push: the OWNER revoked a standing page_grants row — pushed to the
  // revoked user only, so an open card/page can react (e.g. show "access
  // revoked" instead of stale content). The read gate is authoritative on the
  // next fetch_page regardless — this is a UX nicety, not the security boundary.
  page_access_revoked: { pageId: string };
  // Live push (feat/onboarding-company-email): a workspace join request was
  // created (→ the workspace owner/admins) or decided (→ the requester). The
  // payload IS the serialized request row the approvals inbox renders. Pushed
  // per-user, not workspace-wide. Mirrors daemon_access_request_changed.
  join_request_changed: { request: WorkspaceJoinRequestView };
  // Live push (CYBORG-1087): an invite request was created (→ owners/admins) or
  // decided (→ the requester and the owners/admins).
  invite_request_changed: { request: WorkspaceInviteRequestView };
  notification_pref_changed: { workspaceId: string; scopeId: string; preference: string };
  // "Archive channel" (personal): the viewer archived/unarchived a channel on ANOTHER device.
  channel_archived_for_me_changed: {
    workspaceId: string;
    channelId: string;
    archived: boolean;
    hiddenUnreadByWorkspace?: HiddenUnreadByWorkspace;
  };
  // A DM hidden (hiddenAt) or shown again (null) on the viewer's OTHER device.
  dm_hidden_changed: { workspaceId: string; peerId: string; hiddenAt: number | null };
  // Server-side draft sync (#610): a composer draft created/updated/cleared on
  // the user's OTHER device. text === null means it was sent or cleared there.
  draft_changed: { workspaceId: string; scope: string; text: string | null; updatedAt: number };
  members_updated: { workspaceId: string; members: WorkspaceMember[] };
  // A workspace's name, logo or settings changed (the row as stored) → patch it everywhere.
  workspace_updated: {
    workspaceId: string;
    name: string;
    avatarUrl: string | null;
    settings: WorkspaceSettings;
  };
  // A task was created, updated, or deleted server-side → patch the board live.
  // Carries the full row so a client can upsert it (created/updated) or drop it
  // (deleted) from workspaceState.tasks without a refetch (the relay fans this out
  // to every member of the workspace).
  tasks_changed: { workspaceId: string; op: "created" | "updated" | "deleted"; task: Task };
  // A task's ACTIVITY feed changed (a comment was posted) → any client with that
  // task's Activity tab open re-fetches it. Deliberately separate from
  // tasks_changed (task-ROW level) so a comment can't blank board fields.
  task_activity_changed: { workspaceId: string; taskId: string };
  // A page was created/updated/archived/deleted server-side → workspace members
  // with an open pages view refetch. The full row rides along (created/updated);
  // a "deleted" op sends only `{ id }`. The list view's own refetch hooks in.
  pages_changed: {
    workspaceId: string;
    projectId: string;
    op: "created" | "updated" | "deleted";
    page: Partial<Page> & { id: string };
  };
  // knowledge/135 §3: every status change and every block write of an agent run on a
  // page. `blockContent` (the agentBlock's current TipTap blocks) rides along on writes.
  page_agent_run: {
    workspaceId: string;
    pageId: string;
    projectId: string;
    run: PageAgentRun;
    blockContent?: Record<string, unknown>[];
  };
  // workspaceId: the workspace this copy was sent for (absent from an older relay).
  daemon_status: { daemonId: string; status: "online" | "offline"; workspaceId?: string };
  channel_created: { channel: Channel };
  // #608: a group DM was created → add it to the sidebar's DM section, but ONLY
  // for its members (a hidden channel must never surface for a non-member).
  // memberIds is the authoritative member list (creator + participants).
  group_dm_created: { channel: Channel; memberIds: string[] };
  channel_updated: { channel: Channel };
  // No department in it: the client re-reads fetch_projects.
  channel_project_changed: { channelId: string; workspaceId: string };
  channel_deleted: { channelId: string; workspaceId: string };
  channel_member_added: { channelId: string; userId: string; workspaceId: string };
  channel_member_removed: { channelId: string; userId: string; workspaceId: string };
  channel_cybo_added: { channelId: string; cyboId: string; workspaceId: string };
  channel_cybo_removed: { channelId: string; cyboId: string; workspaceId: string };
  // #640: a cybo's editable display fields changed → patch the workspace roster.
  cybo_updated: {
    cybo: {
      workspaceId: string;
      id: string;
      slug: string;
      // Absent: unchanged (an agent's update_agent broadcasts only the changed fields).
      name?: string;
      description?: string | null;
      avatar?: string | null;
      role?: string | null;
      provider?: string;
      model?: string | null;
      // #636: provider readiness, recomputed against the post-edit provider.
      readiness?: "ready" | "needs-daemon" | "created";
      // Post-write capability grants. See CyboUpdateBroadcast.toolGrants — the
      // profile's Tools card and the editor's Tools step both read this off the
      // roster row, so a grant changed server-side is invisible without it.
      toolGrants?: Record<string, unknown> | null;
      // The CEO seat after the write (the relay always sends it). See applyCeoSeat.
      isDefault?: boolean;
    };
  };
  // #644: a cybo was created → append it to the workspace roster live.
  cybo_created: {
    cybo: {
      workspaceId: string;
      id: string;
      slug: string;
      name: string;
      description: string | null;
      avatar: string | null;
      role: string | null;
      provider: string;
      model: string | null;
      isDefault: boolean;
      createdAt: number;
      // #636: provider readiness at creation time ("needs-daemon" if nothing
      // reachable can run it yet — surfaced as a banner, never blocked).
      readiness?: "ready" | "needs-daemon" | "created";
    };
  };
  // #644: a cybo was deleted → drop it from the workspace roster live.
  cybo_deleted: { cyboId: string; workspaceId: string };
  // Thread-state events carry the viewer's state for the root plus canonical workspace
  // totals; the client uses them only as a TRIGGER to refresh the canonical snapshot
  // (app.svelte.ts's onThreadEvent) and to scope that refresh by workspaceId. The
  // optional fields are absent only on a relay older than that contract.
  thread_updated: ThreadStateEvent & { channelId: string | null; last_reply_at: number };
  thread_read_changed: ThreadStateEvent;
  thread_follow_changed: { toUserId: string; rootId: string; following: boolean };
  // A scheduled message (#607) FAILED at fire time. The payload is the updated
  // row (processedAt set, errorCode non-null) so the Scheduled list flips it from
  // pending → failed live. Successful sends arrive as a normal channel/dm
  // broadcast (no dedicated event); a re-list reflects the processedAt change.
  schedule_message_failed: ScheduledMessage;
  // A recurring cybo schedule (#619) was mutated (created/updated/enabled/deleted/
  // run-once) — possibly by another client or the runner. The relay fans the
  // `cyborg:schedule_mutated` ack out without a requestId so every open board
  // stays live. Structurally matches ws-client's ScheduleMutatedPayload; kept as
  // a record here so core/ stays free of the cyborg schedule types.
  schedule_mutated: {
    requestId?: string;
    ok: boolean;
    op: string;
    scheduleId: string | null;
    schedule?: Record<string, unknown> | null;
    error?: string;
  };
  // A live screencast frame from a cybo's browser session (browser control,
  // knowledge/95 Phase 4). base64 JPEG + CDP's Page.screencastFrame metadata
  // verbatim (flat keys — no nested scrollOffset object), which is what
  // BrowserView needs to map a viewer click back to a viewport CSS pixel. Every
  // frame is a FULL repaint; streamed only while a viewer is subscribed
  // (measured ~10 fps / ~9.9 KB per frame at the daemon's 300ms ack delay),
  // never always-on.
  browse_frame: {
    sessionId: string;
    daemonId?: string;
    data: string;
    metadata: {
      deviceWidth: number;
      deviceHeight: number;
      pageScaleFactor: number;
      offsetTop?: number;
      scrollOffsetX?: number;
      scrollOffsetY?: number;
      timestamp?: number;
    };
    url?: string;
  };
  // The AUTHORITATIVE "this browser session is gone" signal (tab closed, the
  // debugger detached, the daemon restarted). It is the only thing that may put
  // BrowserView into its dead-end state — a subscribe timeout is transient and
  // must not.
  browse_session_ended: { sessionId: string; daemonId?: string; reason?: string };
  // The authoritative state of one agent browser session (paused / who is
  // driving / "needs you to sign in"). A full replacement every time — idempotent,
  // so a reconnect just waits for the next one.
  browser_session_state: {
    workspaceId: string;
    sessionId: string;
    daemonId?: string;
    agentId?: string;
    cyboId?: string;
    target: "extension" | "agent" | "cyborg";
    paused: boolean;
    pausedBy?: string;
    // null: a wire null — "no open question".
    needsHuman: {
      requestId: string;
      reason: string;
      message: string;
      kind: "human" | "approval";
    } | null;
    url?: string;
    title?: string;
    toUserId?: string;
  };
  // The browser session DIRECTORY push (cyborg:terminals_changed's twin): the
  // daemon re-pushes the owner's full session list whenever one opens or ends, so
  // the cybo profile's Browser tab stays live without polling. Owner-scoped — the
  // list carries the URLs the owner is browsing.
  browser_sessions_changed: {
    workspaceId: string;
    sessions: {
      sessionId: string;
      daemonId: string;
      cyboId?: string;
      url: string;
      title?: string;
      startedAt?: number;
      viewers?: number;
    }[];
  };
  // The relay announced a planned restart (deploy). The socket will drop
  // shortly — the UI shows "Updating…" and reconnects quietly.
  server_shutdown: { reason?: string; etaMs?: number; restartMark?: string };
  // The relay rejected our token with `4003` and the silent refresh could NOT
  // heal it (no stored refresh token, or the refresh token itself is dead). The
  // app clears the session and routes to /login — an actionable re-login, never
  // an infinite reconnect loop against a dead token (CYBORG-87).
  // `cause`: which path ended the session, for the forced-logout report
  // (reauth_budget_exhausted = transient refresh failures outlasted the budget).
  auth_expired: {
    reason?: string;
    cause?: "terminal_close" | "rejected" | "refresh_expired" | "reauth_budget_exhausted";
  };
  // clientMsgId: set on a refused or lost send (`stale_send`, `already_deleted`, `send_lost`) so the
  // optimistic bubble it belongs to can be marked failed.
  error: { requestId?: string; code: string; message: string; clientMsgId?: string };
  // CYBORG-1459: the relay could not store a send yet and parked it; its replay delivers it within
  // budgetMs (or a `send_lost` error says it never will).
  message_queued: {
    requestId?: string;
    clientMsgId?: string;
    messageId?: string;
    budgetMs?: number;
  };
  // `attempt`/`delayMs` are present only on a "reconnecting" emit from
  // scheduleReconnect — they drive the banner's "attempt N · retrying in Ns" copy.
  // The socket's auth state changed (true: passed cyborg:auth, so sends reach the relay; false:
  // dropped, replaced or reconnecting). Every assignment of `authenticated` emits it.
  auth_state: { authenticated: boolean };
  connection: {
    status: "connected" | "disconnected" | "reconnecting";
    attempt?: number;
    delayMs?: number;
  };
  // HARD-PAUSE gate fired on a fire-and-forget path (the guest agent-prompt
  // send carries no requestId, so it can't reject a pending RPC). The relay
  // emits a top-level `guest_error` with error:'license_required'; the app
  // opens the activate modal in response.
  license_required: { message: string; license?: LicensePayload };
  // Pricing v2 upgrade gate on a fire-and-forget path. Distinct from
  // license_required (that's a paused trial; this is a Free workspace hitting a
  // Pro feature or the seat cap). `code` is 'pro_required' | 'seat_limit_reached';
  // `message` is the server's ready-to-show copy. The app opens the upgrade modal.
  upgrade_required: { message: string; code: string };
  // A hole was detected in the per-workspace broadcast sequence on an OPEN socket
  // (#499): an incoming broadcast's seq jumped past expected, so events were lost
  // between relay instances or on a flaky link WITHOUT the socket dropping. The
  // app responds with a bounded catch-up (drainSync + DM since-drain) from
  // `fromSeq`, recovering the gap without waiting for a reconnect/foreground.
  // `toSeq` revealed the hole: rows fromSeq < seq < toSeq never reached this socket.
  seq_gap: { workspaceId: string; fromSeq: number; toSeq: number };
  // A seq-carrying broadcast moved this socket's high-water (CYBORG-1205 sync cursor).
  broadcast_seq: { workspaceId: string; seq: number };
}

// Connecting/authenticating can fail two ways that demand OPPOSITE responses:
//  - AuthError: the token is bad/expired → clear the session and send the user
//    to login.
//  - NetworkError: the relay is unreachable (down, deploying, offline) → KEEP
//    the session, stay in the app, show "reconnecting", and retry. Treating a
//    transient relay outage as an auth failure is what logged users out on
//    every deploy.
export class AuthError extends Error {
  constructor(message = "unauthorized") {
    super(message);
    this.name = "AuthError";
  }
}
export class NetworkError extends Error {
  constructor(message = "relay unreachable") {
    super(message);
    this.name = "NetworkError";
  }
}

// CYBORG-1187: an authenticated HTTP call asked with no sign-in token at all. Not
// sent, so not a failure worth reporting; callers wait for the next sign-in.
// `code` is the field to test on (same idea as the `status` field on HTTP errors).
export class NotSignedInError extends Error {
  readonly code = "not_signed_in";
  constructor(message = "not signed in") {
    super(message);
    this.name = "NotSignedInError";
  }
}

/** Only emitted when a prompt failed before it could enter the socket. */
export class PromptNotSentError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause), { cause });
    this.name = "PromptNotSentError";
  }
}

// An error REPLY to a send is the other proof of non-delivery, and it has to keep
// its own subtype (the license/upgrade modals branch on those), so it carries a
// flag instead of being wrapped. Read both kinds through isPromptNotSent.
interface PromptNotSentMark {
  promptNotSent?: true;
}

function markPromptNotSent(error: unknown): unknown {
  if (error instanceof Error) (error as Error & PromptNotSentMark).promptNotSent = true;
  return error;
}

/** Did this failure prove the prompt never reached the agent? A lost response did not. */
export function isPromptNotSent(error: unknown): boolean {
  if (error instanceof PromptNotSentError) return true;
  return (error as PromptNotSentMark | null)?.promptNotSent === true;
}

// Server-authoritative license payload (mirror of PgSync.getLicenseStatus).
// Carried on the cyborg:error / guest_error `license_required` rejection so the
// UI can re-seed the trial bar and open the activate modal from the same event.
export interface LicensePayload {
  state: "trialing" | "active" | "paused";
  plan: string;
  trialEndsAt: number | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  status: string | null;
  // ADDITIVE entitlement fields (pricing v2) — mirror of getLicenseStatus. The
  // server always sends them; consumers gate the UI on them (isPro / seat cap).
  isPro: boolean;
  billableHumans: number;
  humanLimit: number | null;
  interval: string | null;
}

// Server-computed billing intent (mirror of PgSync.getBillingIntent →
// resolveBillingIntent). Returned alongside the license from fetchLicense; tells
// the client WHICH billing action to offer on the caller's surface + the copy.
// Keep BillingActionPayload in lockstep with the server BillingAction union.
export type BillingActionPayload =
  | "stripe_checkout"
  | "stripe_portal"
  | "iap_purchase"
  | "purchase_unavailable"
  | "iap_manage"
  | "manage_on_web"
  | "manage_in_mobile"
  | "contact_admin"
  | "owner_only";

// The caller's surface, sent to the relay so it can resolve the right intent
// (web/desktop = Stripe rail, ios/android = IAP rail). Mirror of the server
// BillingPlatform union.
export type BillingPlatformPayload = "web" | "desktop" | "ios" | "android";

export interface BillingIntentPayload {
  action: BillingActionPayload;
  title: string;
  message: string;
  ctaLabel?: string;
}

// Thrown when an agent op (create/run/spawn) is rejected because the workspace's
// trial ended with no active subscription (HARD-PAUSE gate). The UI catches this
// to open the activate modal instead of surfacing a raw error string.
export class LicenseRequiredError extends Error {
  readonly code = "license_required";
  readonly license?: LicensePayload;
  constructor(message: string, license?: LicensePayload) {
    super(message);
    this.name = "LicenseRequiredError";
    this.license = license;
  }
}

// Thrown when an op is rejected because a Free workspace hit a Pro-only feature
// (pro_required) or the 5-human seat cap (seat_limit_reached) — pricing v2. NOT a
// paused trial (that's LicenseRequiredError): here the workspace is fine, the
// action just needs an upgrade. The UI catches this to open the upgrade prompt
// with the server's `message` instead of toasting a raw string.
export class UpgradeRequiredError extends Error {
  readonly code: "pro_required" | "seat_limit_reached";
  constructor(message: string, code: "pro_required" | "seat_limit_reached") {
    super(message);
    this.name = "UpgradeRequiredError";
    this.code = code;
  }
}

// Thrown when a cybo can't start because its backend (e.g. anthropic) is
// unavailable on the daemon. Carries the daemon's CLASSIFIED reason so the UI
// shows the exact remedy (add usage / reconnect with an API key) instead of a
// generic "needs X". `reasonKind` is kept a loose string here to avoid a
// plugins->core import; the UI casts it to ProviderReasonKind for the mapper.
export class ProviderUnavailableError extends Error {
  readonly code = "unavailable";
  readonly backend?: string;
  readonly reasonKind?: string;
  readonly unavailableReason?: string;
  constructor(
    message: string,
    opts?: { backend?: string; reasonKind?: string; unavailableReason?: string },
  ) {
    super(message);
    this.name = "ProviderUnavailableError";
    this.backend = opts?.backend;
    this.reasonKind = opts?.reasonKind;
    this.unavailableReason = opts?.unavailableReason;
  }
}

// A relay RPC error payload (cyborg:error / rpc_error). `error` is a coded reason
// (license_required / pro_required / seat_limit_reached); `code`/`backend`/… carry
// a provider-unavailable classification. `message` is always present.
interface RpcErrorPayload {
  message: string;
  error?: string;
  code?: string;
  license?: LicensePayload;
  backend?: string;
  reasonKind?: string;
  unavailableReason?: string;
  // The relay GUESSED which daemon to ask (blind-timeline-route.ts): a not_found from it
  // is no evidence the session is gone (CYBORG-1182).
  routedBlind?: boolean;
}

// Classify a relay RPC error payload into the right typed Error so callers branch
// on the failure (open the activate/upgrade modal, show a provider remedy) instead
// of matching a raw string. Kept out of handleMessage to keep its error path flat.
export interface ForwardOriginal {
  messageId: string;
  channelId: string | null;
  /** A DM original: the other end of the pair, from the viewer's side. */
  peerId: string | null;
  /** A thread reply: its root. */
  parentId: string | null;
}

function buildRpcError(p: RpcErrorPayload): Error {
  // HARD-PAUSE gate: trial ended with no active sub — open the activate modal.
  if (p.error === "license_required") {
    return new LicenseRequiredError(p.message, p.license);
  }
  // Pricing v2 upgrade gate: Free workspace hit a Pro feature / seat cap — open
  // the upgrade prompt. Distinct from the paused-trial gate above.
  if (p.error === "pro_required" || p.error === "seat_limit_reached") {
    return new UpgradeRequiredError(p.message, p.error);
  }
  // A cybo backend gap (spawn refusal) — carry the daemon's classified reason so
  // the caller shows the exact remedy, not a raw string.
  if (p.code === "unavailable" && (p.backend || p.reasonKind)) {
    return new ProviderUnavailableError(p.message, {
      backend: p.backend,
      reasonKind: p.reasonKind,
      unavailableReason: p.unavailableReason,
    });
  }
  // Fallback: no dedicated Error subtype for this envelope, but it may still
  // carry a structured `code` (e.g. the relay's stable "no_daemon" reason on a
  // daemon-forward with nothing to forward to — relay-standalone.ts's
  // respondError no-daemon branch). Thread it onto the plain Error as `.code` so
  // a caller can branch on the stable reason instead of matching `message`
  // prose that's free to change. Purely additive: `code` is undefined whenever
  // the envelope doesn't carry one, same as today.
  // The daemon's own rpc_error puts its sentence in `error` and leaves `message`
  // empty, so reading only `message` produced `new Error("")` — an `{#if error}`
  // block that renders nothing. Both names are read; `code` still rides along.
  const err = new Error(p.message || p.error || "That request failed.");
  if (p.code) (err as Error & { code?: string }).code = p.code;
  if (p.routedBlind === true) (err as Error & { routedBlind?: boolean }).routedBlind = true;
  return err;
}

// Hard ceiling on attachment size, mirrored on the relay (MAX_ATTACHMENT_BYTES
// in relay-standalone.ts). Guarded client-side before upload so an oversized
// file fails fast with a clear message instead of a slow upload + server reject.
export const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024; // 50 MB

// Who a presigned upload belongs to (CYBORG-897 phase 3). The relay records it in
// asset_objects so a later phase can answer "may this caller read this key?" with
// one primary-key lookup. Every field is optional on the wire and the relay
// verifies `workspaceId` against membership before trusting it — a client cannot
// widen its own object's audience by asserting a scope, only describe it.
//
// `workspaceId` is genuinely absent in two cases, not merely unknown: the
// onboarding workspace LOGO (uploaded before the workspace exists) and the
// account-level profile photo (global by design — it renders on login, in the
// workspace switcher and on invites). Both are `avatar`.
export interface AssetUploadScope {
  workspaceId?: string;
  scopeKind?: "avatar" | "workspace" | "channel" | "dm" | "session" | "page" | "task";
  scopeId?: string;
}

// Close REASONS the relay pairs with a normal `1000` when the identity behind the
// socket is gone for good (relay-standalone.ts). A 1000 otherwise means a clean
// disconnect worth reconnecting from, so the reason string is the only signal that
// separates "the relay is restarting" from "this account no longer exists" — match
// it exactly, and keep it in sync with the relay's close() calls.
export const TERMINAL_CLOSE_REASONS: ReadonlySet<string> = new Set([
  "account deleted",
  "account deactivated",
  "account secured", // a "Not me" answer signed every session out (login-guard verdict)
]);

// The two rejections the CLIENT ITSELF mints when a socket dies under an in-flight
// RPC (CYBORG-1171). Such a request can never be answered: the relay's reply goes
// to a socket nobody is listening on, and the requestId only ever existed on that
// connection. "Client disconnected" is the INTENTIONAL teardown (disconnect():
// logout/shutdown); "Connection lost" is a drop. Anything that has to tell "the
// socket went away" from "the relay answered with an error" - ping()'s liveness
// verdict, the task drain's retry - matches on these exact strings, so they live
// here instead of inline at each throw site.
export const CLIENT_DISCONNECTED_ERROR = "Client disconnected";
export { CONNECTION_LOST_ERROR, isTransportFailure, TransportError };

function sessionChanged(): TransportError {
  return new TransportError("session_changed", "Client session changed");
}
// Stable code on both rejections above, so the failure report reads them as transient.
const CONNECTION_LOST_CODE = "connection_lost";
// After a deploy announcement (or a 1001 close) the reconnect storm that follows is
// expected for this long past the relay's own ETA.
const PLANNED_RESTART_GRACE_MS = 30_000;
const WS_CLOSE_GOING_AWAY = 1001;

// Public (unauthenticated) invitation preview shown on the /invite/:token
// landing page. The relay answers GET /api/invite/:token with this shape so the
// page can render the workspace name + status before any login/connect.
export interface AcceptInvitationResult {
  membership: Membership;
  workspaceId: string;
  alreadyAccepted?: boolean;
  /** Already a member (the team's reusable link reopened): nothing was joined. */
  alreadyMember?: boolean;
}

export interface PublicInvitePreview {
  workspace: { id: string; name: string; logoUrl?: string | null } | null;
  invitedEmail: string | null;
  /** "revoked": an admin cancelled it (an older relay answers "invalid" for that). */
  status: "pending" | "accepted" | "expired" | "revoked" | "invalid";
  expiresAt: number | null;
  /** Display name of whoever sent the invite, when the relay reports it. */
  invitedByName?: string | null;
  /** Pending only: the workspace's live agents (<= 6), their total and the active people.
   *  Absent from an older relay. */
  agents?: { name: string; avatar: string | null }[];
  agentCount?: number;
  peopleCount?: number;
  /** A reusable link (no email lock). */
  isOpen?: boolean;
  /** Legacy (older desktops): whether the invited address has a real account. The join card
   *  never reads it. */
  invitedHasAccount?: boolean | null;
}

/** cyborg:invitee_arrival (relay-only): the invitee's arrival screen + where they land. */
export type InviteeLanding =
  | { kind: "dm"; cyboId: string }
  | { kind: "channel"; channelId: string }
  | { kind: "workspace" };
export interface InviteeArrival {
  workspace: { id: string; name: string; logoUrl: string | null };
  agents: { cyboId: string; name: string; avatar: string | null }[];
  channels: { id: string; name: string }[];
  channelCount: number;
  teammates: number;
  computer: { name: string } | null;
  ai: { provider: string; label: string } | null;
  invitedBy: { name: string; userId?: string } | null;
  firstThing: { cyboId: string; agentName: string; line: string } | null;
  landing: InviteeLanding;
}
/** Where an invited member lands in the app (never a dead pointer: the relay re-reads it). */
export function inviteeLandingPath(workspaceId: string, l: InviteeLanding): string {
  const home = `/workspace/${encodeURIComponent(workspaceId)}`;
  if (l.kind === "dm") return `${home}/agent/${encodeURIComponent(l.cyboId)}`;
  if (l.kind === "channel") return `${home}/channel/${encodeURIComponent(l.channelId)}`;
  return home;
}
export interface InviteeArrivalResponse {
  ok: boolean;
  error?: "disabled" | "forbidden" | "not_found";
  arrival?: InviteeArrival;
}

// Derive the relay's HTTP(S) origin from a saved-session WS url:
//   ws(s)://host[/api/ws] → http(s)://host
// Used by the unauthenticated invite landing page, which has the saved session's
// ws url but no live (authenticated) client yet.
export function relayHttpBaseFromWsUrl(wsUrl: string): string {
  return wsUrl
    .replace(/^ws:/, "http:")
    .replace(/^wss:/, "https:")
    .replace(/\/api\/ws\/?$/, "")
    .replace(/\/ws\/?$/, "");
}

// Fetch the public invitation preview for the /invite/:token landing page. No
// auth — the relay route is intentionally unauthenticated so an invitee who
// isn't signed in yet can still see what they were invited to.
export async function fetchInvitePreview(
  wsUrl: string,
  token: string,
): Promise<PublicInvitePreview> {
  const base = relayHttpBaseFromWsUrl(wsUrl);
  const resp = await fetch(`${base}/api/invite/${encodeURIComponent(token)}`);
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json() as Promise<PublicInvitePreview>;
}

/** "Ask for a new invite" on an expired/cancelled one. No auth; the relay always answers
 *  {ok:true} (it never says whether anything happened). Throws on 429 or a network failure. */
export async function requestInviteRenewal(wsUrl: string, token: string): Promise<void> {
  const base = relayHttpBaseFromWsUrl(wsUrl);
  const resp = await fetch(`${base}/api/invite/${encodeURIComponent(token)}/renew`, {
    method: "POST",
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
}

type EventHandler<T> = (data: T) => void;

interface PendingRequest {
  resolve: (data: unknown) => void;
  reject: (error: Error) => void;
  // The promise's OWN reject, with no per-type verdict wrapper on it. Used for a
  // failure that proves nothing about delivery - the socket dying under a request
  // already on the wire (CYBORG-1171). `reject` for cyborg:send_agent_prompt marks
  // the error "not sent", which DELETES the outbox row and offers a Retry that can
  // double-send; a dropped socket cannot support that verdict, since the frame may
  // have reached the relay before the link died. Same distinction the response
  // timeout already makes (it rejects the promise directly and "stays unknown on
  // purpose"). Absent on entries that never had a wrapper, e.g. ping().
  rejectRaw?: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  // The trace id sent on a user-intent request, carried onto whatever rejects it.
  traceId?: string;
}

// Workspace-level slash-command config (the AI settings tab). The CONTRACT W2's
// server endpoints must honour:
//   slashCommandsEnabled — global on/off for the whole workspace. When false,
//                          the composer must not process slash commands in any
//                          channel (persist as `workspaces.slash_commands_enabled`).
//   defaultDaemonId   — the workspace default daemon for slash commands
//   fallbackDaemonIds — ordered fallbacks, tried in order when the default is
//                       offline/inaccessible
//   model             — workspace default model for channel AI commands
//                       (null = auto-resolve, the zero-config default)
// Mutations are owner/admin-only and the server must enforce that.
export interface WorkspaceSlashConfig {
  slashCommandsEnabled: boolean;
  defaultDaemonId: string | null;
  fallbackDaemonIds: string[];
  model: { provider: string; model: string } | null;
}

// The server's flat wire shape (3 workspace columns). The client methods map
// to/from WorkspaceSlashConfig so callers never see this.
interface ServerWorkspaceSlashConfig {
  defaultSlashDaemonId: string | null;
  fallbackDaemons: string[];
  model: { provider: string; model: string } | null;
}

// CYBORG-992: floor for the jittered reconnect. Full jitter is uniform over the
// envelope, so `rand()` near zero used to mean an INSTANT redial — the same lockstep
// burst the jitter exists to break, just at the other end of the window, and the guest
// fleet is the bigger one. 250ms is invisible to a human and enough to spread a herd.
export const RECONNECT_MIN_MS = 250;

const MAX_RESTART_MARK_LEN = 64;

// The page's monotonic clock, or wall time where it is missing (then it reports no sleep).
function monotonicNow(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

/**
 * Reconnect backoff delay with FULL JITTER (AWS "Exponential Backoff And Jitter").
 *
 * The base envelope is the capped exponential `min(1000 * 2**attempt, 30_000)`;
 * full jitter then picks a uniformly-random point in `[RECONNECT_MIN_MS, envelope]`.
 * Without jitter every client that dropped on the same relay deploy
 * (`server_shutdown`) retries in lockstep — 1s, 2s, 4s, 8s… — slamming the
 * just-restarted single-process cloud relay in a synchronized thundering herd
 * (#504). Spreading each client across its own window de-synchronizes the herd
 * while preserving the same exponential growth of the upper bound.
 *
 * Pure (the caller injects `rand`) so the envelope and bounds are unit-testable.
 *
 * @param attempt 0-based reconnect attempt count.
 * @param rand    RNG returning [0, 1); defaults to Math.random.
 */
export function reconnectBackoffMs(attempt: number, rand: () => number = Math.random): number {
  // Cap the exponent at 5 (1000 * 2**5 = 32_000 already exceeds the 30_000 cap):
  // mathematically identical for every attempt, but avoids 2**attempt blowing up
  // to Infinity on a long-lived client (maxReconnectAttempts is unbounded).
  const envelope = Math.min(1000 * 2 ** Math.min(attempt, 5), 30_000);
  return Math.max(RECONNECT_MIN_MS, Math.round(rand() * envelope));
}

/** One envelope's worth (a field is capped here, so a bad relay value cannot park a client). */
const HINT_FIELD_CAP_MS = 60_000;

/**
 * The relay's redial hint on `cyborg:server_shutdown` (relay-shutdown.ts): the first redial after
 * the close is `reconnectAfterMs + rand × reconnectSpreadMs`, spreading the fleet past the
 * replacement's boot. Undefined from an old relay without the fields: plain backoff then.
 */
export function hintedReconnectDelayMs(
  payload: { reconnectAfterMs?: unknown; reconnectSpreadMs?: unknown },
  rand: () => number = Math.random,
): number | undefined {
  const field = (v: unknown) =>
    typeof v === "number" && Number.isFinite(v) && v >= 0
      ? Math.min(v, HINT_FIELD_CAP_MS)
      : undefined;
  const after = field(payload.reconnectAfterMs);
  const spread = field(payload.reconnectSpreadMs);
  if (after === undefined || spread === undefined) return undefined;
  return Math.max(RECONNECT_MIN_MS, after + Math.round(rand() * spread));
}

function fromServerSlashConfig(c: ServerWorkspaceSlashConfig): WorkspaceSlashConfig {
  return {
    slashCommandsEnabled: true,
    defaultDaemonId: c.defaultSlashDaemonId,
    fallbackDaemonIds: c.fallbackDaemons ?? [],
    model: c.model,
  };
}

// In-flight read coalescing (see request()). The ALLOWLIST is exhaustive by
// design: only an idempotent, side-effect-free READ may share one wire request
// across concurrent identical callers. NEVER add a mutation here — a coalesced
// mutation would silently drop the second caller's write. Also deliberately
// EXCLUDED are the side-effecting "reads" (refresh_providers, open_browser_pairing,
// composio_login_*) and the rare interactive/probe reads (list_directory,
// list_terminals, cli status/version) — those keep firing per-call, exactly as
// today. An unlisted type is simply not coalesced, so omitting a read is safe.
const COALESCEABLE_READ_TYPES = new Set<string>([
  // The handshake itself. MEASURED x2 on every page load (CYBORG-989 browser trace),
  // because it has TWO callers by design and always has: doConnect()'s `open` handler
  // fires it opportunistically so RPCs parked in waitForAuth unblock even when the app
  // layer is slow, and connectToServer()/handleReconnect() await their OWN
  // client.authenticate() to read the user payload back. Both land on the same socket in
  // the same tick, so they share one frame here and BOTH still get the answer they need —
  // which is strictly better than deleting either call site (each exists for a reason the
  // other does not serve). It is idempotent by the relay's own contract (see the doConnect
  // comment) and the coalescing key carries the token, so a re-auth with a DIFFERENT token
  // is a different key and always goes out fresh.
  //
  // Idempotence was never the whole safety argument, though — LIFETIME is (CYBORG-991).
  // An entry that outlives the socket it was sent on is a promise that can no longer be
  // answered, and handing it to the next caller is fatal HERE in a way it is not for an
  // ordinary read: a drop mid-handshake would have the reconnected socket's authenticate()
  // share the dead promise, send NO auth frame on the new socket, and leave the client
  // open-but-unauthenticated until the user reloaded (every gated RPC failing "Not
  // authenticated", the timeout reading as transient to runReconnectBurst). That is why
  // inflightReads is cleared on close AND at the top of doConnect() — see both.
  "cyborg:auth",
  // Chat / workspace bootstrap + navigation + reconnect (the reported dupes: the
  // default channel's messages fetched twice on entry, and reconnect re-firing
  // fetch_unread/fetch_channels/etc.).
  "cyborg:fetch_messages",
  "cyborg:fetch_dm_messages",
  "cyborg:fetch_dm_since",
  "cyborg:fetch_unread",
  "cyborg:fetch_activity",
  "cyborg:fetch_workspace_activity",
  "cyborg:fetch_channels",
  // CYBORG-989: bootstrap replaces six of the reads on this list, so it inherits
  // their coalescing or it un-does it. On a reconnect the active workspace is
  // bootstrapped twice — once by subscribeAndSeedAllWorkspaces for every workspace,
  // once by reconcileWorkspacePanels for the open one — which is exactly the
  // duplicate pair fetch_unread/fetch_channels were put here for.
  "cyborg:bootstrap",
  "cyborg:list_channels",
  "cyborg:fetch_workspaces",
  "cyborg:list_members",
  "cyborg:fetch_notification_prefs",
  "cyborg:fetch_drafts",
  "cyborg:sync",
  "cyborg:thread_counts",
  "cyborg:fetch_threads",
  "cyborg:fetch_thread",
  // Same deep-link open as fetch_thread, one tick earlier: the channel route effect
  // and SharedFilesPanel can both resolve the same root id.
  "cyborg:fetch_message",
  "cyborg:list_saved",
  "cyborg:fetch_user_statuses",
  "cyborg:list_pending_invitations",
  "cyborg:search",
  "cyborg:search_tasks",
  "cyborg:search_pages",
  // Daemons / access — fired on workspace entry, panels, and reconnect.
  "cyborg:list_daemons",
  "cyborg:fetch_daemon_info",
  "cyborg:fetch_backend_status",
  "cyborg:fetch_daemon_access",
  "cyborg:fetch_daemon_access_requests",
  "cyborg:fetch_cybo_transfer_requests",
  "cyborg:fetch_page_access_requests",
  "cyborg:fetch_page_grantees",
  "cyborg:list_daemon_workspaces",
  // Cybos / agents — workspace entry + panels.
  "cyborg:fetch_cybos",
  "cyborg:list_agents",
  "cyborg:list_daemon_sessions",
  "cyborg:list_cybo_memory_files",
  // CONVERSATION OPEN — the agent-DM and work-session surfaces (CYBORG-989). These are
  // the two conversation surfaces the coalescer never covered: a channel and a human DM
  // dedup through fetch_messages/fetch_dm_messages above, while an agent DM and a
  // session detail view opened with NO dedup at all, so every re-entrant open (the mount
  // effect racing the `connection` retry, the `daemon_status:"online"` retry, and — on
  // the session view — a `work_session_changed` echo landing mid-open) put a second
  // identical read on the wire. All four are pure, idempotent daemon/relay READS: they
  // hydrate a timeline or a subagent snapshot and mutate nothing. Adding them HERE rather
  // than guarding each call site is what makes a fifth conversation surface inherit the
  // dedup for free.
  "cyborg:fetch_agent_timeline",
  "cyborg:fetch_session_work",
  "cyborg:fetch_session_context",
  "cyborg:fetch_provider_subagents",
  // The reconnect burst's archived-session refill. MEASURED x4 on ONE reconnect, the worst
  // offender in the trace — and the reason is visible as a natural experiment in that same
  // trace: runReconnectBurst fires fetchSessions / fetchCybos / seedThreadCounts /
  // seedUserStatuses as a group of `void`-ed, UN-AWAITED calls, and coalesceBurst's
  // deliberate trailing re-run (coalesce-burst.ts) starts a SECOND burst while the first
  // group is still in flight. Three of those four collapsed to a single frame because
  // fetch_cybos / thread_counts / fetch_user_statuses are already on this list; only
  // list_archived_sessions doubled, because it was not. Same code path, same re-run, one
  // variable. A pure read, and `cursor` rides the key — so "Show more" paging (a page with
  // a cursor) is a different key and is never merged into the cursor-less first page.
  "cyborg:list_archived_sessions",
  // Channel detail / members — on channel open.
  "cyborg:fetch_channel_members",
  "cyborg:fetch_channel_cybos",
  "cyborg:get_channel_role",
  "cyborg:get_channel_file_count",
  "cyborg:fetch_channel_files",
  "cyborg:fetch_dm_files",
  // Workspace config catalogs — settings + entry.
  "cyborg:get_workspace_autonomy",
  "cyborg:list_prompt_templates",
  "cyborg:mcp_list",
  "cyborg:webhook_list",
  // Tasks board — on tab open.
  "cyborg:fetch_tasks",
  "cyborg:fetch_projects",
  "cyborg:fetch_project_states",
  "cyborg:fetch_project_labels",
  "cyborg:fetch_cycles",
  "cyborg:fetch_modules",
  "cyborg:fetch_task_views",
  "cyborg:fetch_pages",
  "cyborg:fetch_page",
  "cyborg:fetch_task_activity",
  "cyborg:fetch_task_links",
  "cyborg:fetch_project_roles",
  "cyborg:fetch_project_brief",
  // Settings → Logs (CYBORG-1155): pure relay reads, keyed by filters/day/cursor.
  "cyborg:fetch_logs_timeline",
  "cyborg:fetch_logs_run_detail",
  "cyborg:fetch_logs_group_events",
  "cyborg:fetch_logs_session_detail",
  // Schedules / recipes — panels.
  "cyborg:list_schedules",
  "cyborg:list_recipes",
  "cyborg:schedule_message_list",
  "cyborg:list_commands",
  // Push / license — entry.
  "cyborg:get_vapid_key",
  "cyborg:fetch_license_pool",
]);

// CYBORG-1210: list reads the relay can answer `notModified` (rpc-etag.ts on the relay).
const ETAG_READ_TYPES: ReadonlySet<string> = new Set([
  "cyborg:fetch_activity",
  "cyborg:thread_state",
  "cyborg:list_agents",
  "cyborg:fetch_cybos",
  "cyborg:list_archived_sessions",
  "cyborg:list_daemons",
]);
const MAX_ETAG_HELD = 64;

// Deterministic key for read coalescing: sort object keys (so `{a,b}` and `{b,a}`
// collide) and drop `undefined` values (mirroring JSON.stringify, so an explicit
// `field: undefined` matches an omitted field). Arrays keep their order.
function stableStringify(value: unknown): string {
  return JSON.stringify(stableNormalize(value)) ?? "";
}

function stableNormalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableNormalize);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      const v = (value as Record<string, unknown>)[key];
      if (v !== undefined) out[key] = stableNormalize(v);
    }
    return out;
  }
  return value;
}

/**
 * One PENDING Connect invitation addressed to the signed-in account, as
 * `cyborg:pending_external_invites_response` carries it.
 *
 * Declared HERE rather than beside `ExternalInvitePreview` in state/connect.svelte.ts
 * because it is a wire-only payload: no store holds it, the surface that renders it
 * fetches it and drops the row on accept. `ExternalInvitePreview` lives over there
 * precisely because the Connect STORE shares that shape with the wire.
 */
export interface PendingConnectInvitation {
  /** The INVITING organization's workspace — the id `acceptExternalInvite` takes. */
  hostWorkspaceId: string;
  hostWorkspaceName: string;
  /**
   * The one channel this invitation opens. Null when it was deleted between the
   * invite and now — legal, and then the accept lands on the workspace instead.
   */
  channelId: string | null;
  channelName: string | null;
  /** Who sent it, for the "X at Acme shared #… with you" line. Null when that account is gone. */
  invitedByName: string | null;
  /** epoch ms; a pending invitation always has a deadline. */
  expiresAt: number | null;
}

export class SlackClient<EventMap extends SlackEventMap = SlackEventMap> {
  private ws: WebSocket | null = null;
  private url = "";
  private token = "";
  // Explicit login/endpoint changes invalidate parked RPCs, including same-account login.
  // Automatic network reconnects stay within this session.
  private sessionRevision = 0;
  // Injected silent-refresh hook (CYBORG-87). Given the guest WS url this client
  // connected to, trade the stored refresh token for a fresh access token
  // (`POST /api/auth/refresh`). Set by the app layer; unset in headless/core
  // contexts, in which case a `4003` rejection surfaces `auth_expired` directly.
  reauth?: (wsUrl: string) => Promise<RefreshOutcome>;
  // True while the ONE refresh attempt for the current rejection episode is in
  // flight or its reconnect is pending. Guarantees exactly one refresh per
  // episode (mirrors the CLI's single retry — never loops); reset when auth next
  // succeeds, so a later, independent expiry can heal again.
  private reauthing = false;
  // CYBORG-989: whether the connected relay serves cyborg:bootstrap, as reported by
  // the last cyborg:auth. False until an auth answers otherwise — so an older relay
  // (or one with CYBORG7_BOOTSTRAP_SNAPSHOT off) never sees a bootstrap RPC at all.
  bootstrapSupported = false;
  // When the CURRENT run of transient refresh failures started (null = none in
  // progress), and how long that run may last before the session is treated as
  // unrecoverable. A transient outcome ("error": network / timeout / 5xx / 429)
  // keeps the token and reconnects, and each reconnect that OPENS resets
  // reconnectAttempts — so a relay that keeps answering `4003` while the refresh
  // keeps failing is a ~1s loop with no growing backoff behind it. Rate limiting
  // is where that bites: /api/auth/refresh allows 20 per 5 minutes, the loop
  // spends that in seconds, and every later attempt is answered 429 — which is
  // not 401/403, so it never surfaced re-login. The user sat on "Reconnecting…"
  // forever. The budget bounds ONLY the run: a blip (or a relay restart) is well
  // inside it and still self-heals, while a failure that outlasts it falls
  // through to the same auth_expired path a dead refresh token takes.
  private reauthFailingSince: number | null = null;
  private static readonly REAUTH_TRANSIENT_BUDGET_MS = 120_000;
  private listeners = new Map<string, Set<EventHandler<unknown>>>();
  private pendingRequests = new Map<string, PendingRequest>();
  // In-flight coalescing for idempotent reads (COALESCEABLE_READ_TYPES): key =
  // `type:stableStringify(params)`. Concurrent identical reads share the ONE
  // promise here; the entry is removed when it settles (see request()), so a read
  // that starts after the first resolves fires fresh.
  private inflightReads = new Map<string, Promise<unknown>>();
  private etagHeld = new Map<string, { etag: string; body: string }>();
  private requestCounter = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  // No give-up cap: a mobile client can be backgrounded for a long time, and we
  // want it to keep retrying (the backoff is capped at 30s in scheduleReconnect)
  // so it reconnects whenever the network/app returns, instead of going
  // permanently "disconnected" after a handful of tries.
  private maxReconnectAttempts = Number.POSITIVE_INFINITY;
  private intentionalClose = false;
  protected requestTimeoutMs = 15_000;
  // A WebSocket can sit in CONNECTING indefinitely when the TCP/TLS/HTTP-upgrade
  // handshake stalls (relay cold start, load-balancer drain, flaky network) —
  // neither `open` nor `error` ever fires. doConnect() bounds the wait with this
  // timeout so it can never hang forever; without it a stalled first connect left
  // connectToServer()/handleReconnect awaiting indefinitely and the UI stuck on a
  // spinner with NO error until a manual reload (the signup verify-then-hang bug).
  protected connectTimeoutMs = 15_000;
  // Transport-level liveness heartbeat (#503). A half-open / zombie socket
  // (proxy idle-timeout, NAT/VPN drop, sleep-wake, relay redeploy whose FIN never
  // arrives) can read OPEN forever without firing `close`, so neither the close
  // handler nor scheduleReconnect() ever runs and the client is "online to
  // itself, offline to everyone else". The heartbeat pings every interval and,
  // on a missed pong, closes the socket so the close handler reconnects. It runs
  // for the LIFE OF THE CONNECTION — not foreground-gated — so a backgrounded-
  // but-open desktop/web tab recovers too (on mobile the OS suspends the timer
  // while backgrounded, so a frozen socket is never pinged; the foreground hook
  // covers resume).
  private static readonly HEARTBEAT_INTERVAL_MS = 30_000;
  private static readonly HEARTBEAT_TIMEOUT_MS = 10_000;
  // Any inbound frame is proof of life (a pong can queue behind a big history frame);
  // a half-open link that still trickles frames is closed once no pong came for this long.
  private static readonly HEARTBEAT_PONG_CEILING_MS = 45_000;
  private lastInboundAt = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatInFlight = false;
  // ─── Disconnect instrumentation (MEASUREMENT ONLY) ───────────────
  // Frozen renderer vs dropped network: a suspended renderer cannot fire its 30s
  // heartbeat on time, a network drop leaves it on time. `driftTracker` records
  // that lateness; the rest stamp the outage for one report on the next `open`.
  // No reconnect decision reads any of these fields.
  private driftTracker = new DriftTracker(SlackClient.HEARTBEAT_INTERVAL_MS);
  private lastCloseAt: number | null = null;
  private lastCloseCode: number | null = null;
  private suspension = new SuspensionMeter(SUSPENSION_TICK_MS);
  private suspensionTimer: ReturnType<typeof setInterval> | null = null;
  // The draining relay's signed restart mark, echoed once on the first auth to a later socket.
  private restartMark: { mark: string; socketGen: number } | undefined;
  // A socket drop announced as a relay restart (deploy notice or 1001 close). Bound to
  // the socket that got the notice, so a NEW relay's timeouts stay faults; cleared on re-auth.
  private plannedRestart: { socketGen: number; etaMs: number; until: number } | null = null;
  // The relay's hinted first redial after a deploy notice; one-shot, consumed by scheduleReconnect.
  private hintedRedialMs: number | undefined;
  // Counts sockets this client has opened; a request records the one it was sent on.
  private socketGen = 0;
  private sawCloseEvent = false;
  // True between THIS client closing the socket itself (it judged the socket dead,
  // or chose to restart it) and that socket's `close`: nothing came from outside.
  private selfInitiatedClose = false;
  private userInitiatedClose = false;
  // Reports waiting out their jittered send delay. A Set, not one handle: a second
  // reconnect inside the delay schedules its own, and logout must cancel them all.
  private pendingReportTimers = new Set<ReturnType<typeof setTimeout>>();
  // Highest per-workspace broadcast seq observed on THIS connection (#499). Used
  // to detect a forward gap (lost broadcast on an open socket). Cleared on close
  // so a reconnect re-seeds from the first live event after drainSync has already
  // caught history up — avoiding a spurious gap at the reconnect boundary.
  //
  // The relay's seq is a per-workspace GLOBAL counter shared by channel messages
  // (fanned out workspace-wide → dense for every client) AND DMs (delivered only
  // to participants → a DM between OTHER users consumes a seq this client never
  // receives). So a forward "gap" is NOT always a real loss; the app coalesces /
  // rate-limits the resulting catch-up (it's idempotent + dedups, so a false
  // positive only costs one bounded sync RPC).
  private broadcastSeqs = new Map<string, number>();
  // Auth gate. A freshly-opened socket is unauthenticated until cyborg:auth
  // succeeds on it. The relay SILENTLY DROPS any non-auth RPC on an
  // unauthenticated socket (no response), so an RPC that races the
  // post-reconnect auth would hang for requestTimeoutMs and leave the view
  // permanently empty until a manual reload. request() therefore gates every
  // non-auth RPC on this flag, not just on the socket being OPEN.
  // Public so fire-and-forget senders (sendMessage/sendDm in app.svelte.ts) can
  // check it before a raw send — an open-but-unauthenticated socket would
  // otherwise accept the send and have the relay silently drop it.
  private _authenticated = false;
  get authenticated(): boolean {
    return this._authenticated;
  }
  set authenticated(next: boolean) {
    if (this._authenticated === next) return;
    this._authenticated = next;
    this.emit("auth_state", { authenticated: next } as EventMap["auth_state"]);
  }
  private authWaiters = new Set<{ resolve: () => void; reject: (err: Error) => void }>();

  // CYBORG-1187: bumps every time the sign-in token changes (connect, silent
  // refresh). The media pass uses it to tell "same refused token" from "new sign-in".
  public authTokenRevision = 0;

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // THE gate of every fire-and-forget frame (watch, app state, active view, typing, reads,
  // drafts): an open socket that has authenticated. These run from Svelte effects and timers,
  // where sendRaw's "Not connected" throw escapes into the flush and drops every update in it
  // (desktop 0.0.929 freeze); `authenticated` alone lags a closing socket until its close
  // event. Each frame is re-asserted after auth or on its next change, so skipping loses nothing.
  canSend(): boolean {
    return this.authenticated && this.connected;
  }

  /** Is there a sign-in token to put on an authenticated HTTP call? */
  get hasAuthToken(): boolean {
    return this.token.length > 0;
  }

  connect(url: string, token: string): Promise<void> {
    this.sessionRevision += 1;
    this.authTokenRevision += 1;
    this.authenticated = false;
    if (this.ws) {
      this.intentionalClose = true;
      this.ws.close();
      this.ws = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.url = url;
    this.token = token;
    this.intentionalClose = false;
    this.reconnectAttempts = 0;
    // MEASUREMENT ONLY — connect() is the explicit NEW-SESSION entry (app boot,
    // re-login, endpoint switch), never the automatic reconnect (that one goes
    // straight to doConnect(), including the 4003 silent re-auth). So whatever
    // outage is stamped here belongs to a session the user already left: drop it
    // instead of reporting the time they spent on the login screen as downtime.
    this.clearReconnectTelemetry();
    this.driftTracker.reset();
    this.cancelPendingReconnectReports();
    this.restartMark = undefined; // a new session never echoes the last one's deploy
    return this.doConnect();
  }

  // The workspaces the relay's membership floor admits on THIS socket: the memberships it read
  // at auth, then each create or subscribe it answered. A workspace joined after auth is outside
  // it until its subscribe lands, and every read before then is refused ("not a member").
  private heldWorkspaceIds = new Set<string>();

  holdsWorkspace(workspaceId: string): boolean {
    return this.heldWorkspaceIds.has(workspaceId);
  }

  protected noteHeldWorkspace(workspaceId: string): void {
    this.heldWorkspaceIds.add(workspaceId);
  }

  async authenticate(): Promise<{
    user: {
      id: string;
      email: string;
      name: string | null;
      imageUrl?: string | null;
      canonicalIds?: string[] | null;
    };
    workspaces: { id: string; name: string; avatarUrl: string | null; role: string }[];
    externalWorkspaces?: ExternalWorkspacePayload[];
  }> {
    const sessionRevision = this.sessionRevision;
    const socket = this.ws;
    const resp = await this.request<{
      user: {
        id: string;
        email: string;
        name: string | null;
        imageUrl?: string | null;
        canonicalIds?: string[] | null;
      };
      workspaces: { id: string; name: string; avatarUrl: string | null; role: string }[];
      externalWorkspaces?: ExternalWorkspacePayload[];
      bootstrap?: boolean;
    }>("cyborg:auth", { token: this.token, restartMark: this.takeRestartMark() });
    if (this.sessionRevision !== sessionRevision || this.ws !== socket) {
      throw new TransportError("session_changed", "Client session changed during authentication");
    }
    // CYBORG-989: does this relay serve cyborg:bootstrap? Read from the auth
    // response so the shell can SKIP the call rather than probe with a round trip
    // that is going to come back `supported: false`. An older relay omits the
    // field → false → the per-RPC workspace open, unchanged.
    this.bootstrapSupported = resp.bootstrap === true;
    this.heldWorkspaceIds = new Set(resp.workspaces.map((w) => w.id));
    // Socket is now authenticated — release any RPCs parked in waitForAuth.
    this.authenticated = true;
    this.plannedRestart = null;
    this.hintedRedialMs = undefined;
    // A token was accepted → the current rejection episode (if any) is over, so
    // a FUTURE expiry is free to trigger a fresh single refresh (never loops),
    // and the transient-failure budget starts over with it.
    this.reauthing = false;
    this.reauthFailingSince = null;
    for (const w of this.authWaiters) w.resolve();
    this.authWaiters.clear();
    return resp;
  }

  // Presence auto-heal (Part B): a lightweight liveness ping. Sends `cyborg:ping`
  // and resolves `true` if ANY round-trip response with the matching requestId
  // arrives within `timeoutMs` — INCLUDING a `cyborg:error` (an error reply still
  // proves the socket is alive, which keeps this backward-compatible with relays
  // that don't recognize `cyborg:ping`). Resolves `false` only on timeout or when
  // not connected. Unlike request(), it does NOT wait for auth and does NOT
  // reject on an error response — both error and success count as "alive".
  ping(timeoutMs: number, opts?: { extendWhileReceivingUpToMs?: number }): Promise<boolean> {
    if (!this.connected) return Promise.resolve(false);
    return new Promise<boolean>((resolve) => {
      const requestId = `ping_${++this.requestCounter}_${Date.now()}`;
      const sentAt = Date.now();
      const ceilingMs = opts?.extendWhileReceivingUpToMs ?? timeoutMs;
      let settled = false;
      const done = (alive: boolean) => {
        if (settled) return;
        settled = true;
        this.pendingRequests.delete(requestId);
        resolve(alive);
      };
      const onDeadline = () => {
        const now = Date.now();
        const quietMs = now - Math.max(this.lastInboundAt, sentAt);
        const leftMs = sentAt + ceilingMs - now;
        if (quietMs >= timeoutMs || leftMs <= 0) {
          done(false);
          return;
        }
        const entry = this.pendingRequests.get(requestId);
        if (!entry) return;
        entry.timer = setTimeout(onDeadline, Math.min(timeoutMs - quietMs, leftMs));
      };
      const timer = setTimeout(onDeadline, timeoutMs);
      // Any RELAY response keyed by this requestId — resolve/reject alike —
      // proves the socket round-trips. handleMessage() calls resolve() for a pong
      // and reject() for a cyborg:error; both count as "alive". The one exception
      // is disconnect(), which rejects pending requests with "Client disconnected"
      // during an intentional teardown — that torn-down socket is NOT alive.
      this.pendingRequests.set(requestId, {
        resolve: () => done(true),
        // A relay error round-trip still proves the socket is alive → true. But
        // the client rejects its own in-flight requests whenever the socket goes
        // away — "Client disconnected" on an intentional teardown (disconnect())
        // and "Connection lost" on a drop (the close handler, CYBORG-1171).
        // Neither is a live socket, so both report false; shared constants, so the
        // literals can never drift from the throw sites.
        reject: (err) =>
          done(
            err?.message !== CLIENT_DISCONNECTED_ERROR && err?.message !== CONNECTION_LOST_ERROR,
          ),
        timer,
      });
      try {
        this.send({ type: "cyborg:ping", requestId });
      } catch {
        // Socket went down between the connected check and the send — not alive.
        clearTimeout(timer);
        done(false);
      }
    });
  }

  async setProfileImage(imageUrl: string | null): Promise<void> {
    await this.request("cyborg:set_profile_image", { imageUrl });
  }

  async setProfileName(name: string): Promise<void> {
    await this.request("cyborg:set_profile_name", { name });
  }

  // The caller's OWN job title in one workspace ("Head of Sales") — shown under their
  // name and on the roster agents read. Trimmed server-side; "" clears it; > 80 refused.
  async setFunctionalRole(workspaceId: string, role: string): Promise<void> {
    await this.request("cyborg:set_functional_role", { workspaceId, role });
  }

  // Per-workspace identity override (0121) on the caller's own membership: the
  // avatar IN ONE workspace. Distinct from setProfileImage, which is user-wide. Each field updates
  // INDEPENDENTLY — omit one to leave it untouched, pass `null` to clear the
  // override back to "use my global profile".
  // `displayEmailId` (0125) joins them: which of my verified addresses THIS
  // workspace shows. Same independent-field contract — `null` clears the override
  // back to "use my account default".
  async setWorkspaceProfile(
    workspaceId: string,
    fields: {
      avatarUrl?: string | null;
      displayEmailId?: string | null;
    },
  ): Promise<void> {
    await this.request("cyborg:set_workspace_profile", { workspaceId, ...fields });
  }

  // Native mobile push: register/unregister this device's FCM token with the
  // relay so it can deliver push to the phone when the user is offline.
  async registerFcmToken(token: string, platform: string, deviceName?: string): Promise<void> {
    await this.request("cyborg:fcm_register", { token, platform, deviceName });
  }

  async unregisterFcmToken(token: string): Promise<void> {
    await this.request("cyborg:fcm_unregister", { token });
  }

  // Tell the relay whether this client is foregrounded or backgrounded. A mobile
  // OS keeps the WebSocket nominally OPEN while the app is suspended, so the relay
  // would otherwise count the user as "active" and SKIP the push (it only pushes
  // to non-active users). Flagging "background" makes the relay deliver the push
  // the frozen socket can't. Fire-and-forget.
  // The desktop app's own daemon id (desktop bridge), sent in hello and app_state.
  private localDaemonId: string | undefined;

  setLocalDaemonId(daemonId: string): void {
    this.localDaemonId = daemonId;
  }

  setAppState(state: "active" | "background"): void {
    // Guard: send() throws "Not connected" on a closed/connecting socket. Skip
    // silently — connectToServer/handleReconnect re-assert app state right after
    // auth, so a drop during a toggle can't leave the relay with a stale value.
    // `clientType` lets the relay suppress the MOBILE push when the user is
    // actively on a desktop/web session (v1 parity: "active desktop ⇒ quiet phone").
    if (this.canSend()) {
      this.send({
        type: "cyborg:app_state",
        state,
        clientType: this.clientSurface(),
        ...(this.localDaemonId ? { localDaemonId: this.localDaemonId } : {}),
      });
    }
  }

  // Push decisions E-H: the conversation this client has open, so the relay can skip the OS
  // push for it and clear pushes already delivered for it. Skipped when closed;
  // syncAppStateToRelay re-sends right after auth.
  sendActiveView(frame: {
    workspaceId: string;
    view:
      | { kind: "dm"; peerId: string }
      | { kind: "channel"; channelId: string }
      | { kind: "thread"; rootId: string }
      | null;
    foreground: boolean;
  }): void {
    if (this.canSend()) this.send({ type: "cyborg:active_view", ...frame });
  }

  // ─── CYBORG-990: agent live-stream watches ────────────────────────────────
  //
  // The relay stops fanning an agent's per-token `timeline` deltas to every guest in
  // the workspace and sends them only to the sockets that say they are rendering them.
  // This is the client half of that: an EXPLICIT frame, because there is nothing else
  // honest to infer it from — `fetchAgentTimeline` short-circuits on
  // `isServerHydrated`, so re-opening an agent issues no RPC at all, and no stream is
  // ever torn down (`clearStream` has no callers). Inferring interest from the fetch
  // meant a returning user got dead air.
  //
  // REFCOUNTED because several surfaces legitimately render the same agent at once —
  // the agent route, the work-session body, a session chip in a list. Each holds one
  // ref for as long as it is mounted and drops it on unmount, so the watched set is
  // exactly "what is on screen right now" with no eviction policy to get wrong. The
  // frame goes out on 0→1 and the unwatch on 1→0; the map IS the reconnect replay set.
  private agentWatchRefs = new Map<string, number>();

  // Take a watch on an agent's live stream. Returns the release function — call it on
  // unmount. Safe to call while disconnected: the ref is recorded and
  // `resendAgentWatches` puts it on the wire once the socket is authenticated.
  watchAgent(workspaceId: string, agentId: string): () => void {
    if (!workspaceId || !agentId) return () => {};
    // Keyed by BOTH, matching the relay's own watch key: a workspace switch leaves the
    // old key behind rather than silently re-pointing a watch at another tenant.
    const key = `${workspaceId}	${agentId}`;
    const next = (this.agentWatchRefs.get(key) ?? 0) + 1;
    this.agentWatchRefs.set(key, next);
    // `authenticated`, not `connected`: the relay silently DROPS a send on an
    // open-but-unauthenticated socket (see the flag's own doc), so gating on OPEN alone
    // would burn a frame that never lands. Harmless either way — resendAgentWatches
    // replays the whole map right after auth — but there is no reason to send it twice.
    if (next === 1 && this.canSend()) {
      this.send({ type: "cyborg:watch_agent", workspaceId, agentId });
    }
    let released = false;
    return () => {
      // Idempotent: a Svelte effect can run its teardown more than once, and a double
      // release would drop a ref another live surface still holds.
      if (released) return;
      released = true;
      const remaining = (this.agentWatchRefs.get(key) ?? 1) - 1;
      if (remaining > 0) {
        this.agentWatchRefs.set(key, remaining);
        return;
      }
      this.agentWatchRefs.delete(key);
      if (this.canSend()) {
        this.send({ type: "cyborg:unwatch_agent", workspaceId, agentId });
      }
    };
  }

  // Re-assert every held watch on a freshly authenticated socket. The relay's watch set
  // is CONNECTION-scoped (a new GuestConnection starts empty), so without this a
  // reconnect silently stops the tokens for every agent still on screen. Called from
  // the same two post-auth points as setAppState, for the same reason.
  //
  // "Visible wins" is structural here rather than a sort: only a MOUNTED surface holds
  // a ref, so this set is already exactly what the user is looking at. That is the rule
  // docs/timeline-sync.md asks for, without the ordering bug that made
  // catchUpAgentTimelines drop the foreground agent past its cap.
  resendAgentWatches(): void {
    if (!this.canSend()) return;
    for (const key of this.agentWatchRefs.keys()) {
      const tab = key.indexOf("	");
      if (tab < 0) continue;
      this.send({
        type: "cyborg:watch_agent",
        workspaceId: key.slice(0, tab),
        agentId: key.slice(tab + 1),
      });
    }
  }

  // Test/diagnostic seam: the `workspaceId 	 agentId` keys this client holds a watch on.
  watchedAgentKeys(): string[] {
    return Array.from(this.agentWatchRefs.keys());
  }

  // Which surface this client is: the Electron desktop shell exposes
  // `window.cyborg7Desktop`; the Tauri mobile shell exposes `window.__TAURI__`;
  // anything else is a plain browser tab.
  private clientSurface(): "mobile" | "desktop" | "web" {
    if (typeof window === "undefined") return "web";
    const w = window as {
      __TAURI__?: unknown;
      __TAURI_INTERNALS__?: unknown;
      cyborg7Desktop?: unknown;
    };
    if (w.__TAURI_INTERNALS__ ?? w.__TAURI__) return "mobile";
    if (w.cyborg7Desktop) return "desktop";
    return "web";
  }

  // Relay push: a new desktop version shipped → kick the app's own updater now
  // (collapses its 30-min poll to seconds). Desktop-only — web/mobile have no
  // updater. The updater no-ops if already current, so no version-compare needed.
  private triggerDesktopUpdateCheck(): void {
    if (this.clientSurface() !== "desktop") return;
    window.cyborg7Desktop?.update?.check?.();
  }

  disconnect(): void {
    this.sessionRevision += 1;
    this.authenticated = false;
    this.intentionalClose = true;
    // Measurement only. Cancel HERE, not only in the close handler: with the socket
    // already down (ws null) no close event comes, and a pending report would
    // still fire with the logged-out user's token.
    this.cancelPendingReconnectReports();
    this.restartMark = undefined;
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.failPendingRequests(() => new Error(CLIENT_DISCONNECTED_ERROR), false);
    this.emit("connection", { status: "disconnected" } as EventMap["connection"]);
  }

  // Fail every RPC that is already on the wire, because its socket is gone. None of
  // them can be answered — the relay replies into a socket nobody is listening on,
  // and the requestId lives only on that connection — so leaving them pending buys
  // the caller nothing but the full response budget (15s) and then "Request <type>
  // timed out": a slowness story for what is really a dropped connection. That is
  // CYBORG-1171 — the Tasks board drains 12 sequential pages on a 1,196-task
  // workspace, and one page caught by a drop painted "Request cyborg:fetch_tasks
  // timed out" over the whole board.
  //
  // `deliveryUnknown` picks WHICH reject a request that carries a delivery verdict
  // (cyborg:send_agent_prompt) gets. A drop leaves delivery genuinely unknown — the
  // frame may have reached the relay before the link died — so it takes the raw
  // reject and stays "unknown", exactly as the response timeout does. An intentional
  // teardown keeps the wrapper it has always had.
  //
  // The map is drained BEFORE anything is rejected: a reject handler can re-enter
  // the client (ping()'s done() deletes its own entry), and an already-empty map
  // makes a double reject impossible. Each entry's response timer is cleared with
  // it, so no timeout can fire behind the rejection.
  private failPendingRequests(failure: () => Error, deliveryUnknown: boolean): void {
    if (this.pendingRequests.size === 0) return;
    const pending = [...this.pendingRequests.values()];
    this.pendingRequests.clear();
    for (const entry of pending) {
      clearTimeout(entry.timer);
      const reject = (deliveryUnknown ? entry.rejectRaw : undefined) ?? entry.reject;
      reject(withTraceId(failure(), entry.traceId));
    }
  }

  // Treat the current socket as dead and reconnect immediately, bypassing the
  // backoff. Used by the app layer when a heartbeat probe times out or the app
  // returns to the foreground after a freeze — a mobile OS can leave the socket
  // in a zombie OPEN state where neither close nor the auto-reconnect ever fires.
  // `userInitiated` (the sidebar button) is not an outage: the close below stamps nothing.
  forceReconnect(opts?: { userInitiated?: boolean }): void {
    if (this.intentionalClose) return;
    this.reconnectAttempts = 0; // restart backoff so the retry is near-instant
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      // Not an intentional close, so the existing close handler runs
      // scheduleReconnect() for us (now with attempts reset to 0).
      try {
        this.selfInitiatedClose = true; // measurement only: labels the close below
        // Only a click on a healthy socket is no outage; mid-outage the stamp is real.
        this.userInitiatedClose =
          opts?.userInitiated === true && this.connected && this.lastCloseAt === null;
        this.ws.close();
      } catch {
        // already closing/closed
      }
    } else {
      this.scheduleReconnect();
    }
  }

  // ─── Transport liveness heartbeat (#503) ─────────────────────────
  // Started on `open`, stopped on `close`/disconnect — one timer per connection.
  private startHeartbeat(): void {
    this.stopHeartbeat();
    if (typeof setInterval === "undefined") return; // SSR safety
    this.heartbeatTimer = setInterval(() => {
      void this.heartbeatTick();
    }, SlackClient.HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // One heartbeat: ping and, if no round-trip within the window, tear the socket
  // down so the `close` handler's scheduleReconnect() takes over. ping() resolves
  // true on ANY matching-requestId reply (incl. a relay error → backward-compat),
  // so only a genuine timeout / dead socket trips the teardown.
  private async heartbeatTick(): Promise<void> {
    // MEASUREMENT ONLY, deliberately FIRST: how late this fixed 30s tick lands is
    // the frozen-renderer signal, and it must be recorded even when the guards
    // below no-op the tick. Records a timestamp and nothing else.
    this.driftTracker.tick(Date.now());
    // ping() has its own timeout (< the interval), so ticks don't normally
    // overlap; guard anyway so a stalled tick can't pile up.
    if (this.heartbeatInFlight) return;
    if (!this.connected) return;
    // Capture the socket this tick is probing. If the connection drops mid-ping,
    // the ping() promise stays pending until its timeout; by the time it resolves
    // false, scheduleReconnect() may have already swapped in a NEW healthy socket.
    // Without this guard the stale tick would close that new socket. Only tear
    // down the socket we actually pinged, and only if it's still the current one.
    const probedWs = this.ws;
    this.heartbeatInFlight = true;
    let alive = false;
    try {
      alive = await this.ping(SlackClient.HEARTBEAT_TIMEOUT_MS, {
        extendWhileReceivingUpToMs: SlackClient.HEARTBEAT_PONG_CEILING_MS,
      });
    } finally {
      this.heartbeatInFlight = false;
    }
    if (alive) {
      // A freeze this socket survived is not the lead-up to a later outage.
      this.driftTracker.reset();
      this.driftTracker.tick(Date.now());
      return;
    }
    // Half-open socket. Don't fight an intentional teardown (logout/shutdown), and
    // don't close a socket that was already replaced by a reconnect mid-ping.
    if (this.ws === probedWs && probedWs && !this.intentionalClose) {
      try {
        this.selfInitiatedClose = true; // measurement only: labels the close below
        // The outage starts now: a browser fires `close` only after its handshake (up to ~60s).
        this.lastCloseAt ??= Date.now();
        probedWs.close();
      } catch {
        // Already closing/closed — the close handler (or next connect) covers it.
      }
    }
  }

  // ─── Disconnect instrumentation (MEASUREMENT ONLY) ───────────────
  // Forget the outage stamp. Called when an outage has been reported, and on the
  // intentional-close path where there was no outage to report at all.
  private clearReconnectTelemetry(): void {
    this.lastCloseAt = null;
    this.lastCloseCode = null;
    this.sawCloseEvent = false;
    this.selfInitiatedClose = false;
    this.userInitiatedClose = false;
    this.stopSuspensionMeter();
  }

  private cancelPendingReconnectReports(): void {
    for (const timer of this.pendingReportTimers) clearTimeout(timer);
    this.pendingReportTimers.clear();
    this.stopSuspensionMeter();
  }

  // Measurement only: ticks while the socket is down, so a lid shut mid-outage reads as asleep.
  private startSuspensionMeter(): void {
    if (this.suspension.running || typeof setInterval === "undefined") return;
    this.suspension.start(Date.now(), monotonicNow());
    this.suspensionTimer = setInterval(() => this.suspension.tick(Date.now()), SUSPENSION_TICK_MS);
  }

  private stopSuspensionMeter(): void {
    if (this.suspensionTimer) clearInterval(this.suspensionTimer);
    this.suspensionTimer = null;
    this.suspension.stop();
  }

  private takeRestartMark(): string | undefined {
    const held = this.restartMark;
    if (!held || held.socketGen === this.socketGen) return undefined; // not the socket that got it
    this.restartMark = undefined;
    return held.mark;
  }

  // Build the body NOW, from the outage that just ended, and send it after the
  // jittered delay so a mass reconnect does not double the relay's auth DB reads
  // at its peak. Synchronous and fully wrapped: a throw must never cost the user
  // their reconnect.
  private scheduleReconnectReport(downMs: number, asleepMs: number, attempts: number): void {
    try {
      const body = buildReconnectReport({
        downMs,
        driftMs: this.driftTracker.maxDriftMs(),
        asleepMs,
        attempts,
        sawClose: this.sawCloseEvent,
        closeCode: this.lastCloseCode,
        surface: this.clientSurface(),
      });
      if (typeof setTimeout === "undefined") return; // SSR → send nothing
      const timer = setTimeout(() => {
        this.pendingReportTimers.delete(timer);
        this.postReconnectReport(body);
      }, reconnectReportDelayMs());
      this.pendingReportTimers.add(timer);
    } catch {
      /* telemetry must never break reconnect: a throw here would cost the user their reconnect */
    }
  }

  // Reads only url + token, at SEND time: the token may have been refreshed by a
  // 4003 re-auth during the delay (the old one was rejected). NOT via
  // reportClientError: its client_log_error rate drives a CloudWatch alarm, and a
  // reconnect is a counter, not a fault. The token rides only in the header.
  private postReconnectReport(body: ReconnectReportBody): void {
    try {
      if (typeof fetch !== "function") return; // no fetch → send nothing
      // Throws on an unparseable URL; the catch below turns that into "send nothing".
      const base = wsUrlToHttpBase(this.url);
      const headers: Record<string, string> = { "content-type": "application/json" };
      if (this.token) headers.Authorization = `Bearer ${this.token}`;
      void fetch(`${base}/api/cyborg/client-reconnect`, {
        method: "POST",
        keepalive: true,
        mode: "cors",
        credentials: "omit",
        headers,
        body: JSON.stringify(body),
      })
        // intentional: telemetry is fire-and-forget — the POST fails whenever the network is still down, and that must never surface to the user or become an unhandled rejection.
        .catch(() => {});
    } catch {
      /* telemetry must never throw out of its timer: it would surface as an uncaught page error */
    }
  }

  on<K extends keyof EventMap & string>(event: K, handler: EventHandler<EventMap[K]>): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(handler as EventHandler<unknown>);
    return () => set!.delete(handler as EventHandler<unknown>);
  }

  // ─── Messaging ──────────────────────────────────────────────────

  sendMessage(
    workspaceId: string,
    channelId: string,
    text: string,
    mentions?: string[],
    attachments?: Attachment[],
    parentId?: string,
    // Client-generated id (#501): the relay echoes it on the broadcast so the
    // sender reconciles the echo to its exact optimistic bubble. Omitted →
    // legacy fromId+text reconciliation (still works with older relays).
    clientMsgId?: string,
    // #602 — text came from a composer prompt TEMPLATE: ask the server to expand
    // {channel}/{user}/{date} on send. Only set for template-sourced sends so a
    // normal message with literal braces is left untouched. Omitted otherwise.
    expandTemplate?: boolean,
    // /pages slash command (CYBORG-309): a page id the picker selected. The
    // server resolves it to a title/icon-only card at insert time — this is a
    // REFERENCE ONLY, never card data (see CyborgChannelMessageSchema.pageRef).
    pageRef?: string,
    // Epoch ms the human pressed Send; a replay carries its original compose time so
    // the relay can refuse a queue that is hours old (`stale_send`).
    composedAt?: number,
    // Ids picked from the @-autocomplete (vs `mentions`, which adds text matches).
    mentionPicks?: string[],
  ): void {
    this.send({
      type: "cyborg:channel_message",
      workspaceId,
      channelId,
      text,
      mentions,
      attachments,
      parentId,
      clientMsgId,
      ...(expandTemplate ? { expandTemplate: true } : {}),
      ...(pageRef ? { pageRef } : {}),
      ...(composedAt ? { composedAt } : {}),
      ...(mentionPicks?.length ? { mentionPicks } : {}),
    });
  }

  // Run a channel slash command (e.g. /summarize). The ack only confirms
  // dispatch — the actual result arrives asynchronously as a normal channel
  // message broadcast (cyborg:channel_message_broadcast).
  async slashCommand(
    workspaceId: string,
    channelId: string,
    trigger: string,
    args?: string,
    // Target daemon: slash commands are DAEMON_SPAWN forwards, and the relay
    // refuses to pick one itself when several daemons are connected.
    daemonId?: string,
    // A thread root: the command reads that thread and replies in it.
    parentId?: string,
  ): Promise<{
    ok: boolean;
    trigger: string;
    dispatched: string[];
    error?: string;
    // Ephemeral notices about clamped/ignored args (daemon-side parse). Shown
    // only to the sender as a local system note — never persisted. Absent on
    // clean input and from older daemons.
    warnings?: string[];
    // Set by the relay when no daemon could be resolved for the command — the UI
    // renders it as a system message in the channel (not small grey error text).
    systemAlert?: string;
    // Discriminates the no-daemon alerts so the UI can show a "Configure AI" CTA
    // for the unconfigured case (vs offline / generic slash errors).
    alertType?: "no_daemon_configured" | "slash_daemons_offline";
    channelId?: string | null;
  }> {
    return this.request("cyborg:slash_command", {
      workspaceId,
      channelId,
      trigger,
      ...(args ? { args } : {}),
      ...(daemonId ? { daemonId } : {}),
      ...(parentId ? { parentId } : {}),
    });
  }

  // ─── Prompt templates (#602 — reusable composer snippets) ────────
  // Workspace-scoped CRUD for composer templates. Routed relay-side (PG-direct)
  // / local-dispatcher; create/update/delete return ok + the row (or a friendly
  // error on a name clash / validation failure).

  async listPromptTemplates(workspaceId: string): Promise<{ templates: PromptTemplate[] }> {
    return this.request<{ templates: PromptTemplate[] }>("cyborg:list_prompt_templates", {
      workspaceId,
    });
  }

  async createPromptTemplate(
    workspaceId: string,
    name: string,
    body: string,
  ): Promise<{ ok: boolean; template?: PromptTemplate; error?: string }> {
    return this.request("cyborg:create_prompt_template", { workspaceId, name, body });
  }

  async updatePromptTemplate(
    workspaceId: string,
    id: string,
    fields: { name?: string; body?: string },
  ): Promise<{ ok: boolean; template?: PromptTemplate; error?: string }> {
    return this.request("cyborg:update_prompt_template", { workspaceId, id, ...fields });
  }

  async deletePromptTemplate(
    workspaceId: string,
    id: string,
  ): Promise<{ ok: boolean; id: string; error?: string }> {
    return this.request("cyborg:delete_prompt_template", { workspaceId, id });
  }

  async fetchThread(
    workspaceId: string,
    parentId: string,
  ): Promise<{
    parentId: string;
    messages: Message[];
    lastViewed?: number | null;
  }> {
    // lastViewed = the viewer's frozen per-thread read cursor (epoch ms | null),
    // used to seed the per-thread "New replies" divider (#7).
    return this.request("cyborg:fetch_thread", { workspaceId, parentId });
  }

  // ONE message by id — resolves a shared `?thread=<rootId>` link whose root is
  // older than the loaded page (fetchThread above returns the REPLIES only, so it
  // cannot). `message: null` covers BOTH a nonexistent id and one this viewer may
  // not see: the server deliberately does not distinguish them.
  async fetchMessage(workspaceId: string, messageId: string): Promise<{ message: Message | null }> {
    return this.request("cyborg:fetch_message", { workspaceId, messageId });
  }

  // The stored reactions of the rows a surface has on screen, keyed by id — the agent chat's
  // reload/reconnect backfill (its rows render off the agent timeline, not a message fetch).
  // A row this viewer may not read, or that does not exist, is simply absent.
  async fetchMessageReactions(
    workspaceId: string,
    messageIds: string[],
  ): Promise<Record<string, unknown>> {
    const res = await this.request<{ reactions?: Record<string, unknown> }>(
      "cyborg:fetch_message_reactions",
      { workspaceId, messageIds },
    );
    return res.reactions ?? {};
  }

  async fetchThreads(
    workspaceId: string,
    opts?: { unreadOnly?: boolean; limit?: number },
  ): Promise<{ threads: ThreadSummary[] }> {
    return this.request<{ threads: ThreadSummary[] }>("cyborg:fetch_threads", {
      workspaceId,
      unreadOnly: opts?.unreadOnly,
      limit: opts?.limit,
    });
  }

  // The feed's window (newest `limit` followed threads, up to THREADS_WINDOW_MAX) + the
  // viewer's totals, from ONE server transaction. Rejects with an "unsupported: …"
  // error on a relay older than this message.
  async threadState(
    workspaceId: string,
    opts?: { limit?: number },
  ): Promise<{ threads: ThreadSummary[]; counts: ThreadCounts }> {
    // rootPreview (CYBORG-1198): roots come trimmed; ThreadFeedCard reads the full one.
    return this.request("cyborg:thread_state", {
      workspaceId,
      limit: opts?.limit,
      rootPreview: true,
    });
  }

  async threadCounts(workspaceId: string): Promise<{
    totalUnreadThreads: number;
    totalUnreadMentions: number;
    // Server time of this seed, so it orders against thread-state events' `at`.
    at?: number;
  }> {
    return this.request("cyborg:thread_counts", { workspaceId });
  }

  // `viewedAt` (epoch ms) pins the cursor to the last reply the viewer actually saw;
  // omitted, the relay uses its own clock. The answer carries the counts the relay
  // observed before and after, so a device whose feed never held the thread can still
  // move its aggregate badge.
  markThreadRead(
    workspaceId: string,
    rootId: string,
    viewedAt?: number,
  ): Promise<ThreadStateEvent & { ok: boolean }> {
    return this.request("cyborg:mark_thread_read", { workspaceId, rootId, viewedAt });
  }

  // Every followed thread read up to `before` (the click time), not just the loaded page.
  // `threads` is each touched thread's REMAINING state (a reply after the cutoff stays
  // unread and says so); `counts` the canonical totals afterwards.
  markAllThreadsRead(
    workspaceId: string,
    before: number,
  ): Promise<{
    ok: boolean;
    cleared: number;
    counts: ThreadCounts;
    // Absent on a relay older than the remaining-state contract.
    at?: number;
    threads?: ThreadStateEvent[];
  }> {
    return this.request("cyborg:mark_all_threads_read", { workspaceId, before });
  }

  followThread(workspaceId: string, rootId: string, following: boolean): void {
    this.send({ type: "cyborg:follow_thread", workspaceId, rootId, following });
  }

  sendDm(
    workspaceId: string,
    toId: string,
    text: string,
    attachments?: Attachment[],
    parentId?: string,
    // See sendMessage: echoed by the relay for exact optimistic reconciliation (#501).
    clientMsgId?: string,
    // See sendMessage: a /pages picker selection, reference only (CYBORG-309).
    pageRef?: string,
    composedAt?: number,
  ): void {
    this.send({
      type: "cyborg:dm",
      workspaceId,
      toId,
      text,
      attachments,
      parentId,
      clientMsgId,
      ...(pageRef ? { pageRef } : {}),
      ...(composedAt ? { composedAt } : {}),
    });
  }

  // One forward = the normal send frame plus `forwardRef` (the relay builds the card).
  async forwardMessage(
    workspaceId: string,
    dest: { channelId: string } | { toId: string },
    opts: { messageId: string; text: string; clientMsgId: string },
  ): Promise<void> {
    await this.sendConfirmed(
      "channelId" in dest ? "cyborg:channel_message" : "cyborg:dm",
      {
        workspaceId,
        ...dest,
        text: opts.text,
        forwardRef: { messageId: opts.messageId },
        // ponytail: no confirm step any more, but a relay older than this client still
        // refuses a private source without the flag. Drop once no such relay is live.
        confirmPrivate: true,
        composedAt: Date.now(),
      },
      opts.clientMsgId,
    );
  }

  /** A DM that settles once the relay stored it (rejects on its refusal or a timeout), for a
   *  caller that must not report success for a message that never went out. */
  async sendDmConfirmed(
    workspaceId: string,
    toId: string,
    text: string,
    clientMsgId: string,
  ): Promise<void> {
    await this.sendConfirmed(
      "cyborg:dm",
      { workspaceId, toId, text, composedAt: Date.now() },
      clientMsgId,
    );
  }

  // A send has no ack, so this settles on the echo of our clientMsgId or on an error for the
  // requestId.
  private async sendConfirmed(
    type: "cyborg:dm" | "cyborg:channel_message",
    payload: Record<string, unknown>,
    clientMsgId: string,
  ): Promise<void> {
    let stop = (): void => {};
    const echo = new Promise<void>((resolve) => {
      const seen = (m: Message): void => {
        if (m.clientMsgId === clientMsgId) resolve();
      };
      const offs = [this.on("channel_message", seen), this.on("dm", seen)];
      stop = () => offs.forEach((off) => off());
    });
    const answer = this.request<unknown>(type, { ...payload, clientMsgId });
    try {
      await Promise.race([echo, answer]);
    } finally {
      stop();
      // intentional: once the echo settled the send, the unanswered request's timeout is moot.
      answer.catch(() => undefined);
    }
  }

  // Where a forwarded copy's original lives for THIS viewer (access re-checked per call);
  // undefined = no access, or the original is gone.
  async openForwardOriginal(
    workspaceId: string,
    messageId: string,
  ): Promise<ForwardOriginal | undefined> {
    const resp = await this.request<{ original?: ForwardOriginal | null }>(
      "cyborg:open_forward_original",
      { workspaceId, messageId },
    );
    return resp.original ?? undefined;
  }

  // Continue on a paused agent chain (its hop-limit system line). Rejects with the relay's refusal.
  async continueAgentHop(workspaceId: string, messageId: string): Promise<{ note?: string }> {
    return await this.request<{ note?: string }>("cyborg:continue_agent_hop", {
      workspaceId,
      messageId,
    });
  }

  async fetchDmMessages(
    workspaceId: string,
    peerId: string,
    // CYBORG-767: `aroundTs` (frozen last-read ms) → around-window read (mirrors
    // fetchMessages); `hasMoreNewer` says the tail wasn't loaded. b1: `after`
    // (a loaded message id) pages NEWER than that row (the twin of `before`).
    // knowledge/117: `aroundId` (a target DM) reads the window around that row.
    opts?: {
      before?: string;
      after?: string;
      limit?: number;
      aroundTs?: number;
      aroundId?: string;
    },
  ): Promise<{ messages: Message[]; hasMore: boolean; hasMoreNewer?: boolean }> {
    return this.request<{ messages: Message[]; hasMore: boolean; hasMoreNewer?: boolean }>(
      "cyborg:fetch_dm_messages",
      {
        workspaceId,
        peerId,
        ...opts,
      },
    );
  }

  // Daemon-owner audit read of a cybo↔user DEFAULT session — the "single truth" twin
  // of fetchDmMessages for the audit lens. fetchDmMessages scopes the human side to
  // the caller (guest.userId), so a daemon owner inspecting ANOTHER user's cybo DM
  // saw their OWN pair; this passes the AUDITED session's owner as the human side so
  // the panel renders exactly what that user sees. The relay reuses the same
  // symmetric-pair getDmMessages, gated by `admin` on the daemon that ran the session
  // (same bar as list_daemon_sessions) — a non-admin caller is rejected server-side.
  async auditDmMessages(
    workspaceId: string,
    daemonId: string,
    cyboId: string,
    ownerUserId: string,
    opts?: { before?: string; limit?: number },
  ): Promise<{ messages: Message[]; hasMore: boolean }> {
    return this.request<{ messages: Message[]; hasMore: boolean }>("cyborg:audit_dm_messages", {
      workspaceId,
      daemonId,
      cyboId,
      ownerUserId,
      ...opts,
    });
  }

  // Seq-cursored DM catch-up (#500): the DM-scoped twin of sync(). Returns DM
  // messages with seq > sinceSeq ASCENDING, paginated, so a reconnect can loop
  // until caught up instead of blindly refetching the latest 50 (which silently
  // drops everything older when >50 arrived during a long disconnect). Throws on
  // older relays that don't implement it → caller falls back to fetchDmMessages.
  async fetchDmSince(
    workspaceId: string,
    peerId: string,
    sinceSeq: number,
    limit?: number,
  ): Promise<{ messages: Message[]; hasMore: boolean; nextSeq: number }> {
    return this.request<{ messages: Message[]; hasMore: boolean; nextSeq: number }>(
      "cyborg:fetch_dm_since",
      { workspaceId, peerId, sinceSeq, ...(limit !== undefined ? { limit } : {}) },
    );
  }

  // channelId → channel typing; toId → DM typing (relay scopes to sender+peer);
  // parentId (#11 thread-typing) → routing hint so receivers show the indicator
  // in the open thread panel. They ride along together on one message.
  // workSessionId: typing inside a work session, which has neither channelId nor DM peer.
  sendTyping(
    workspaceId: string,
    channelId?: string,
    toId?: string,
    parentId?: string,
    workSessionId?: string,
  ): void {
    // A throttled keystroke timer calls this: on a closed socket the throw escaped to
    // window.onerror. The indicator is advisory and the next keystroke re-sends it.
    if (!this.canSend()) return;
    this.send({
      type: "cyborg:typing",
      workspaceId,
      channelId: channelId ?? "",
      toId,
      parentId,
      workSessionId,
    });
  }

  // Reactions ride the SAME acked `request()` path as every sibling MESSAGE
  // mutation (edit / delete / pin / save). They used to be the one on the
  // fire-and-forget `send()` path, which made reconciliation structurally
  // impossible: the relay's refusals answer `cyborg:error` with the frame's
  // requestId, and with no requestId on the wire there was nothing to reject —
  // so a rejected reaction (viewer role, private channel, deleted message) or a
  // dropped socket left the optimistic emoji on the reactor's screen forever
  // while no other viewer ever saw it. The reactor is also the ONE actor that
  // cannot self-heal from the broadcast: app.svelte.ts's `reaction` handler
  // drops its own echo on purpose (consuming it would double-apply the
  // optimistic patch), so the ack is its only feedback channel.
  //
  // Resolves with the relay's AUTHORITATIVE post-write state, so the caller
  // reconciles against an ABSOLUTE value instead of a toggle it computed
  // locally — two in-flight clicks then converge instead of racing. `null` when
  // the peer acks without one: the local-daemon path broadcasts the reaction
  // without persisting it (message-router.handleReaction), so it has no
  // authoritative answer to give and the caller keeps its optimistic guess.
  async sendReaction(
    workspaceId: string,
    messageId: string,
    emoji: string,
  ): Promise<"added" | "removed" | null> {
    const res = await this.request<{ action?: "added" | "removed" | "add" | "remove" | null }>(
      "cyborg:reaction",
      { workspaceId, messageId, emoji },
    );
    if (res.action === "add" || res.action === "added") return "added";
    if (res.action === "remove" || res.action === "removed") return "removed";
    // null: the peer acked without an authoritative answer (local daemon).
    return null;
  }

  async editMessage(
    workspaceId: string,
    messageId: string,
    text: string,
    // Ids newly picked from the @-autocomplete while editing.
    mentionPicks?: string[],
  ): Promise<void> {
    await this.request("cyborg:edit_message", {
      workspaceId,
      messageId,
      text,
      ...(mentionPicks?.length ? { mentionPicks } : {}),
    });
  }

  async deleteMessage(workspaceId: string, messageId: string): Promise<void> {
    await this.request("cyborg:delete_message", { workspaceId, messageId });
  }

  async pinMessage(
    workspaceId: string,
    channelId: string | null,
    messageId: string,
    pinned: boolean,
  ): Promise<void> {
    await this.request("cyborg:pin_message", { workspaceId, channelId, messageId, pinned });
  }

  // Read receipts are FIRE-AND-FORGET and they all guard the socket. send() throws
  // "Not connected" synchronously on a closed/connecting one, and these are called
  // from focus handlers and route mounts that legitimately fire mid-reconnect — an
  // uncaught throw there took down the REST of the caller. clearActiveConversation-
  // Badge (app.svelte.ts) is the measured case: its DM branch threw, so the channel
  // and agent branches below it never ran and their badges stayed lit. Dropping the
  // receipt is free: connectToServer/handleReconnect re-assert read state after auth,
  // exactly as setAppState's identical guard already relies on.
  // Last cursor value actually PUT ON THE WIRE, per scope. When a caller omits
  // `lastReadAt` the relay stamps its own Date.now(), so the persisted value was
  // different on every single frame — the upsert could never be a no-op and always
  // took a row lock on the same dm_reads/message_reads row. Read receipts fire from
  // focus handlers, route mounts and live-message timers that legitimately repeat
  // with nothing new read, and that is what produced the lock convoy. Cleared on
  // disconnect (see resetReadReceiptDedup) so a re-login as a different user, or a
  // reconnect that reseeds cursors from server truth, never inherits a stale entry.
  private lastSentReadCursor = new Map<string, number>();
  // The stamps actually sent, so a read_broadcast carrying one is this client's own
  // echo (the daemon broadcasts to the sender too) — see isOwnChannelRead.
  private readonly sentReadStamps = new SentReadStamps();

  /** Drop a receipt whose cursor has not moved since the last one we sent. */
  private readReceiptIsRedundant(key: string, lastReadAt: number | undefined): boolean {
    if (lastReadAt === undefined) return false; // server-stamped: cannot compare
    const sent = this.lastSentReadCursor.get(key);
    if (sent !== undefined && lastReadAt <= sent) return true;
    this.lastSentReadCursor.set(key, lastReadAt);
    this.sentReadStamps.record(key, lastReadAt);
    return false;
  }

  /** Forget every remembered cursor. Called on disconnect/logout. */
  resetReadReceiptDedup(): void {
    this.lastSentReadCursor.clear();
    this.sentReadStamps.clear();
  }

  /** A `read_broadcast` for this channel carrying a stamp this client sent. */
  isOwnChannelRead(workspaceId: string, channelId: string, lastReadAt: number): boolean {
    return this.sentReadStamps.isOwn(`c:${workspaceId}:${channelId}`, lastReadAt);
  }

  /** DM twin of isOwnChannelRead. */
  isOwnDmRead(workspaceId: string, peerId: string, lastReadAt: number): boolean {
    return this.sentReadStamps.isOwn(`d:${workspaceId}:${peerId}`, lastReadAt);
  }

  markRead(workspaceId: string, channelId: string, lastReadAt?: number): void {
    if (!this.canSend()) return;
    if (this.readReceiptIsRedundant(`c:${workspaceId}:${channelId}`, lastReadAt)) return;
    this.send({ type: "cyborg:mark_read", workspaceId, channelId, lastReadAt });
  }

  // P2 Item 12: the DM analogue of markRead. Fire-and-forget (mirrors markRead);
  // the relay's read_broadcast confirms cross-device.
  markDmRead(workspaceId: string, peerId: string, lastReadAt?: number): void {
    if (!this.canSend()) return; // see markRead
    if (this.readReceiptIsRedundant(`d:${workspaceId}:${peerId}`, lastReadAt)) return;
    this.send({ type: "cyborg:mark_dm_read", workspaceId, peerId, lastReadAt });
  }

  // Task analogue: opening a task's detail card clears its Activity rows. Fire-
  // and-forget; the relay marks them read and broadcasts activity_read_changed
  // (carrying taskId) back to all the user's devices for cross-device reconcile.
  markTaskRead(workspaceId: string, taskId: string): void {
    if (!this.canSend()) return; // see markRead
    this.send({ type: "cyborg:mark_task_read", workspaceId, taskId });
  }

  async searchMessages(
    workspaceId: string,
    query: string,
    limit = 50,
    // Omitted = the original whole-workspace search; spread onto cyborg:search when scoped.
    scope?: MessageSearchScope,
  ): Promise<Message[]> {
    const resp = await this.request<{
      messages: Message[];
      gone?: Record<string, "deactivated" | "deleted">;
    }>("cyborg:search", { workspaceId, query, limit, ...scope });
    // The relay flags an author / DM peer who left the workspace; an older relay sends none.
    const gone = resp.gone ?? {};
    // The rows are this response's own objects: flag them in place.
    for (const m of resp.messages) {
      const fromGone = gone[m.fromId];
      const toGone = m.toId ? gone[m.toId] : undefined;
      if (fromGone) m.fromGone = fromGone;
      if (toGone) m.toGone = toGone;
    }
    return resp.messages;
  }

  async fetchUnread(workspaceId: string): Promise<{
    // Per-channel unread MESSAGE count (any unread → BOLD the channel name).
    counts: Record<string, number>;
    // BUG #2: per-channel MENTION count (messages whose `mentions` include me,
    // after my read cursor, not my own) → the RED numeric badge. Additive +
    // backward-compatible: older relays omit it, so the client defaults to {}.
    mentionCounts?: Record<string, number>;
    reads: Record<string, number>;
    // OPTIONAL because the daemon-direct dispatcher (message-router.ts
    // handleFetchUnread) answers `{counts, reads}` only. `undefined` = "this shape
    // does not report these, leave them alone"; `{}` = "the server confirmed empty".
    dmCounts?: Record<string, number>;
    dmReads?: Record<string, number>;
    // Same server-seeded DM recency map as the bootstrap answer carries
    // (BootstrapUserState.unread). Optional for the same reason.
    dmLastMessageAt?: Record<string, number>;
    dmHiddenAt?: Record<string, number>;
    // Channels hidden from me in that workspace (archived for me, not resurfaced).
    archivedChannelIds?: string[];
  }> {
    return this.request("cyborg:fetch_unread", { workspaceId });
  }

  async fetchActivity(
    workspaceId: string,
    opts?: { before?: number; unreadOnly?: boolean; limit?: number },
  ): Promise<{
    items: Record<string, unknown>[];
    unread: number;
    totalUnread?: number;
    hiddenUnreadByWorkspace?: HiddenUnreadByWorkspace;
    unreadByWorkspace?: Record<string, number>;
  }> {
    return this.request("cyborg:fetch_activity", { workspaceId, ...opts });
  }

  async markActivityRead(
    workspaceId: string,
    eventId?: string,
    by?: { sourceId: string; eventType: string },
  ): Promise<BadgeAck & { eventIds?: string[] }> {
    // No row named = the explicit mark-all (the daemon refuses a request that names nothing).
    const all = eventId || by ? {} : { all: true };
    return this.request("cyborg:mark_activity_read", { workspaceId, eventId, ...by, ...all });
  }

  // CYBORG-1408: a drive member's Implement ("allow") / Dismiss ("deny") on a plan_pending row.
  async answerPlanAlert(
    workspaceId: string,
    permissionRequestId: string,
    behavior: "allow" | "deny",
  ): Promise<{ ok: boolean }> {
    return this.request("cyborg:plan_alert_answer", { workspaceId, permissionRequestId, behavior });
  }

  // The reverse of markActivityRead: the row goes back to unread server-side (Alerts "Mark as unread").
  async markActivityUnread(
    workspaceId: string,
    eventId?: string,
    by?: { sourceId: string; eventType: string },
  ): Promise<BadgeAck> {
    return this.request("cyborg:mark_activity_read", { workspaceId, eventId, ...by, unread: true });
  }

  async setNotificationPref(
    workspaceId: string,
    scopeId: string,
    preference: "all" | "mentions_only" | "muted",
  ): Promise<void> {
    await this.request("cyborg:set_notification_pref", { workspaceId, scopeId, preference });
  }

  async setChannelArchivedForMe(
    workspaceId: string,
    channelId: string,
    archived: boolean,
  ): Promise<void> {
    await this.request("cyborg:set_channel_archived_for_me", { workspaceId, channelId, archived });
  }

  // Hide (the DM row's ×) or show a DM in my own list; answered by the relay from Postgres.
  async setDmHidden(
    workspaceId: string,
    peerId: string,
    hidden: boolean,
  ): Promise<{ hiddenAt?: number | null }> {
    return this.request("cyborg:set_dm_hidden", { workspaceId, peerId, hidden });
  }

  // `archivedChannelIds` rides on the prefs read: channels I archived for myself and nothing has
  // resurfaced. Optional because an older relay omits it.
  async fetchNotificationPrefs(
    workspaceId: string,
  ): Promise<{ prefs: Record<string, string>; archivedChannelIds?: string[] }> {
    return this.request("cyborg:fetch_notification_prefs", { workspaceId });
  }

  // CYBORG-989 — ONE round trip for a workspace open. Replaces fetch_channels +
  // listMembers + listWorkSessions + fetchUnread + fetchNotificationPrefs +
  // fetchDrafts, which the shell fired per workspace on every reconnect.
  //
  // `version` is the snapshot version last received for this workspace (0 when we
  // hold nothing). If the relay's version still matches, the answer is
  // `unchanged: true` with NO channels/members/sessions and no workspace-wide query
  // on the relay — only the per-user half (unread, prefs, drafts) comes back.
  //
  // `supported: false` means this relay does not serve bootstrap (older relay, or
  // the flag is off, or the caller is an EXTERNAL in this workspace — Cyborg7
  // Connect scopes are answered by the individual RPCs, not by a snapshot). The
  // caller falls back to the per-RPC path. `bootstrapSupported` below lets the
  // shell skip this call entirely rather than probe with a doomed round trip.
  async bootstrapWorkspace(
    workspaceId: string,
    version: number,
  ): Promise<BootstrapWorkspaceResponse> {
    return this.request("cyborg:bootstrap", { workspaceId, version });
  }

  // ─── Composer drafts (server-side draft sync, #610) ──────────────
  // Upsert the caller's draft for a (workspace, scope). Fire-and-forget: drafts
  // are debounced on the client and lossy by nature, so we don't await a response
  // (mirrors the optimistic setNotificationPref persist). `updatedAt` is epoch ms,
  // the reconcile tiebreaker the other device uses on load.
  draftSet(workspaceId: string, scope: string, text: string, updatedAt: number): void {
    if (!this.canSend()) return; // recovered on the next edit / load reconcile
    this.send({ type: "cyborg:draft_set", workspaceId, scope, text, updatedAt });
  }

  // Clear the caller's draft for a (workspace, scope) at the client's clear time.
  draftClear(workspaceId: string, scope: string, updatedAt: number): void {
    if (!this.canSend()) return; // the local tombstone re-sends it on the next load reconcile
    this.send({ type: "cyborg:draft_clear", workspaceId, scope, updatedAt });
  }

  // All of the caller's drafts in a workspace, to seed a fresh device on load.
  async fetchDrafts(workspaceId: string): Promise<{
    drafts: Array<{ scope: string; text: string; updatedAt: number }>;
    draftTombstones?: Array<{ scope: string; updatedAt: number }>;
  }> {
    return this.request("cyborg:fetch_drafts", { workspaceId });
  }

  // ─── Workspaces ─────────────────────────────────────────────────

  async fetchWorkspaces(): Promise<Workspace[]> {
    const resp = await this.request<{ workspaces: Workspace[] }>("cyborg:fetch_workspaces");
    return resp.workspaces;
  }

  /** `idempotencyKey`: a repeat with the same key (a reload that lost the first answer) gets
   *  the workspace that key already made instead of a second one (CYBORG-1082's pattern). */
  async createWorkspace(
    name: string,
    settings?: Record<string, unknown>,
    idempotencyKey?: string,
  ): Promise<Workspace> {
    validateWorkspaceInput({ name, settings }, true);
    const resp = await this.request<{ workspace: Workspace }>("cyborg:create_workspace", {
      name,
      settings,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    });
    this.noteHeldWorkspace(resp.workspace.id);
    return resp.workspace;
  }

  async updateWorkspace(
    workspaceId: string,
    updates: { name?: string; avatarUrl?: string | null; settings?: WorkspaceSettings },
  ): Promise<void> {
    validateWorkspaceInput(updates, false);
    await this.request("cyborg:update_workspace", { workspaceId, ...updates });
  }

  async deleteWorkspace(workspaceId: string): Promise<void> {
    await this.request("cyborg:delete_workspace", { workspaceId });
  }

  // App-Store Guideline 5.1.1(v): permanently delete the authenticated user's
  // own account. No args — the relay always acts on the authenticated socket's
  // userId, so this can only ever delete the caller's own account.
  // `handoffs` (CYBORG-1231): per-workspace decisions for the agents this account
  // owns/hosts, so they are moved or deleted instead of orphaned.
  async deleteAccount(handoffs?: WorkspaceHandoffs[]): Promise<string[]> {
    const resp = await this.request<{ failedCyboIds?: string[] }>(
      "cyborg:delete_account",
      handoffs ? { handoffs } : {},
    );
    return resp.failedCyboIds ?? [];
  }

  async handoffCandidates(workspaceId: string, userId: string): Promise<HandoffCandidates> {
    return this.request<HandoffCandidates>("cyborg:handoff_candidates", { workspaceId, userId });
  }

  async accountHandoffCandidates(): Promise<AccountHandoffCandidates> {
    return this.request<AccountHandoffCandidates>("cyborg:account_handoff_candidates", {});
  }

  // ─── Channels ───────────────────────────────────────────────────

  async fetchChannels(workspaceId: string): Promise<Channel[]> {
    const resp = await this.request<{ channels: Channel[] }>("cyborg:fetch_channels", {
      workspaceId,
    });
    return resp.channels;
  }

  // `projectId` is persisted by the server in the SAME transaction as the channel.
  // The separate setChannelProject follow-up is kept by callers as an idempotent
  // fallback for an older relay that ignores this field — it is no longer the only
  // thing standing between a new channel and its project link.
  async createChannel(
    workspaceId: string,
    name: string,
    opts?: {
      description?: string;
      isPrivate?: boolean;
      instructions?: string;
      projectId?: string | null;
      // CYBORG-1082: one key per create-form session; a retry returns the channel it made.
      idempotencyKey?: string;
    },
  ): Promise<Channel> {
    const resp = await this.request<{ channel: Channel }>("cyborg:create_channel", {
      workspaceId,
      name,
      ...opts,
    });
    return resp.channel;
  }

  // #608: start a group DM with `participants` (the OTHER members' user-ids; the
  // creator is implicit). 2–8 others. The server validates membership + the cap,
  // derives the auto-name (members' display names, sorted), and returns the new
  // hidden group_dm channel — which then rides the normal channel pipeline.
  async createGroupDm(workspaceId: string, participants: string[]): Promise<Channel> {
    const resp = await this.request<{ channel: Channel }>("cyborg:create_group_dm", {
      workspaceId,
      participants,
    });
    return resp.channel;
  }

  // All workspace channels (public + your private) with a per-channel isMember
  // flag, for the Browse-channels modal. Backed by cyborg:list_channels.
  async listChannels(
    workspaceId: string,
  ): Promise<Array<Channel & { isMember: boolean; memberCount: number }>> {
    const resp = await this.request<{
      channels: Array<Channel & { isMember: boolean; memberCount: number }>;
    }>("cyborg:list_channels", { workspaceId });
    return resp.channels;
  }

  // ─── Messages ───────────────────────────────────────────────────

  async fetchMessages(
    workspaceId: string,
    channelId: string,
    // CYBORG-767: `aroundTs` (frozen last-read ms) asks for the around-window read
    // (read context + unread head) so the open lands at the "New messages" divider;
    // `hasMoreNewer` says the tail wasn't loaded (a large unread block). b1: `after`
    // (a loaded message id) pages NEWER than that row (the twin of `before`).
    // knowledge/117: `aroundId` (an alert's target) reads the window around that
    // row — Mattermost's loadPostsAround; the relay serves the tail if it is gone.
    opts?: {
      before?: string;
      after?: string;
      limit?: number;
      aroundTs?: number;
      aroundId?: string;
    },
  ): Promise<{ messages: Message[]; hasMore: boolean; hasMoreNewer?: boolean }> {
    return this.request<{ messages: Message[]; hasMore: boolean; hasMoreNewer?: boolean }>(
      "cyborg:fetch_messages",
      {
        workspaceId,
        channelId,
        ...opts,
      },
    );
  }

  // CYBORG-762 — the workspace-open bootstrap only feeds the Activity feed + Logs
  // pane, which render a compact preview (author/channel/text/timestamp/mentions),
  // never the heavy jsonb. `light` asks the relay for the trimmed projection so this
  // (the largest single bootstrap payload) drops attachments/reactions/unfurls/card.
  // Defaults off → any caller that needs the full message rows keeps getting them.
  async fetchWorkspaceActivity(
    workspaceId: string,
    limit?: number,
    light?: boolean,
  ): Promise<{ messages: Message[] }> {
    return this.request<{ messages: Message[] }>("cyborg:fetch_workspace_activity", {
      workspaceId,
      limit,
      light,
    });
  }

  // CYBORG-1086: saved Logs history (app_events), scoped by the caller's role server-side.
  async fetchWorkspaceLogs(
    workspaceId: string,
    opts: {
      since?: number;
      before?: number;
      beforeId?: string; // Export's cursor: the previous page's last row, beside `before`
      severity?: "info" | "warn" | "error";
      cyboId?: string;
      daemonId?: string;
      limit?: number;
      // Export's current filter; `subject` is the owner/admin audit (refused for members).
      tab?: LogsTab;
      search?: LogsSearch | null;
      subject?: LogsSubjectRef;
    } = {},
  ): Promise<WorkspaceLogsPage> {
    return this.request<WorkspaceLogsPage>("cyborg:fetch_workspace_logs", { workspaceId, ...opts });
  }

  // CYBORG-1155: the Logs timeline — day summaries, one day's items, a run's steps, and
  // the raw rows behind a folded item. Role-scoped by the relay.
  async fetchLogsTimeline(req: LogsTimelineRequest): Promise<LogsTimelinePage> {
    return this.request<LogsTimelinePage>("cyborg:fetch_logs_timeline", { ...req });
  }

  async fetchLogsRunDetail(req: LogsRunDetailRequest): Promise<LogsRunDetail> {
    return this.request<LogsRunDetail>("cyborg:fetch_logs_run_detail", { ...req });
  }

  async fetchLogsSessionDetail(req: LogsSessionDetailRequest): Promise<LogsSessionDetail> {
    return this.request<LogsSessionDetail>("cyborg:fetch_logs_session_detail", { ...req });
  }

  async fetchLogsGroupEvents(req: LogsGroupEventsRequest): Promise<LogsGroupEventsResponse> {
    return this.request<LogsGroupEventsResponse>("cyborg:fetch_logs_group_events", { ...req });
  }

  async fetchBackendStatus(): Promise<{
    mode: "local" | "remote";
    // Retired (knowledge/112 §1.5); an older backend still sends it, nobody reads it.
    authorityMode?: "local" | "shared";
    // CYBORG-970 (SECURITY): no service ADDRESS is reported any more. This RPC
    // carries no workspaceId, so the relay's membership floor never gates it and
    // every authenticated socket gets the answer — it can only carry what is safe
    // for all of them. `status` is "connected" | "disconnected" | "not_configured";
    // the last one used to be inferred client-side from a null url.
    postgres: { status: string };
    redis: { status: string };
    relay: { status: string };
    s3?: { status: string };
  }> {
    return this.request("cyborg:fetch_backend_status", {});
  }

  async fetchDaemonInfo(): Promise<{
    daemonId: string | null;
    ownerId: string | null;
    // #browser-agent — the Chrome-extension pairing snapshot, present ONLY when
    // this client is talking straight to a local daemon over its own /ws. The
    // relay answers this RPC itself with nulls and never forwards it, so a
    // browser client on the far side never sees a loopback token. Optional: an
    // older daemon omits it entirely and the UI degrades to "not available here".
    browserExtension?: {
      connected: boolean;
      pairingUrl: string;
      extensionId: string;
    };
  }> {
    return this.request("cyborg:fetch_daemon_info", {});
  }

  // #browser-agent — ask the daemon to open the Chrome extension's options page
  // with the pairing URL prefilled. Deliberately payload-free: the daemon owns
  // the token and the pinned extension ID, so a client can never steer what gets
  // opened. `ok: false` means no Chromium binary was found — the caller shows the
  // copyable pairing URL instead.
  async openBrowserPairing(): Promise<{ ok: boolean }> {
    return this.request("cyborg:open_browser_pairing", {});
  }

  async listDaemons(workspaceId: string): Promise<{
    daemons: Array<{
      id: string;
      label: string;
      ownerId: string;
      status: string;
      lastSeenAt: number | null;
      meta?: {
        cpu?: number;
        memMb?: number;
        agents?: number;
        queueDepth?: number;
        host?: string;
        platform?: string;
        arch?: string;
        cyboRuntime?: {
          configured: boolean;
          modelCount: number;
          backends: { backend: string; modelCount: number }[];
        };
      } | null;
      // Worker designation. The LANDED wire (#3422) is FLAT: worker boolean +
      // memberDefaultRole/workerManagerId below; the nested object form is
      // tolerated for forward-compat. All optional — an older relay omits them,
      // and normalizeDaemonRow (plugins/agents/types.ts) folds either shape
      // into the client's canonical Daemon.worker.
      worker?: boolean | { defaultScopes: Array<"chat" | "spawn">; managerId: string } | null;
      memberDefaultRole?: string | null;
      workerManagerId?: string | null;
      ownerIsMember?: boolean;
    }>;
    // This workspace's PENDING worker setup codes (optional — omitted on older
    // relays, liveness polls, and when the read degrades server-side).
    pendingWorkerInvites?: WorkerInviteWire[];
    // Whether the $99 workspace-machine price is live in Stripe. false = the
    // server connects without checkout. Absent (older relay) = unknown.
    workerBillingLive?: boolean;
    // The caller's configured default daemon for slash commands (null = unset).
    defaultSlashDaemonId?: string | null;
    // DEPRECATED per-user preferred model (back-compat; may hold a stale value).
    // Prefer `workspaceSlashConfig.model` — the admin-controlled source of truth.
    slashCommandModel?: { provider: string; model: string } | null;
    // Workspace-level slash AI config (admin-controlled, #opt-A). `model` is the
    // authoritative run-target model, parsed server-side from "provider/model".
    workspaceSlashConfig?: {
      defaultSlashDaemonId: string | null;
      fallbackDaemons: string[];
      model: { provider: string; model: string } | null;
    } | null;
  }> {
    return this.request("cyborg:list_daemons", { workspaceId });
  }

  // CYBORG-1198: the liveness poll's tick. A relay that knows the flag answers only
  // id/status/lastSeenAt with `livenessOnly: true`; an older relay ignores the flag
  // and answers the full rows, which the caller then uses as-is.
  async listDaemonLiveness(
    workspaceId: string,
  ): Promise<
    | { livenessOnly: true; daemons: DaemonLivenessRow[] }
    | ({ livenessOnly?: undefined } & Awaited<ReturnType<SlackClient["listDaemons"]>>)
  > {
    return this.request("cyborg:list_daemons", { workspaceId, livenessOnly: true });
  }

  // Set the daemon's user-facing authority model (CYBORG-270/279). The relay
  // forwards this to the OWNING daemon, which persists it (daemon_settings) and
  // applies it at runtime — no restart. Only the daemon owner may change it.
  // "shared" = Team (daemon + relay + shared PG); "local" = Solo (daemon, no PG).
  // The forwarded response nests its fields under `payload`, so `request` resolves
  // with { ok, mode?, error? } directly.
  async setAuthorityMode(
    mode: "local" | "shared",
    workspaceId?: string,
  ): Promise<{ ok: boolean; mode?: "local" | "shared"; error?: string }> {
    return this.request("cyborg:set_authority_mode", {
      mode,
      ...(workspaceId ? { workspaceId } : {}),
    });
  }

  // Persist the user's default slash-command daemon (a daemon they own; null clears it).
  async setDefaultSlashDaemon(
    workspaceId: string,
    daemonId: string | null,
  ): Promise<{ daemonId: string | null }> {
    return this.request("cyborg:set_default_slash_daemon", { workspaceId, daemonId });
  }

  // Persist the user's preferred model for channel AI commands (/summarize etc.).
  // Pass null to clear it (→ auto-resolve, the zero-config default).
  async setSlashCommandModel(
    model: { provider: string; model: string } | null,
  ): Promise<{ model: { provider: string; model: string } | null }> {
    return this.request("cyborg:set_slash_command_model", { model });
  }

  // Per-channel override for channel AI commands (wins over the user default).
  // Pass null to clear it (→ inherit the user default / auto-resolve).
  async setChannelSlashCommandModel(
    workspaceId: string,
    channelId: string,
    model: { provider: string; model: string } | null,
  ): Promise<{ channelId: string; model: { provider: string; model: string } | null }> {
    return this.request("cyborg:set_channel_slash_command_model", {
      workspaceId,
      channelId,
      model,
    });
  }

  // ─── Workspace slash-command config (admin-only) — see WorkspaceSlashConfig ──
  // The server persists a FLAT shape (defaultSlashDaemonId / fallbackDaemons /
  // model as a "provider/model" string) across 3 workspace columns; map to/from
  // the richer client shape here so the UI is decoupled from the wire format.
  // (slashCommandsEnabled has no server column yet → defaults true on read; the
  // round-trip persists the daemon + fallbacks + model.)
  async getWorkspaceSlashConfig(workspaceId: string): Promise<WorkspaceSlashConfig> {
    const resp = await this.request<{ config: ServerWorkspaceSlashConfig }>(
      "cyborg:get_workspace_slash_config",
      { workspaceId },
    );
    return fromServerSlashConfig(resp.config);
  }

  async setWorkspaceSlashConfig(
    workspaceId: string,
    config: WorkspaceSlashConfig,
  ): Promise<WorkspaceSlashConfig> {
    const resp = await this.request<{
      ok?: boolean;
      error?: string;
      config?: ServerWorkspaceSlashConfig;
    }>("cyborg:set_workspace_slash_config", {
      workspaceId,
      defaultSlashDaemonId: config.defaultDaemonId,
      fallbackDaemons: config.fallbackDaemonIds,
      model: config.model ? `${config.model.provider}/${config.model.model}` : null,
    });
    if (resp.ok === false) throw new Error(resp.error ?? "Failed to save the AI config");
    // Preserve the (client-only) enabled flag; daemon/fallbacks/model come back
    // from the server's authoritative round-trip.
    return resp.config
      ? { ...fromServerSlashConfig(resp.config), slashCommandsEnabled: config.slashCommandsEnabled }
      : config;
  }

  // ─── Workspace agent autonomy (read-only) ─────────────────────────
  // Autonomy is fixed on (server autonomy-policy.ts); this reads the effective state.
  async getWorkspaceAutonomy(workspaceId: string): Promise<{ enabled: boolean }> {
    return this.request<{ enabled: boolean }>("cyborg:get_workspace_autonomy", { workspaceId });
  }

  // ─── Channel task-activity feed (Channel → Tasks tab) ────────────
  // Per-channel config for the Jira-in-Slack task feed. Readable by any
  // workspace member (`canEdit` says whether THIS user may write it — the same
  // creator / workspace owner-admin / channel-admin gate the other channel
  // config writes use); persisted server-side in `channel_task_activity`.
  async getChannelTaskActivity(
    workspaceId: string,
    channelId: string,
  ): Promise<{ config: ChannelTaskActivityConfig; canEdit: boolean }> {
    return this.request<{ config: ChannelTaskActivityConfig; canEdit: boolean }>(
      "cyborg:get_channel_task_activity",
      { workspaceId, channelId },
    );
  }

  // Spec §1.4's monthly ceiling on one partner organization, in cents. Owner/admin
  // only on the relay; `orgId` is that organization's own workspace id, the same id
  // the connection carries. Rejects rather than clamps an out-of-range value, so a
  // caller must surface the error instead of assuming its number was stored.
  // Spec §1.6's whole visual model in one payload: which org each visible person
  // belongs to, which orgs are in each channel, and whether the VIEWER is the outsider
  // here. One call on workspace entry, because the mark has to be on the first paint of
  // any channel.
  //
  // `hostWorkspaceId`, not `workspaceId`: this is the one Connect endpoint an external
  // must reach, and the relay's universal membership floor is keyed on `workspaceId` —
  // an external is deliberately not in that set. The endpoint gates itself instead
  // (member OR active external) and narrows an outsider's payload to the people they
  // share a channel with.
  async connectDirectory(hostWorkspaceId: string): Promise<ConnectSnapshot> {
    return this.request<ConnectSnapshot>("cyborg:connect_directory", { hostWorkspaceId });
  }

  // The INVITEE's two calls (spec §8.2), and the only Connect endpoints reachable by
  // somebody who is not in the host workspace yet. Both name `hostWorkspaceId` for the
  // reason connectDirectory does: the relay's universal membership floor is keyed on
  // `workspaceId`, and an invitee is scoped to nothing here. Neither skips a gate —
  // both are keyed on the signed-in account, so they only ever see or accept the
  // invitation addressed to that account.
  async externalInvitePreview(hostWorkspaceId: string): Promise<ExternalInvitePreview> {
    return this.request<ExternalInvitePreview>("cyborg:external_invite_preview", {
      hostWorkspaceId,
    });
  }

  // The SECOND door, for the invitee who lost the email: every pending invitation
  // addressed to THIS account, whichever workspace sent it. Takes no arguments at
  // all — the relay reads the caller's id and nothing else, which is what makes it
  // incapable of answering about a workspace they were not invited to.
  async pendingExternalInvites(): Promise<{ invitations: PendingConnectInvitation[] }> {
    return this.request<{ invitations: PendingConnectInvitation[] }>(
      "cyborg:pending_external_invites",
    );
  }

  // ONE transaction server-side: `invited` → `active` plus the channel_members row the
  // invitation named, so there is no observable moment where somebody is connected with
  // reach to nothing. The returned row carries `inviteChannelId` — the channel to land
  // them in — which is why the caller does not have to ask a second time.
  // `externalWorkspace` is the SAME entry cyborg:auth's externalWorkspaces carries,
  // server-built: the caller appends it to the workspace list before navigating, so
  // the host workspace is drawable on this hop rather than only after a re-auth.
  // Optional — an older relay omits it, and the accept still succeeds.
  async acceptExternalInvite(hostWorkspaceId: string): Promise<{
    external: { workspaceId: string; inviteChannelId: string | null; status: string };
    workspaceId: string;
    externalWorkspace?: ExternalWorkspacePayload | null;
  }> {
    return this.request("cyborg:accept_external_invite", { hostWorkspaceId });
  }

  // Persist the whole config in one write (the form is a single object, so a
  // per-field PATCH would only invent partial-update races). Answers with the
  // PERSISTED config read back — `projectId` canonicalized server-side — so the
  // form reflects what actually landed rather than what it hoped for.
  async setChannelTaskActivity(
    workspaceId: string,
    config: ChannelTaskActivityConfig,
  ): Promise<{ config: ChannelTaskActivityConfig }> {
    return this.request<{ config: ChannelTaskActivityConfig }>("cyborg:set_channel_task_activity", {
      workspaceId,
      channelId: config.channelId,
      enabled: config.enabled,
      projectId: config.projectId,
      events: config.events,
      filterAssignees: config.filterAssignees,
      filterMinPriority: config.filterMinPriority,
      skipOwnChanges: config.skipOwnChanges,
      grouping: config.grouping,
      expiresAt: config.expiresAt,
    });
  }

  // ─── Slack channel placement (owner/admin) ─────────────
  // Per-workspace switch (0166, DEFAULT ON since 0167). When on, people are
  // placed into the Cyborg channels linked to the Slack channels they are
  // already in — on join, and at channel-import time for existing members. The read is open to any member, so a
  // non-admin's settings page can show the current state without editing it.
  // NOT nested under the autonomy master switch: that switch consolidates the
  // "let agents act on their own" decision, and this is a membership write on
  // the join seams, not an agent-agency dial.
  async getWorkspaceSlackChannelPlacement(workspaceId: string): Promise<{ enabled: boolean }> {
    return this.request<{ enabled: boolean }>("cyborg:get_workspace_slack_channel_placement", {
      workspaceId,
    });
  }

  async setWorkspaceSlackChannelPlacement(
    workspaceId: string,
    enabled: boolean,
  ): Promise<{ ok: boolean; enabled?: boolean; error?: string }> {
    return this.request<{ ok: boolean; enabled?: boolean; error?: string }>(
      "cyborg:set_workspace_slack_channel_placement",
      {
        workspaceId,
        enabled,
      },
    );
  }

  // ─── Slack sync dialog (relay: slack-sync.ts) ──────────
  // channels + addChannels are owner/admin (a member gets a cyborg:error); status and
  // memberChannels are any member. Slack-side failures are typed on the response's
  // `error` (rate_limited carries retryAfterSec). The member view's show/hide toggle is
  // addChannelMember / removeChannelMember on yourself — there is no separate setter.
  // `refresh`: the page's Refresh — also re-reads each Slack team's icon and domain.
  async slackSyncChannels(
    workspaceId: string,
    teamId?: string,
    opts: { refresh?: boolean } = {},
  ): Promise<SlackSyncChannelsResponse> {
    return this.request<SlackSyncChannelsResponse>("cyborg:slack_sync_channels", {
      workspaceId,
      ...(teamId ? { teamId } : {}),
      ...(opts.refresh ? { refresh: true } : {}),
    });
  }

  // `importHistory: false` links without queueing the 30-day history import.
  async slackSyncAddChannels(
    workspaceId: string,
    teamId: string,
    slackChannelIds: string[],
    opts: { importHistory?: boolean } = {},
  ): Promise<SlackSyncAddResponse> {
    return this.request<SlackSyncAddResponse>("cyborg:slack_sync_add_channels", {
      workspaceId,
      teamId,
      slackChannelIds,
      ...(opts.importHistory === false ? { importHistory: false } : {}),
    });
  }

  // Already-linked channels only (≤100); idempotent — already imported / importing are answered.
  async slackSyncImportHistory(
    workspaceId: string,
    slackChannelIds: string[],
  ): Promise<SlackSyncImportHistoryResponse> {
    return this.request<SlackSyncImportHistoryResponse>("cyborg:slack_sync_import_history", {
      workspaceId,
      slackChannelIds,
    });
  }

  // Stop syncing (≤100): the channels and their messages stay.
  async slackSyncRemoveChannels(
    workspaceId: string,
    slackChannelIds: string[],
  ): Promise<SlackSyncRemoveChannelsResponse> {
    return this.request<SlackSyncRemoveChannelsResponse>("cyborg:slack_sync_remove_channels", {
      workspaceId,
      slackChannelIds,
    });
  }

  async slackSyncStatus(workspaceId: string): Promise<SlackSyncStatusResponse> {
    return this.request<SlackSyncStatusResponse>("cyborg:slack_sync_status", { workspaceId });
  }

  async slackSyncMemberChannels(
    workspaceId: string,
  ): Promise<{ channels: SlackSyncMemberChannel[] }> {
    return this.request<{ channels: SlackSyncMemberChannel[] }>(
      "cyborg:slack_sync_member_channels",
      { workspaceId },
    );
  }

  // ─── Agent DMs to Slack non-members (owner/admin) ──────────
  // workspaces.slack_agent_dm_nonmembers_enabled (0199) — may an agent write FIRST, in
  // Slack, to somebody who is in the connected Slack workspace and holds no Cyborg
  // account? DEFAULT ON. Same get/set shape as the placement pair above: the read is open
  // to any member so a non-admin's settings page can show the state without editing it.
  //
  // The whole reason this pair exists is that the flag it is nearest to —
  // slack_guest_asks_enabled (0150) — shipped with no client methods at all, so its
  // off-switch was only reachable by hand-written SQL.
  async getWorkspaceSlackAgentDmNonmembers(workspaceId: string): Promise<{ enabled: boolean }> {
    return this.request<{ enabled: boolean }>("cyborg:get_workspace_slack_agent_dm_nonmembers", {
      workspaceId,
    });
  }

  async setWorkspaceSlackAgentDmNonmembers(
    workspaceId: string,
    enabled: boolean,
  ): Promise<{ ok: boolean; enabled?: boolean; error?: string }> {
    return this.request<{ ok: boolean; enabled?: boolean; error?: string }>(
      "cyborg:set_workspace_slack_agent_dm_nonmembers",
      {
        workspaceId,
        enabled,
      },
    );
  }

  // ─── Per-link external speakers (owner/admin) ──────────
  // Sets slack_channel_links.external_speakers_enabled (0172) for ONE link. When on,
  // a person from the other organization in a Slack Connect channel reaches an agent
  // as a forced guest — no standing, no shell, bridged transcript only.
  //
  // SET only, no `get_` twin: the value already arrives with the link list
  // (fetchSlackChannelLinks), so a second read would be a second source of truth. The
  // relay re-checks the role AND that the link is this workspace's, so a caller that
  // skipped the disabled switch gains nothing.
  async setSlackLinkExternalSpeakers(
    workspaceId: string,
    linkId: string,
    enabled: boolean,
  ): Promise<{ ok: boolean; enabled?: boolean; error?: string }> {
    return this.request<{ ok: boolean; enabled?: boolean; error?: string }>(
      "cyborg:set_slack_link_external_speakers",
      {
        workspaceId,
        linkId,
        enabled,
      },
    );
  }

  // ─── Slack identity mapping (owner/admin only) ──────────
  // Agents reach people in Slack by email address, so somebody who signed up here with one
  // address and uses another at work is unreachable. These three let an admin confirm the
  // pairings once for the whole workforce.
  //
  // The READ is owner/admin gated too, not only the write: it returns the Slack org's
  // member list WITH addresses. The relay is the real gate — hiding the section from a
  // member is the convenience, not the authorization.
  async fetchSlackIdentityCandidates(
    workspaceId: string,
  ): Promise<{ ok: boolean; orgs?: SlackIdentityOrg[]; error?: string }> {
    return this.request<{ ok: boolean; orgs?: SlackIdentityOrg[]; error?: string }>(
      "cyborg:fetch_slack_identity_candidates",
      { workspaceId },
    );
  }

  // `evidence` is deliberately NOT sent: the relay re-derives it from the same inputs the
  // screen was shown and refuses any pairing it cannot reproduce. A client that could name
  // its own evidence could label a name guess "primary_email".
  async confirmSlackIdentityLinks(
    workspaceId: string,
    slackTeamId: string,
    links: Array<{ slackUserId: string; userId: string }>,
  ): Promise<{ ok: boolean; confirmed?: number; error?: string }> {
    return this.request<{ ok: boolean; confirmed?: number; error?: string }>(
      "cyborg:confirm_slack_identity_links",
      { workspaceId, slackTeamId, links },
    );
  }

  async removeSlackIdentityLink(
    workspaceId: string,
    linkId: string,
  ): Promise<{ ok: boolean; error?: string }> {
    return this.request<{ ok: boolean; error?: string }>("cyborg:remove_slack_identity_link", {
      workspaceId,
      linkId,
    });
  }

  async fetchDaemonAccess(workspaceId: string): Promise<{
    access: Array<{
      daemonId: string;
      userId: string;
      grantedBy: string;
      grantedAt: number;
      scopes?: string[];
    }>;
  }> {
    return this.request("cyborg:fetch_daemon_access", { workspaceId });
  }

  // Idempotently SET a user's daemon scopes (#705). Empty `scopes` revokes. This
  // is the authoritative access RPC the per-daemon matrix uses. grant/revoke below
  // remain for the binary call-sites (Settings toggle, sidebar "remove access").
  async setDaemonAccess(
    workspaceId: string,
    daemonId: string,
    userId: string,
    scopes: string[],
  ): Promise<{ ok: boolean; daemonId: string; userId: string; scopes: string[] }> {
    return this.request("cyborg:set_daemon_access", { workspaceId, daemonId, userId, scopes });
  }

  async grantDaemonAccess(
    workspaceId: string,
    daemonId: string,
    userId: string,
  ): Promise<{ granted: boolean }> {
    return this.request("cyborg:grant_daemon_access", { workspaceId, daemonId, userId });
  }

  async revokeDaemonAccess(
    workspaceId: string,
    daemonId: string,
    userId: string,
  ): Promise<{ revoked: boolean }> {
    return this.request("cyborg:revoke_daemon_access", { workspaceId, daemonId, userId });
  }

  // ─── Daemon access REQUESTS (#705 REQUEST → NOTIFY → APPROVE) ───
  //
  // A non-owner asks a daemon OWNER for access at the requested scopes; the owner
  // approves (running the grant) or denies. The owner is notified live via the
  // daemon_access_request_changed event + an activity_new badge.

  // Requester → owner: ask for access at `scopes`. Returns the created (or existing
  // pending) request row.
  async requestDaemonAccess(
    workspaceId: string,
    daemonId: string,
    scopes: Array<"chat" | "spawn" | "terminal" | "admin">,
    source?: "permission_card",
  ): Promise<{ request: DaemonAccessRequestView }> {
    return this.request("cyborg:request_daemon_access", {
      workspaceId,
      daemonId,
      scopes,
      ...(source ? { source } : {}),
    });
  }

  // Owner → resolve a pending request. `decision` is "approve" | "deny"; on
  // approve, optional `scopes` overrides the requested set before the grant runs.
  // The request id travels as `requestIdToResolve` to avoid colliding with the RPC
  // correlation id (`requestId`).
  async resolveDaemonAccessRequest(
    workspaceId: string,
    requestId: string,
    decision: "approve" | "deny",
    scopes?: Array<"chat" | "spawn" | "terminal" | "admin">,
  ): Promise<{ request: DaemonAccessRequestView }> {
    return this.request("cyborg:resolve_daemon_access_request", {
      workspaceId,
      requestIdToResolve: requestId,
      decision,
      ...(scopes !== undefined ? { scopes } : {}),
    });
  }

  // Owner inbox + requester outbox: pending requests for daemons the caller owns,
  // plus the caller's own outgoing requests.
  async fetchDaemonAccessRequests(
    workspaceId: string,
  ): Promise<{ requests: DaemonAccessRequestView[] }> {
    return this.request("cyborg:fetch_daemon_access_requests", { workspaceId });
  }

  // ─── Agent transfers (the owner gives an agent; the recipient accepts or declines) ───

  async cyboTransferPreview(
    workspaceId: string,
    cyboId: string,
    toUserId?: string,
  ): Promise<CyboTransferPreviewView> {
    return this.request("cyborg:cybo_transfer_preview", {
      workspaceId,
      cyboId,
      ...(toUserId ? { toUserId } : {}),
    });
  }

  async requestCyboTransfer(
    workspaceId: string,
    cyboId: string,
    toUserId: string,
  ): Promise<{ request: CyboTransferRequestView }> {
    return this.request("cyborg:request_cybo_transfer", { workspaceId, cyboId, toUserId });
  }

  async fetchCyboTransferRequests(
    workspaceId: string,
  ): Promise<{ requests: CyboTransferRequestView[] }> {
    return this.request("cyborg:fetch_cybo_transfer_requests", { workspaceId });
  }

  async resolveCyboTransferRequest(
    workspaceId: string,
    requestId: string,
    decision: "accept" | "decline",
    daemonId?: string,
  ): Promise<{ request: CyboTransferRequestView }> {
    return this.request("cyborg:resolve_cybo_transfer_request", {
      workspaceId,
      requestIdToResolve: requestId,
      decision,
      ...(daemonId ? { daemonId } : {}),
    });
  }

  async cancelCyboTransferRequest(
    workspaceId: string,
    requestId: string,
  ): Promise<{ request: CyboTransferRequestView }> {
    return this.request("cyborg:cancel_cybo_transfer_request", {
      workspaceId,
      requestIdToResolve: requestId,
    });
  }

  // ─── Worker tier (workspace-owned machines, $99/machine/mo) ───

  // Designate (true) or clear (false) a daemon as a workspace Worker — the
  // LANDED wire shape (#3422): a boolean; the manager is the acting caller,
  // stamped server-side. Workspace admin only — the relay re-checks.
  async setWorkerDesignation(
    workspaceId: string,
    daemonId: string,
    worker: boolean,
  ): Promise<{ ok: boolean; error?: string }> {
    return this.request("cyborg:set_workspace_daemon_worker", { workspaceId, daemonId, worker });
  }

  // A workspace admin takes a machine out of this workspace, or puts it back; the relay re-checks.
  async removeWorkspaceMachine(
    workspaceId: string,
    daemonId: string,
  ): Promise<{ ok: boolean; parkedSchedules?: number }> {
    return this.request("cyborg:remove_workspace_machine", { workspaceId, daemonId });
  }

  async restoreWorkspaceMachine(workspaceId: string, daemonId: string): Promise<{ ok: boolean }> {
    return this.request("cyborg:restore_workspace_machine", { workspaceId, daemonId });
  }

  async listRemovedWorkspaceMachines(workspaceId: string): Promise<RemovedWorkspaceMachine[]> {
    const res = await this.request<{ machines: RemovedWorkspaceMachine[] }>(
      "cyborg:list_removed_workspace_machines",
      { workspaceId },
    );
    return res.machines;
  }

  // "Stop all browsing" on one machine: closes every agent browser session there and
  // answers a count only. Machine owner, or workspace owner/admin on a workspace
  // machine — the relay and the daemon both re-check.
  async stopAllBrowsing(workspaceId: string, daemonId: string): Promise<{ closed: number }> {
    return this.request("cyborg:browser_stop_all", { workspaceId, daemonId });
  }

  // Change a Worker's member-default role — the LANDED wire shape (#3422):
  // viewer | operator | null (Off). Never an RCE role by construction.
  async setWorkerDefaultAccess(
    workspaceId: string,
    daemonId: string,
    role: "viewer" | "operator" | null,
  ): Promise<{ ok: boolean; error?: string }> {
    return this.request("cyborg:set_worker_default_access", { workspaceId, daemonId, role });
  }

  // Mint a setup code for a NEW worker machine. The label names the pending
  // row; memberDefaultRole is stored on the invite and applied at redeem. The
  // response carries the plaintext `code` + the copy-pasteable `setupCommand`
  // beside the row — here and ONLY here, never re-fetchable.
  async mintWorkerInvite(
    workspaceId: string,
    label?: string,
    memberDefaultRole?: "viewer" | "operator" | null,
  ): Promise<{ invite: WorkerInviteView }> {
    const res = await this.request<{
      code: string;
      setupCommand: string;
      invite: WorkerInviteWire;
    }>("cyborg:mint_worker_invite", {
      workspaceId,
      ...(label !== undefined ? { label } : {}),
      ...(memberDefaultRole !== undefined ? { memberDefaultRole } : {}),
    });
    return {
      invite: {
        ...normalizeWorkerInvite(res.invite),
        code: res.code,
        setupCommand: res.setupCommand,
      },
    };
  }

  // "Get new code": same row, new code (the old one stops working at once). Like
  // mint, the plaintext code + setupCommand ride this response ONLY.
  async rotateWorkerInvite(
    workspaceId: string,
    inviteId: string,
  ): Promise<{ invite: WorkerInviteView }> {
    const res = await this.request<{
      code: string;
      setupCommand: string;
      invite: WorkerInviteWire;
    }>("cyborg:rotate_worker_invite", { workspaceId, inviteId });
    return {
      invite: {
        ...normalizeWorkerInvite(res.invite),
        code: res.code,
        setupCommand: res.setupCommand,
      },
    };
  }

  async cancelWorkerInvite(
    workspaceId: string,
    inviteId: string,
  ): Promise<{ invite: WorkerInviteView }> {
    const res = await this.request<{ invite: WorkerInviteWire }>("cyborg:cancel_worker_invite", {
      workspaceId,
      inviteId,
    });
    return { invite: normalizeWorkerInvite(res.invite) };
  }

  // The invite rows ride the cyborg:list_daemons response (no dedicated fetch
  // RPC), so this read inherits list_daemons' coalescing and an older relay
  // (field absent) degrades to an empty list.
  async fetchWorkerInvites(workspaceId: string): Promise<{ invites: WorkerInviteView[] }> {
    const res = await this.request<{ pendingWorkerInvites?: WorkerInviteWire[] }>(
      "cyborg:list_daemons",
      { workspaceId },
    );
    return { invites: (res.pendingWorkerInvites ?? []).map(normalizeWorkerInvite) };
  }

  // ─── Corporate-email domain onboarding (feat/onboarding-company-email) ───
  //
  // Admin claims a company domain (NO DNS — the proof is the admin's own email is
  // at it), picks the join policy, and reviews request-to-join approvals; a
  // signup whose domain matches requests to join. Cloud-only (relay handlers).
  // All admin+ methods are gated SERVER-side on the caller's real workspace role.

  // Admin → claim `domain` for `workspaceId`. Requires the caller's own email to
  // be at `domain`. Optional `joinPolicy` (defaults to "request" server-side).
  // Rejected if the domain is public or already owned by another workspace.
  async claimWorkspaceDomain(
    workspaceId: string,
    domain: string,
    joinPolicy?: JoinPolicy,
  ): Promise<{ domain: WorkspaceEmailDomainView }> {
    return this.request("cyborg:claim_workspace_domain", {
      workspaceId,
      domain,
      ...(joinPolicy !== undefined ? { joinPolicy } : {}),
    });
  }

  // Admin → the domains this workspace has claimed (newest-first).
  async listWorkspaceDomains(
    workspaceId: string,
  ): Promise<{ domains: WorkspaceEmailDomainView[] }> {
    return this.request("cyborg:list_workspace_domains", { workspaceId });
  }

  // Admin → release a claim this workspace owns. Idempotent (a domain not owned
  // here is a no-op success).
  async unclaimWorkspaceDomain(workspaceId: string, domain: string): Promise<{ ok: true }> {
    return this.request("cyborg:unclaim_workspace_domain", { workspaceId, domain });
  }

  // Admin → change the join policy for a claimed domain.
  async setWorkspaceJoinPolicy(
    workspaceId: string,
    domain: string,
    joinPolicy: JoinPolicy,
  ): Promise<{ domain: WorkspaceEmailDomainView }> {
    return this.request("cyborg:set_workspace_join_policy", { workspaceId, domain, joinPolicy });
  }

  // Signup/user → request to join the team that owns the caller's email domain.
  // `targetWorkspaceId` (not `workspaceId`) because the caller is not yet a
  // member — see the server schema. Idempotent on a pending request.
  async requestWorkspaceJoin(
    targetWorkspaceId: string,
  ): Promise<{ request: WorkspaceJoinRequestView }> {
    return this.request("cyborg:request_workspace_join", { targetWorkspaceId });
  }

  // Admin inbox → the join requests for this workspace (optional status filter;
  // the pending-approvals list passes "pending").
  async listJoinRequests(
    workspaceId: string,
    status?: "pending" | "approved" | "rejected",
  ): Promise<{ requests: WorkspaceJoinRequestView[] }> {
    return this.request("cyborg:list_join_requests", {
      workspaceId,
      ...(status !== undefined ? { status } : {}),
    });
  }

  // Admin → approve a pending request (adds the requester as a member). The
  // request id travels as `requestIdToDecide` to avoid colliding with the RPC
  // correlation id. Optional `role` (defaults to "member"; only an owner may
  // approve straight to "admin").
  async approveJoinRequest(
    workspaceId: string,
    requestId: string,
    role?: "admin" | "member" | "viewer",
  ): Promise<{ request: WorkspaceJoinRequestView }> {
    return this.request("cyborg:approve_join_request", {
      workspaceId,
      requestIdToDecide: requestId,
      ...(role !== undefined ? { role } : {}),
    });
  }

  // Admin → reject a pending request (grants nothing).
  async rejectJoinRequest(
    workspaceId: string,
    requestId: string,
  ): Promise<{ request: WorkspaceJoinRequestView }> {
    return this.request("cyborg:reject_join_request", {
      workspaceId,
      requestIdToDecide: requestId,
    });
  }

  // ─── Invite requests (CYBORG-1087: a member asks for an invite, an admin
  // decides) ───

  // Member → ask the owners/admins to invite `email`, with an optional note.
  async requestInvite(
    workspaceId: string,
    email: string,
    note?: string,
  ): Promise<{ request: WorkspaceInviteRequestView }> {
    return this.request("cyborg:request_invite", {
      workspaceId,
      email,
      ...(note !== undefined ? { note } : {}),
    });
  }

  // Admin inbox → the invite requests for this workspace (the approvals list
  // passes "pending").
  async listInviteRequests(
    workspaceId: string,
    status?: "pending" | "approved" | "declined",
  ): Promise<{ requests: WorkspaceInviteRequestView[] }> {
    return this.request("cyborg:list_invite_requests", {
      workspaceId,
      ...(status !== undefined ? { status } : {}),
    });
  }

  // Admin → record the decision. Approving requires the email invite to exist
  // already (the caller sends it through inviteMember first), so this never
  // duplicates the invite path. `requestIdToDecide` avoids colliding with the
  // RPC correlation id, same as the join-request RPCs above.
  async decideInviteRequest(
    workspaceId: string,
    requestId: string,
    decision: "approved" | "declined",
  ): Promise<{ request: WorkspaceInviteRequestView }> {
    return this.request("cyborg:decide_invite_request", {
      workspaceId,
      requestIdToDecide: requestId,
      decision,
    });
  }

  // ─── Page access REQUESTS (REQUEST → NOTIFY → APPROVE, structural clone of
  // Daemon access REQUESTS above, minus scopes — a page grant is per-user,
  // all-or-nothing) ───
  //
  // A non-owner asks a private page's OWNER for access; the owner approves
  // (GRANTING the requester per-user access via pageGrants — the page's
  // visibility is never touched) or denies. The owner is notified live via the
  // page_access_request_changed event + an activity_new badge.

  // Requester → owner: ask for access to `pageId`. channelId/dmPeerId identify
  // where the PageCard the requester clicked was posted (at most one is set —
  // the page's title/icon were already broadcast there, so this adds no new
  // existence leak; see PageCard.svelte). Returns the created (or existing
  // pending) request row.
  async requestPageAccess(
    pageId: string,
    origin?: { channelId?: string | null; dmPeerId?: string | null },
  ): Promise<{ request: PageAccessRequestView }> {
    return this.request("cyborg:request_page_access", {
      pageId,
      channelId: origin?.channelId ?? null,
      dmPeerId: origin?.dmPeerId ?? null,
    });
  }

  // Owner → resolve a pending request. `decision` is "approve" | "deny"; approve
  // GRANTS the requester per-user access (the page's visibility is never
  // touched). Access is per-user, not binary/public — this only ever affects
  // the ONE request being resolved, no fan-out to other requesters for the same
  // page. The request id travels as `requestIdToResolve` to avoid colliding
  // with the RPC correlation id (`requestId`).
  async resolvePageAccessRequest(
    requestId: string,
    decision: "approve" | "deny",
  ): Promise<{ request: PageAccessRequestView }> {
    return this.request("cyborg:resolve_page_access_request", {
      requestIdToResolve: requestId,
      decision,
    });
  }

  // Owner inbox + requester outbox: pending page-access requests for pages the
  // caller owns, plus the caller's own outgoing requests. Mirrors
  // fetchDaemonAccessRequests above — seeded on workspace open so a reload (or
  // an owner who was offline when the request landed) still surfaces an
  // actionable row instead of it going invisible until the next live push.
  async fetchPageAccessRequests(
    workspaceId: string,
  ): Promise<{ requests: PageAccessRequestView[] }> {
    return this.request("cyborg:fetch_page_access_requests", { workspaceId });
  }

  // ─── Page Grants — fetch + revoke ───────────────────────────────────
  // An approve above is not "publish forever" — it's a standing per-user grant
  // the owner can take back. Owner-only server-side, mirrors
  // fetchDaemonAccess/revokeDaemonAccess.

  // Owner-only: the current grantee userIds for `pageId`. Bare ids — resolve
  // display name/avatar via authState.getMemberName/getMemberImage, same as
  // every other member-id render in this app (ownerName above, mentions, etc.).
  async fetchPageGrantees(pageId: string): Promise<{ granteeIds: string[] }> {
    return this.request("cyborg:fetch_page_grantees", { pageId });
  }

  // Owner-only: revoke one grantee's standing access. Idempotent — revoking a
  // grant that's already gone is a no-op success, not an error.
  async revokePageAccess(pageId: string, userId: string): Promise<{ ok: true }> {
    return this.request("cyborg:revoke_page_access", { pageId, userId });
  }

  // Owner-only rename (#441) — the server sets the sticky label_user_set flag
  // so daemon reconnects can never overwrite the chosen name.
  async renameDaemon(
    workspaceId: string,
    daemonId: string,
    label: string,
  ): Promise<{ ok: boolean; daemonId: string; label: string }> {
    return this.request("cyborg:rename_daemon", { workspaceId, daemonId, label });
  }

  // Agents → Skills (admin-managed; assigned agents read them from the relay).
  async fetchSkillScopeProjects(
    workspaceId: string,
  ): Promise<{ projects: Array<{ id: string; name: string }> }> {
    return this.request("cyborg:fetch_projects", { workspaceId });
  }

  async listSkills(workspaceId: string): Promise<{ skills: Skill[] }> {
    return this.request("cyborg:list_skills", { workspaceId });
  }

  async upsertSkill(input: {
    workspaceId: string;
    skillId?: string;
    slug: string;
    name: string;
    description?: string;
    labels?: string[];
    // Absent keeps the stored icon.
    icon?: SkillIcon;
    content: string;
    sourceUrl?: string;
    scope: "workspace" | "project";
    projectId?: string;
    // Absent keeps the skill's stored files; [] clears them; an array replaces them.
    files?: SkillFileUpload[];
    // An edit of a few files; everything else the skill holds stays.
    filesPatch?: { put?: SkillFileUpload[]; remove?: string[] };
  }): Promise<{ skill: Omit<Skill, "assignments"> }> {
    return this.request("cyborg:upsert_skill", input);
  }

  async deleteSkill(workspaceId: string, skillId: string): Promise<{ ok: boolean }> {
    return this.request("cyborg:delete_skill", { workspaceId, skillId });
  }

  async assignSkill(
    workspaceId: string,
    skillId: string,
    cyboId: string,
  ): Promise<{ assignment?: SkillAssignment }> {
    return this.request("cyborg:assign_skill", { workspaceId, skillId, cyboId });
  }

  async unassignSkill(
    workspaceId: string,
    skillId: string,
    cyboId: string,
  ): Promise<{ ok: boolean }> {
    return this.request("cyborg:unassign_skill", { workspaceId, skillId, cyboId });
  }

  // A URL (raw SKILL.md, or a github.com blob/tree of a skill folder) or a marketplace
  // result's identifiers. A folder import lists and reads every file on GitHub, so it gets
  // longer than the default response budget; the relay gives up at 40 s on its own.
  async importSkill(
    workspaceId: string,
    source: string | SkillImportIdentifiers,
  ): Promise<ImportedSkill> {
    const params =
      typeof source === "string" ? { workspaceId, url: source } : { workspaceId, github: source };
    return this.request("cyborg:import_skill", params, 60_000);
  }

  // Searches every public marketplace at once; a source that fails reports ok:false. An empty
  // query browses, with a "new" or "installs" sort or a category.
  async searchSkillMarketplaces(
    workspaceId: string,
    query: string,
    options: { sort?: MarketplaceSort; category?: string } = {},
  ): Promise<{ results: MarketplaceSkill[]; sources: MarketplaceSourceStatus[] }> {
    return this.request("cyborg:search_skill_marketplaces", { workspaceId, query, ...options });
  }

  // Per-user session handoff depth — how many agent-to-agent handoffs
  // (cyborg7_prompt_session) may chain between the caller's OWN sessions before
  // the relay refuses. null = the default. Self-only.
  async getSessionHandoffDepth(): Promise<{ maxDepth: number | null; defaultDepth: number }> {
    return this.request("cyborg:get_session_handoff_depth", {});
  }

  async setSessionHandoffDepth(
    maxDepth: number | null,
  ): Promise<{ maxDepth: number | null; defaultDepth: number }> {
    return this.request("cyborg:set_session_handoff_depth", { maxDepth });
  }

  // Owner-only: which workspaces this daemon could serve, and whether each is
  // enabled (workspace_daemons.enabled).
  async listDaemonWorkspaces(daemonId: string): Promise<{
    workspaces: Array<{ workspaceId: string; name: string; enabled: boolean }>;
  }> {
    return this.request("cyborg:list_daemon_workspaces", { daemonId });
  }

  // Owner-only: enable/disable this daemon for a workspace. Applied live by the relay.
  async setDaemonWorkspace(
    daemonId: string,
    workspaceId: string,
    enabled: boolean,
  ): Promise<{ ok: true }> {
    return this.request("cyborg:set_daemon_workspace", { daemonId, workspaceId, enabled });
  }

  async sync(
    workspaceId: string,
    lastSeq: number,
  ): Promise<{
    mode: "delta" | "snapshot";
    messages: Message[];
    hasMore?: boolean;
    nextSeq?: number;
  }> {
    return this.request("cyborg:sync", { workspaceId, lastSeq });
  }

  // CYBORG-1210: rows edited, deleted, pinned or reacted to since `since` (no `since`
  // arms a cursor). `reset` means the feed cannot answer: read conversations again.
  async messageChanges(workspaceId: string, since?: string): Promise<MessageChanges> {
    return this.request("cyborg:message_changes", { workspaceId, since });
  }

  // ─── Asset uploads ──────────────────────────────────────────────

  // Public so presentational leaves that build a relay HTTP URL from a util
  // (e.g. Avatar → avatarThumbUrl) can read the CURRENT relay origin. Absolute
  // is mandatory on desktop: the renderer origin is `cyborg://app`, whose
  // protocol handler serves local static files only (no /api → relay proxy), so
  // a document-relative `/api/...` would hit the shell, not the relay.
  get httpBase(): string {
    return this.url
      .replace(/^ws:/, "http:")
      .replace(/^wss:/, "https:")
      .replace(/\/api\/ws\/?$/, "")
      .replace(/\/ws\/?$/, "");
  }

  // Subclass access (CyborgClient): the relay's HTTP origin + bearer token, for
  // the Stripe checkout/portal/license REST endpoints that have no WS analogue.
  protected get relayHttpBase(): string {
    return this.httpBase;
  }
  protected get authToken(): string {
    return this.token;
  }

  async uploadAsset(
    file: File,
    folder = "avatars",
    // Upload progress + cancel (#517). `onProgress` reports 0–100 from the S3
    // PUT (web/desktop/Android via XHR); `signal` cancels the in-flight PUT.
    // Both optional — older callers (avatars, posters) pass neither and keep the
    // prior fire-and-forget behavior.
    //
    // `scope` (CYBORG-897 phase 3) is who this object belongs to. The relay records
    // it so a later phase can authorize a read by primary key instead of scanning
    // messages.attachments. Omitting it still uploads — the relay falls back to a
    // folder-derived scope with no workspace — so no caller is obliged to pass one.
    opts?: { onProgress?: (pct: number) => void; signal?: AbortSignal; scope?: AssetUploadScope },
  ): Promise<{ publicUrl: string; key: string }> {
    // Some browsers hand back an EMPTY `file.type` for common video containers
    // (.mkv/.avi/.mov), which the presign allowlist then 415s. Fall back to a
    // mime guessed from the extension, then to a generic type, so the presign
    // always receives a real content-type. putToPresignedUrl replays whatever
    // the server signs, so this value only decides accept/disposition.
    const contentType = file.type || guessContentType(file.name) || "application/octet-stream";
    // Absolute base is mandatory on desktop — a relative /api/... resolves against
    // the renderer's `cyborg://app` origin, whose protocol handler serves static
    // files, so the presign POST silently returns index.html with HTTP 200. That is
    // WORSE than a 404: `presignResp.ok` is true, the refusal branch below is
    // skipped, and the failure only surfaces as an opaque JSON parse error on a
    // chip that says "retry". The two sibling asset calls (mintMediaToken,
    // downloadAsset) have carried this fallback for exactly this reason; the
    // upload path, the one that writes bytes, did not.
    const base = this.httpBase || CLOUD_RELAY_HTTP_BASE;
    const presignResp = await fetch(`${base}/api/assets/presign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
      },
      body: JSON.stringify({
        filename: file.name,
        contentType,
        folder,
        // Declared size — the relay rejects oversized uploads at this checkpoint.
        size: file.size,
        // Ownership attribution (CYBORG-897 phase 3). Spread so the three keys are
        // simply ABSENT when the caller passed no scope: they are optional on the
        // wire, which is what keeps the native iOS uploader (AssetUploader.swift,
        // on Apple's release cadence) working unchanged against a newer relay.
        ...opts?.scope,
      }),
      // Never hang the attachment chip on a stalled network — fail with a clear
      // error the composer can show instead of a spinner that never resolves.
      signal: timeoutSignal(20_000),
    });
    if (!presignResp.ok) {
      // CYBORG-987: a 404 here is not a failed upload, it is NO UPLOADER. The
      // presign route is mounted by the relay only, so a UI talking straight to a
      // local daemon gets a 404 (or the SPA's index.html) for every attachment.
      // Since the inline fallback is gone that used to be invisible and is now a
      // hard failure, so it has to say what is actually wrong — "Upload failed:
      // HTTP 404" sends the reader hunting for a broken bucket that does not exist.
      if (presignResp.status === 404) {
        throw new Error(
          "Attachments need the cloud relay — this server has no file storage. Connect to the relay to send files.",
        );
      }
      const err = await presignResp.json().catch(() => ({ error: "presign failed" }));
      // Carry the HTTP status so a caller can tell a server REFUSAL (415
      // content-type reject) from a transport/config failure. CYBORG-987: no
      // caller branches on it to inline the file any more — fileToAttachment
      // throws on every failure now — but the status is still the only thing
      // that distinguishes "we will never accept this type" from "try again".
      //
      // The status also goes in the MESSAGE, not only the field: every composer
      // stores `e.message` on the chip (MessageInput.uploadPending,
      // attachment-uploads.svelte.ts, RichMessageInput) and drops the field, so
      // the surface the user actually reads would otherwise lose the one number
      // that separates "this file will never be accepted" from "try again".
      const reason = typeof err?.error === "string" ? err.error : "presign failed";
      const presignError = new Error(`${reason} (HTTP ${presignResp.status})`) as Error & {
        status?: number;
      };
      presignError.status = presignResp.status;
      throw presignError;
    }
    const { presignedUrl, publicUrl, key, requiredHeaders } = (await presignResp.json()) as {
      presignedUrl: string;
      publicUrl: string;
      key: string;
      // Security handoff: the presigned S3 PUT now BINDS specific headers (the
      // server signs them). The client MUST replay these EXACT headers on the
      // PUT or S3 returns 403. Older servers omit this field — fall back to a
      // hand-set Content-Type so nothing breaks.
      requiredHeaders?: Record<string, string>;
    };

    await this.putToPresignedUrl(presignedUrl, file, requiredHeaders, opts);

    return { publicUrl, key };
  }

  // Mint a short-lived media pass (CYBORG-897). The relay's key-addressed image
  // routes (/api/assets/thumb, /api/assets/fs/:key) accept EITHER a Bearer pass
  // or this one in a `?t=` query param — an <img src> can only carry the second.
  // The pass is per-USER (not per-asset) and is refused everywhere else, so it is
  // never a session. Call it through `$lib/media/asset-url.svelte.ts`, never
  // directly: that module dedupes N broken images into ONE mint.
  // Absolute base is mandatory on desktop — a relative /api/... resolves against
  // cyborg://app and silently returns index.html.
  async mintMediaToken(): Promise<{ token: string; expiresAt: number }> {
    // CYBORG-1187: before connect the token is "" and `Bearer ` can only 401.
    if (!this.token) throw new NotSignedInError();
    const base = this.httpBase || CLOUD_RELAY_HTTP_BASE;
    const res = await fetchOrTransportError(`${base}/api/assets/media-token`, {
      method: "POST",
      headers: { Authorization: `Bearer ${this.token}` },
      signal: timeoutSignal(15_000),
    });
    if (!res.ok) {
      // Carry the status as a FIELD, not just in the message. asset-url.svelte.ts's
      // retry has to distinguish a relay that is restarting (5xx / network — retry)
      // from a pass that will never mint (401/403 — a revoked session or a suspended
      // account; retrying that forever is pure noise). Parsing the string would work
      // until someone rewords it.
      const err = new Error(`media-token failed: HTTP ${res.status}`) as Error & {
        status?: number;
      };
      err.status = res.status;
      throw err;
    }
    return (await res.json()) as { token: string; expiresAt: number };
  }

  // CYBORG-1187: an HTTP call answered 401 on a live socket means the access token
  // expired after the socket authenticated. Trade the refresh token once through
  // the same `reauth` hook the 4003 path uses. "expired" = no refresh can help;
  // "error" = transient (network, 5xx, 429, or another refresh/session change racing).
  async refreshAuthToken(): Promise<RefreshOutcome["status"]> {
    const reauth = this.reauth;
    if (!reauth || !this.url || !this.token) return "expired";
    // The socket's own refresh is running: a second call would race its rotation.
    if (this.reauthing) return "error";
    const sessionRevision = this.sessionRevision;
    const outcome = await reauth(this.url);
    if (this.sessionRevision !== sessionRevision) return "error";
    if (outcome.status !== "refreshed") return outcome.status;
    this.token = outcome.token;
    this.authTokenRevision += 1;
    return "refreshed";
  }

  // Fetch an attachment's bytes through the relay's authenticated download
  // proxy (CYBORG-851). The S3/CloudFront origins send no CORS headers, so a
  // direct blob-fetch of an attachment URL fails cross-origin; the relay is a
  // CORS-allowed origin and streams the object with Content-Disposition:
  // attachment. Bearer-only auth means this MUST be a fetch (a bare <a href>
  // can't send the token). Absolute base is mandatory on desktop — a relative
  // /api/... resolves against cyborg://app and silently returns index.html.
  async downloadAsset(key: string, name: string): Promise<Blob> {
    const base = this.httpBase || CLOUD_RELAY_HTTP_BASE;
    const res = await fetch(
      `${base}/api/assets/download?key=${encodeURIComponent(key)}&name=${encodeURIComponent(name)}`,
      {
        headers: { Authorization: `Bearer ${this.token}` },
        signal: timeoutSignal(60_000),
      },
    );
    if (!res.ok) throw new Error(`download failed: HTTP ${res.status}`);
    return res.blob();
  }

  /**
   * PUT the file bytes to the S3 presigned URL.
   *
   * On the Tauri iOS shell the WKWebView origin is `tauri.localhost`, which the
   * S3 bucket's CORS policy does NOT allow (it only allows `app.cyborg7.com`).
   * A plain `fetch` PUT from the WebView therefore gets CORS-blocked and fails
   * on device. v1 solved this by routing the PUT through Tauri's HTTP plugin
   * (`@tauri-apps/plugin-http`), whose `fetch` runs in Rust/reqwest and sends NO
   * browser `Origin` header — so S3 accepts it. We mirror that here.
   *
   * Web/desktop/Android keep the plain `fetch` path (same-origin or normal CORS
   * applies). The presign POST above stays on `window.fetch` (it's same-origin
   * to the relay).
   *
   * `requiredHeaders` are the headers the presign endpoint BOUND into the
   * signature (Content-Type, and Content-Disposition for forced-download
   * types). They MUST be replayed verbatim on the PUT or S3 rejects the request
   * with 403 (SignatureDoesNotMatch). When the server omits them (older relay),
   * we fall back to a hand-set Content-Type — the legacy behavior.
   */
  private async putToPresignedUrl(
    presignedUrl: string,
    file: File,
    requiredHeaders?: Record<string, string>,
    // #517: optional upload-progress + cancel. Honored on the web/desktop/Android
    // XHR path. The Tauri-iOS plugin-http path supports cancel via `signal` but
    // NOT progress (the Rust fetch has no upload-progress callback, and a browser
    // XHR can't be used there — S3 CORS blocks the `tauri.localhost` origin), so
    // iOS keeps the indeterminate spinner.
    opts?: { onProgress?: (pct: number) => void; signal?: AbortSignal },
  ): Promise<void> {
    // The headers the signed PUT requires. Prefer the server-bound set; fall
    // back to today's behavior (Content-Type only) for older relays.
    const putHeaders: Record<string, string> =
      requiredHeaders && Object.keys(requiredHeaders).length > 0
        ? { ...requiredHeaders }
        : { "Content-Type": file.type };
    const isTauriIOS =
      typeof window !== "undefined" &&
      Boolean(
        (window as { __TAURI_INTERNALS__?: unknown; __TAURI__?: unknown }).__TAURI_INTERNALS__ ??
        (window as { __TAURI__?: unknown }).__TAURI__,
      ) &&
      typeof navigator !== "undefined" &&
      /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isTauriIOS) {
      try {
        const { fetch: tauriFetch } = await import("@tauri-apps/plugin-http");
        const uploadResp = await tauriFetch(presignedUrl, {
          method: "PUT",
          headers: putHeaders,
          // Tauri's HTTP plugin streams an ArrayBuffer/Uint8Array body; a File/Blob
          // isn't transferable across the IPC boundary, so read the bytes first.
          body: new Uint8Array(await file.arrayBuffer()),
          // Bound the PUT so a stalled S3 connection can't spin the chip forever,
          // AND honor an external cancel (#517) — whichever fires first aborts.
          signal: opts?.signal
            ? anySignal([opts.signal, timeoutSignal(45_000)])
            : timeoutSignal(45_000),
        });
        if (!uploadResp.ok) {
          // Surface S3's XML <Code> (e.g. AccessDenied / SignatureDoesNotMatch)
          // instead of an opaque "Load failed".
          const detail = await uploadResp.text().catch(() => "");
          const code = detail.match(/<Code>([^<]+)<\/Code>/)?.[1];
          throw new Error(`Upload failed: HTTP ${uploadResp.status}${code ? ` (${code})` : ""}`);
        }
        return;
      } catch (err) {
        // If the plugin itself is unavailable (shouldn't happen on iOS), fall
        // through to plain fetch rather than hard-failing.
        if (err instanceof Error && err.message.startsWith("Upload failed:")) throw err;
        // The upload still goes through below; this is the plugin fault, once an hour.
        if (shouldReport("upload.tauri_put_fallback", 3_600_000)) {
          reportUiFailure("upload.tauri_put_fallback", err);
        }
      }
    }

    // Web/desktop/Android: PUT via XMLHttpRequest (not fetch) — XHR is the only
    // broadly-supported way to observe UPLOAD progress in a browser/WebView
    // (`fetch` can't report request-body progress). #517.
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", presignedUrl);
      for (const [k, v] of Object.entries(putHeaders)) xhr.setRequestHeader(k, v);
      xhr.timeout = 45_000; // same stall ceiling as the old fetch PUT
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable && opts?.onProgress) {
          opts.onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          // Surface S3's XML <Code> (AccessDenied / SignatureDoesNotMatch) like
          // the Tauri path, instead of an opaque status.
          const code = xhr.responseText?.match(/<Code>([^<]+)<\/Code>/)?.[1];
          reject(new Error(`Upload failed: HTTP ${xhr.status}${code ? ` (${code})` : ""}`));
        }
      });
      xhr.addEventListener("error", () => reject(new Error("Upload failed: network error")));
      xhr.addEventListener("timeout", () => reject(new Error("Upload failed: timed out")));
      // Cancel (#517): abort the in-flight PUT when the caller's signal fires
      // (e.g. the user removes the chip mid-upload).
      xhr.addEventListener("abort", () =>
        reject(new DOMException("Upload cancelled", "AbortError")),
      );
      if (opts?.signal) {
        if (opts.signal.aborted) {
          // Already aborted before send: xhr.abort() fires no "abort" event
          // pre-send, so reject directly or the promise would hang forever.
          reject(new DOMException("Upload cancelled", "AbortError"));
          return;
        }
        opts.signal.addEventListener("abort", () => xhr.abort(), { once: true });
      }
      xhr.send(file);
    });
  }

  async getAssetsConfig(): Promise<{
    enabled: boolean;
    bucket: string | null;
    region: string;
    baseUrl: string | null;
  }> {
    // Same absolute-base requirement as every other /api/assets call above.
    const base = this.httpBase || CLOUD_RELAY_HTTP_BASE;
    const resp = await fetch(`${base}/api/assets/config`, {
      headers: { Authorization: `Bearer ${this.token}` },
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return resp.json();
  }

  // ─── Members ────────────────────────────────────────────────────

  async inviteMember(
    workspaceId: string,
    email: string,
    role?: "admin" | "member" | "viewer",
    channelIds?: string[],
    // keepChannelIds: ADD channelIds to a pending invite instead of replacing its channels
    // (the channel Members tab invites from one channel and must not drop the others).
    // Optional and additive: an older relay ignores it and replaces, as it does today.
    opts?: { keepChannelIds?: boolean },
  ): Promise<{
    membership: Membership;
    invitationId: string;
    inviteUrl: string;
    alreadyInvited: boolean;
  }> {
    const resp = await this.request<{
      membership: Membership;
      invitationId: string;
      inviteUrl: string;
      alreadyInvited?: boolean;
    }>("cyborg:invite_member", {
      workspaceId,
      email,
      role,
      // Prod relay ignores channelIds until the new relay deploy lands; harmless.
      channelIds,
      keepChannelIds: opts?.keepChannelIds,
    });
    return {
      membership: resp.membership,
      invitationId: resp.invitationId,
      inviteUrl: resp.inviteUrl,
      // Absent on an older relay: treat as a fresh invite.
      alreadyInvited: resp.alreadyInvited === true,
    };
  }

  // Single reusable workspace invite link ("open invite"). NEW relay endpoints —
  // they DON'T exist on the prod relay yet, so callers must try/catch (the modal
  // shows a soft "available after next update" note on error).
  async getOpenInvite(
    workspaceId: string,
  ): Promise<{ token: string; inviteUrl: string; role: string; channelIds: string[] } | null> {
    const resp = await this.request<{
      invite: { token: string; inviteUrl: string; role: string; channelIds: string[] } | null;
    }>("cyborg:get_open_invite", { workspaceId });
    return resp.invite;
  }

  // Create or update the reusable invite link. `rotate:true` mints a brand-new
  // token (the "Reset link" action), invalidating the old one.
  async upsertOpenInvite(
    workspaceId: string,
    role: string,
    channelIds: string[],
    rotate = false,
  ): Promise<{ token: string; inviteUrl: string; role: string; channelIds: string[] }> {
    const resp = await this.request<{
      invite: { token: string; inviteUrl: string; role: string; channelIds: string[] };
    }>("cyborg:upsert_open_invite", { workspaceId, role, channelIds, rotate });
    return resp.invite;
  }

  // Re-issue an existing pending invitation: resets the 7-day expiry and
  // re-sends the invite email. Returns the new sentAt timestamp so the UI can
  // refresh the row's countdown.
  async resendInvitation(
    workspaceId: string,
    invitationId: string,
  ): Promise<{ invitationId: string; sentAt: number }> {
    return this.request<{ invitationId: string; sentAt: number }>("cyborg:resend_invitation", {
      workspaceId,
      invitationId,
    });
  }

  // Redeem an invitation token for the authenticated user. Returns the new
  // membership + the workspace the user just joined so the caller can navigate
  // into it. The token comes from the /invite/:token landing page.
  async acceptInvitation(invitationToken: string): Promise<AcceptInvitationResult> {
    return this.request<AcceptInvitationResult>("cyborg:accept_invitation", { invitationToken });
  }

  // The invitee's arrival screen (about the caller only; the relay reads who from the session).
  async inviteeArrival(workspaceId: string): Promise<InviteeArrivalResponse> {
    return this.request<InviteeArrivalResponse>("cyborg:invitee_arrival", { workspaceId });
  }

  // Admin/owner: list the workspace's still-pending invitations (not yet
  // accepted, not expired-and-purged) for the Members settings section.
  async listPendingInvitations(workspaceId: string): Promise<{
    invitations: Array<{
      id: string;
      email: string;
      role: string;
      createdAt: number;
      expiresAt: number;
      createdByName?: string;
    }>;
  }> {
    return this.request<{
      invitations: Array<{
        id: string;
        email: string;
        role: string;
        createdAt: number;
        expiresAt: number;
        createdByName?: string;
      }>;
    }>("cyborg:list_pending_invitations", { workspaceId });
  }

  // Admin/owner: revoke a pending invitation so its token can no longer be
  // redeemed. Returns the cancelled invitation id.
  async cancelInvitation(
    workspaceId: string,
    invitationId: string,
  ): Promise<{ invitationId: string }> {
    return this.request<{ invitationId: string }>("cyborg:cancel_invitation", {
      workspaceId,
      invitationId,
    });
  }

  /** Resolves to the agents whose hand-off failed (the member is removed regardless). */
  async removeMember(workspaceId: string, userId: string, handoffs?: Handoff[]): Promise<string[]> {
    const resp = await this.request<{ removed: boolean; failedCyboIds?: string[] }>(
      "cyborg:remove_member",
      { workspaceId, userId, ...(handoffs ? { handoffs } : {}) },
    );
    return resp.failedCyboIds ?? [];
  }

  async updateRole(
    workspaceId: string,
    userId: string,
    role: "admin" | "member" | "viewer",
  ): Promise<boolean> {
    const resp = await this.request<{ updated: boolean }>("cyborg:update_role", {
      workspaceId,
      userId,
      role,
    });
    return resp.updated;
  }

  async listMembers(
    workspaceId: string,
    opts: { withFormer?: boolean } = {},
  ): Promise<{
    members: WorkspaceMember[];
    external: ExternalParticipant[];
    // Only with withFormer (the search bar); an older relay never sends it.
    former?: FormerParticipant[];
  }> {
    const resp = await this.request<{
      members: WorkspaceMember[];
      external?: ExternalParticipant[];
      former?: FormerParticipant[];
    }>("cyborg:list_members", { workspaceId, ...(opts.withFormer ? { withFormer: true } : {}) });
    return { members: resp.members, external: resp.external ?? [], former: resp.former ?? [] };
  }

  // ── Cyborg7 Connect ──────────────────────────────────────────────────────
  //
  // Spec §1.7's number: what adding somebody from another company to this channel
  // would expose. Messages, FILES (not messages-carrying-files) and the date of
  // the oldest message, which is `null` on an empty channel.
  //
  // The caller must treat a THROW as "we do not know" and say so — never as zero.
  // The whole point of the screen is an honest number, and a fabricated 0 there is
  // worse than Slack, which at least says nothing at all.
  async channelHistorySize(
    workspaceId: string,
    channelId: string,
  ): Promise<{ messages: number; files: number; oldestAt: number | null }> {
    return this.request<{ messages: number; files: number; oldestAt: number | null }>(
      "cyborg:channel_history_size",
      { workspaceId, channelId },
    );
  }

  // Spec §1.1's invite: somebody from another organization into ONE channel, never
  // into the workspace. `channelId` is REQUIRED by the relay and travels with the
  // row as the invite's landing channel — the invite is minted from a channel's
  // settings ("bring somebody from outside into #proyecto-orion"), so the channel is
  // part of it from the first keystroke rather than a later decision.
  //
  // No role, no tier, no history flag: there are none (spec §1.4), and the history
  // decision was already made on the interstitial before this call.
  //
  // Answers with the `workspace_externals` row, which no caller needs — the two facts
  // the screen states afterwards (14-day expiry, revocable) are the same for every
  // invite. What matters is that a refusal (not an admin, no Cyborg account for that
  // address, already a member, a private channel the inviter cannot see) REJECTS, so
  // the caller can never paint a sent invitation over a declined one.
  //
  // TWO OUTCOMES, and the caller has to tell them apart (spec §8.5): a fresh invite
  // that was emailed and awaits acceptance, or an ALREADY-ACTIVE connection that the
  // relay seated in the channel on the spot with no email at all.
  async inviteExternal(
    workspaceId: string,
    channelId: string,
    email: string,
  ): Promise<{ seated: boolean }> {
    const resp = await this.request<{ seated?: boolean }>("cyborg:invite_external", {
      workspaceId,
      channelId,
      email,
    });
    // `seated` = they were ALREADY an active connection and went straight into the
    // channel; no email was sent and there is nothing to accept. Defaulted to false
    // so an older relay (which sends neither field) still reads as "invited", which
    // is what it did.
    return { seated: resp?.seated === true };
  }

  // END A CONNECTION WITH A WHOLE ORGANIZATION, IN ONE CALL.
  //
  // `cyborg:revoke_external { workspaceId, userId }` still exists and is what a
  // channel's Members tab uses — one person, one channel's roster. It is the wrong
  // shape for the Connections screen, where one row IS a company and holds N people:
  // looping it from here is not atomic, and a failure halfway leaves half a company
  // still connected with a screen that has already painted the row as gone. So the
  // whole organization is one server call and one transaction.
  //
  // `orgId` is exactly the id the connection row carries: the partner's own workspace
  // id, or the synthetic `external:<userId>` of an invitation nobody has accepted yet.
  //
  // REJECTS on refusal, and the caller must render a refusal as a refusal — the row
  // stays, the dialog says why, nothing paints success.
  async revokeExternalOrg(workspaceId: string, orgId: string): Promise<void> {
    await this.request<unknown>("cyborg:revoke_external_org", { workspaceId, orgId });
  }

  // CYBORG-791: the workspace's cybo work sessions (relay reads the PG mirror). The
  // relay scopes DM-anchored sessions to their initiator; channel work is shared.
  async listWorkSessions(workspaceId: string): Promise<WorkSession[]> {
    const resp = await this.request<{ sessions: WorkSession[] }>("cyborg:list_work_sessions", {
      workspaceId,
    });
    return resp.sessions ?? [];
  }

  // Close a work session — sets it to "done" and clears its pause reason. Relay-direct
  // (the relay is the PG writer), open to ANY member who can see the session. Returns the
  // updated row so the caller can apply it immediately instead of waiting for the
  // work_session_changed echo the relay also fans out.
  async closeWorkSession(workspaceId: string, sessionId: string): Promise<WorkSession | null> {
    const resp = await this.request<{ session: WorkSession | null }>("cyborg:close_work_session", {
      workspaceId,
      sessionId,
    });
    return resp.session ?? null;
  }

  // Archive a work session for everyone who sees it, or keep it listed (relay-direct, shared).
  async setWorkSessionArchive(
    workspaceId: string,
    sessionId: string,
    archived: boolean,
  ): Promise<void> {
    await this.request("cyborg:set_work_session_archive", { workspaceId, sessionId, archived });
  }

  // The pages a work session created or edited, from the relay's timeline mirror (relay-direct).
  async fetchWorkSessionPages(
    workspaceId: string,
    sessionId: string,
  ): Promise<Array<{ pageId: string; title: string; at?: number }>> {
    const resp = await this.request<{
      pages?: Array<{ pageId: string; title: string; at?: number }>;
    }>("cyborg:fetch_work_session_pages", { workspaceId, sessionId });
    return resp.pages ?? [];
  }

  // CYBORG-1080: WHICH of the two "not found or no access" cases a session id is, so the
  // page can say what happened and offer the right next step instead of one sentence for
  // both. A VISIBLE session comes back with its row, so a deep link the store never
  // hydrated opens instead of reading as gone. Rejects on an older relay (unknown message
  // type), and the caller keeps today's generic wording rather than guessing an answer.
  async getWorkSessionAccess(
    workspaceId: string,
    sessionId: string,
  ): Promise<
    { access: "visible"; session: WorkSession } | { access: "no_access" } | { access: "not_found" }
  > {
    const resp = await this.request<{
      access?: string;
      session?: WorkSession | null;
    }>("cyborg:work_session_access", { workspaceId, sessionId });
    if (resp.access === "visible" && resp.session) {
      return { access: "visible", session: resp.session };
    }
    // ONE no-access answer, never a reason: why would reveal a private channel exists.
    if (resp.access === "no_access") return { access: "no_access" };
    if (resp.access === "not_found") return { access: "not_found" };
    throw new Error("Couldn't check this work session");
  }

  // ─── MCP tokens (settings) ───────────────────────────────────────

  async mcpList(workspaceId: string): Promise<McpListResponse> {
    return this.request<McpListResponse>("cyborg:mcp_list", { workspaceId });
  }

  async mcpSetEnabled(workspaceId: string, enabled: boolean): Promise<boolean> {
    const resp = await this.request<{ enabled: boolean }>("cyborg:mcp_set_enabled", {
      workspaceId,
      enabled,
    });
    return resp.enabled;
  }

  async mcpCreateToken(
    workspaceId: string,
    opts: {
      name: string;
      identityType: "cybo" | "user";
      identityId: string;
      scopes: string[];
      expiresInDays?: number;
      clientMintId?: string;
    },
  ): Promise<{ id: string; token: string; replacedKeyId?: string }> {
    return this.request<{ id: string; token: string; replacedKeyId?: string }>(
      "cyborg:mcp_create_token",
      {
        workspaceId,
        ...opts,
      },
    );
  }

  async mcpToggleToken(workspaceId: string, tokenId: string, enabled: boolean): Promise<boolean> {
    const resp = await this.request<{ updated: boolean }>("cyborg:mcp_toggle_token", {
      workspaceId,
      tokenId,
      enabled,
    });
    return resp.updated;
  }

  // "Test connection": ask the relay why a token does or doesn't work, using the
  // same facts /mcp authenticates with. Never cached or coalesced — the answer is
  // about right now — and the value is never stored client-side either.
  async mcpTestToken(workspaceId: string, token: string): Promise<McpTokenTest> {
    return this.request<McpTokenTest>("cyborg:mcp_test_token", { workspaceId, token });
  }

  async mcpListForIdentity(
    workspaceId: string,
    identityType: "cybo" | "user",
    identityId: string,
  ): Promise<McpIdentityToken[]> {
    const resp = await this.request<{ tokens: McpIdentityToken[] }>(
      "cyborg:mcp_list_for_identity",
      { workspaceId, identityType, identityId },
    );
    return resp.tokens;
  }

  async mcpRevokeForIdentity(
    workspaceId: string,
    identityType: "cybo" | "user",
    identityId: string,
    tokenId: string,
  ): Promise<boolean> {
    const resp = await this.request<{ revoked: boolean }>("cyborg:mcp_revoke_for_identity", {
      workspaceId,
      identityType,
      identityId,
      tokenId,
    });
    return resp.revoked;
  }
  // External agents (knowledge/157): the New agent wizard's RPCs (external-agent-rpc.ts).
  async createExternalAgent(
    workspaceId: string,
    input: { name: string; handle: string; channelIds: string[]; avatar?: string },
  ): Promise<{ cybo: { id: string; slug: string; name: string }; channelIds: string[] }> {
    return this.request("cyborg:create_external_agent", { workspaceId, ...input });
  }

  async externalAgentStatus(workspaceId: string, cyboId: string): Promise<ExternalAgentStatus> {
    const resp = await this.request<{ status: ExternalAgentStatus }>(
      "cyborg:external_agent_status",
      { workspaceId, cyboId },
    );
    return resp.status;
  }

  async externalAgentSignInLink(
    workspaceId: string,
    cyboId: string,
  ): Promise<{ expiresAt: number; reconnectMessage: string }> {
    return this.request("cyborg:external_agent_sign_in_link", { workspaceId, cyboId });
  }

  async externalAgentTest(
    workspaceId: string,
    cyboId: string,
    channelId: string,
  ): Promise<{ messageId: string; channelId: string; startedAt: number }> {
    return this.request("cyborg:external_agent_test", { workspaceId, cyboId, channelId });
  }

  async externalAgentTestResult(
    workspaceId: string,
    cyboId: string,
    messageId: string,
  ): Promise<ExternalAgentTestResult> {
    return this.request("cyborg:external_agent_test_result", { workspaceId, cyboId, messageId });
  }

  async listAgentTriggers(workspaceId: string, cyboId: string): Promise<AgentTrigger[]> {
    const resp = await this.request<{ webhooks?: AgentTrigger[] }>(
      "cyborg:fetch_outgoing_webhooks",
      { workspaceId, cyboId },
    );
    return resp.webhooks ?? [];
  }

  async createAgentTrigger(
    workspaceId: string,
    cyboId: string,
    input: AgentTriggerPatch & { url: string },
  ): Promise<AgentTriggerMutation> {
    return this.request<AgentTriggerMutation>("cyborg:create_outgoing_webhook", {
      workspaceId,
      cyboId,
      ...input,
    });
  }

  async updateAgentTrigger(
    workspaceId: string,
    id: string,
    patch: AgentTriggerPatch,
  ): Promise<AgentTriggerMutation> {
    return this.request<AgentTriggerMutation>("cyborg:update_outgoing_webhook", {
      workspaceId,
      id,
      ...patch,
    });
  }

  async deleteAgentTrigger(workspaceId: string, id: string): Promise<AgentTriggerMutation> {
    return this.request<AgentTriggerMutation>("cyborg:delete_outgoing_webhook", {
      workspaceId,
      id,
    });
  }

  async testAgentTrigger(workspaceId: string, id: string): Promise<AgentTriggerTestResult> {
    return this.request<AgentTriggerTestResult>("cyborg:test_agent_trigger", { workspaceId, id });
  }

  async fetchAgentTriggerDeliveries(
    workspaceId: string,
    id: string,
    limit = 20,
  ): Promise<{ ok: boolean; error?: string; deliveries: AgentTriggerDelivery[] }> {
    return this.request("cyborg:fetch_agent_trigger_deliveries", { workspaceId, id, limit });
  }

  async mcpRevokeToken(workspaceId: string, tokenId: string): Promise<boolean> {
    const resp = await this.request<{ revoked: boolean }>("cyborg:mcp_revoke_token", {
      workspaceId,
      tokenId,
    });
    return resp.revoked;
  }

  // The consent API is HTTP on the relay (routes/mcp-oauth.ts), signed in with this session's bearer.
  private async mcpConsentCall<T>(
    requestId: string,
    action?: "approve" | "deny",
    body?: Record<string, unknown>,
  ): Promise<T> {
    const base = this.httpBase || CLOUD_RELAY_HTTP_BASE;
    const path = `${encodeURIComponent(requestId)}${action ? `/${action}` : ""}`;
    const resp = await fetch(`${base}/api/mcp-oauth/requests/${path}`, {
      method: body ? "POST" : "GET",
      headers: {
        Authorization: `Bearer ${this.token}`,
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
      signal: timeoutSignal(20_000),
    });
    let json: unknown;
    try {
      json = await resp.json();
    } catch (err) {
      // A non-JSON answer (a proxy's error page) is a failure whatever the status.
      throw new McpConsentError(`http_${resp.status}`, resp.status, undefined, { cause: err });
    }
    const fields = (json ?? {}) as { error?: unknown; message?: unknown }; // cast: read field by field below
    if (!resp.ok) {
      const code = typeof fields.error === "string" ? fields.error : `http_${resp.status}`;
      const message = typeof fields.message === "string" ? fields.message : undefined;
      throw new McpConsentError(code, resp.status, message);
    }
    return json as T; // cast: the relay's documented consent shape (knowledge/147)
  }

  async mcpConsentRequest(requestId: string): Promise<McpConsentRequest> {
    return this.mcpConsentCall<McpConsentRequest>(requestId);
  }

  async mcpConsentApprove(
    requestId: string,
    body: { workspaceId: string; actAs: McpConsentActAs },
  ): Promise<{ redirectTo: string }> {
    return this.mcpConsentCall(requestId, "approve", body);
  }

  async mcpConsentDeny(requestId: string, workspaceId?: string): Promise<{ redirectTo: string }> {
    return this.mcpConsentCall(requestId, "deny", workspaceId ? { workspaceId } : {});
  }

  // ─── Webhooks (inbound, GitHub-style config) ─────────────────────

  async webhookList(workspaceId: string, channelId: string): Promise<Webhook[]> {
    return (await this.webhookConfig(workspaceId, channelId)).webhooks;
  }

  // The list plus the relay's action catalog (event -> its actions): the one list the panel renders.
  async webhookConfig(workspaceId: string, channelId: string): Promise<WebhookConfig> {
    const resp = await this.request<{
      webhooks: Webhook[];
      actionCatalog?: Record<string, string[]>;
    }>("cyborg:webhook_list", { workspaceId, channelId });
    return { webhooks: resp.webhooks, actionCatalog: resp.actionCatalog ?? {} };
  }

  // Create a webhook config. `generateSecret` mints an HMAC secret returned ONCE
  // in the response (never re-fetchable).
  async webhookCreate(
    workspaceId: string,
    channelId: string,
    opts: {
      name: string;
      eventMode: "release" | "all" | "select";
      events?: string[];
      eventActions?: Record<string, string[]>;
      contentType?: string;
      active?: boolean;
      generateSecret?: boolean;
      // Fire this cybo on every delivery. Rejected unless the caller could run it
      // themselves (relay-side spawn-scope check).
      triggerCyboId?: string | null;
      // Saved as the webhook's prompt template; "" clears it.
      instructions?: string;
      // Posts appear as this agent (it must sit in the channel; needs a secret). Omit for the creator.
      actorCyboId?: string;
    },
  ): Promise<{ id: string; secret: string | null }> {
    return this.request<{ id: string; secret: string | null }>("cyborg:webhook_create", {
      workspaceId,
      channelId,
      ...opts,
    });
  }

  async webhookUpdate(
    workspaceId: string,
    webhookId: string,
    patch: {
      name?: string;
      eventMode?: "release" | "all" | "select";
      events?: string[];
      // OMIT to leave the filter untouched; {} or null clears it (every action passes).
      eventActions?: Record<string, string[]> | null;
      contentType?: string;
      active?: boolean;
      // OMIT to leave the trigger untouched; pass null to clear it. Never spread a
      // whole webhook object in here — that would turn every edit into a re-assert.
      triggerCyboId?: string | null;
      // OMIT to leave untouched; "" clears. The server refuses anyone but the creator.
      instructions?: string;
    },
  ): Promise<boolean> {
    const resp = await this.request<{ updated: boolean }>("cyborg:webhook_update", {
      workspaceId,
      webhookId,
      ...patch,
    });
    return resp.updated;
  }

  // Rotate (or clear, with `clear: true`) the signing secret. A fresh secret is
  // returned ONCE; `clear` returns null and disables signature verification.
  async webhookRotateSecret(
    workspaceId: string,
    webhookId: string,
    clear = false,
  ): Promise<{ updated: boolean; secret: string | null }> {
    return this.request<{ updated: boolean; secret: string | null }>(
      "cyborg:webhook_rotate_secret",
      { workspaceId, webhookId, clear },
    );
  }

  async webhookDelete(workspaceId: string, webhookId: string): Promise<boolean> {
    const resp = await this.request<{ deleted: boolean }>("cyborg:webhook_delete", {
      workspaceId,
      webhookId,
    });
    return resp.deleted;
  }

  async webhookListDeliveries(workspaceId: string, webhookId: string): Promise<WebhookDelivery[]> {
    const resp = await this.request<{ deliveries: WebhookDelivery[] }>(
      "cyborg:webhook_list_deliveries",
      { workspaceId, webhookId },
    );
    return resp.deliveries;
  }

  async webhookSources(workspaceId: string, channelId: string): Promise<WebhookSource[]> {
    const resp = await this.request<{ sources: WebhookSource[] }>("cyborg:webhook_sources", {
      workspaceId,
      channelId,
    });
    return resp.sources;
  }

  async webhookRules(
    workspaceId: string,
    channelId: string,
    sourceId: string,
  ): Promise<WebhookRulesModel> {
    const resp = await this.request<{
      rules: WebhookRule[];
      actions: WebhookRuleAction[];
      policies?: WebhookRulePolicy[];
    }>("cyborg:webhook_rules", { workspaceId, channelId, sourceId });
    return { rules: resp.rules, actions: resp.actions, policies: resp.policies ?? [] };
  }

  async webhookChannelRules(workspaceId: string, channelId: string): Promise<WebhookChannelRules> {
    const resp = await this.request<{
      sources: WebhookSource[];
      rules: WebhookRule[];
      actions: WebhookRuleAction[];
      policies?: WebhookRulePolicy[];
    }>("cyborg:webhook_rules", { workspaceId, channelId });
    return { ...resp, policies: resp.policies ?? [] };
  }

  // Preview only: the relay persists nothing and runs no action (D24).
  async webhookActionPlan(
    workspaceId: string,
    channelId: string,
    sourceId: string,
    plan: Record<string, unknown>,
  ): Promise<WebhookActionPlanResult> {
    return this.request<WebhookActionPlanResult>("cyborg:webhook_action_plan", {
      workspaceId,
      channelId,
      sourceId,
      plan,
    });
  }

  // Creates an observe-mode rule (W8); the relay derives owner and run-as from the caller.
  async webhookRuleCreate(
    workspaceId: string,
    channelId: string,
    sourceId: string,
    plan: Record<string, unknown>,
  ): Promise<WebhookRulePolicy> {
    const resp = await this.request<{ rule: WebhookRulePolicy }>("cyborg:webhook_rule_create", {
      workspaceId,
      channelId,
      sourceId,
      plan,
    });
    return resp.rule;
  }

  async outboundSubscriptionList(workspaceId: string): Promise<OutboundSubscription[]> {
    const resp = await this.request<{ subscriptions: OutboundSubscription[] }>(
      "cyborg:outbound_subscription_list",
      { workspaceId },
    );
    return resp.subscriptions;
  }

  // The signing secret comes back once, here.
  async outboundSubscriptionCreate(
    workspaceId: string,
    subscription: OutboundSubscriptionInput,
  ): Promise<{ id: string; secret: string }> {
    return this.request<{ id: string; secret: string }>("cyborg:outbound_subscription_create", {
      workspaceId,
      subscription,
    });
  }

  // Replaces the whole subscription; undelivered events for the old destination are dropped.
  async outboundSubscriptionReplace(
    workspaceId: string,
    id: string,
    subscription: OutboundSubscriptionInput,
  ): Promise<{ id: string }> {
    return this.request<{ id: string }>("cyborg:outbound_subscription_replace", {
      workspaceId,
      id,
      subscription,
    });
  }

  async outboundSubscriptionDelete(workspaceId: string, id: string): Promise<{ id: string }> {
    return this.request<{ id: string }>("cyborg:outbound_subscription_delete", { workspaceId, id });
  }

  // One page, newest first; follow `nextCursor` for older events.
  async webhookActivity(
    workspaceId: string,
    query: WebhookActivityQuery = {},
  ): Promise<WebhookActivityPage> {
    return this.request<WebhookActivityPage>("cyborg:webhook_activity", {
      workspaceId,
      ...query,
    });
  }

  // A person's explicit, confirmed request (D24): `execute` and `confirmed` are both required.
  async webhookActivityOperation(
    operation: "retry" | "replay",
    input: {
      workspaceId: string;
      channelId: string;
      sourceId: string;
      eventId: string;
      runId?: string;
      operationId: string;
    },
  ): Promise<Record<string, unknown>> {
    return this.request<Record<string, unknown>>(`cyborg:webhook_${operation}`, {
      ...input,
      execute: true,
      confirmed: true,
    });
  }

  // ─── Tasks ──────────────────────────────────────────────────────

  async createTask(
    workspaceId: string,
    title: string,
    opts?: {
      description?: string;
      assigneeId?: string;
      dueAt?: number;
      // ─── Tasks Redesign (Plane-shaped) — all optional, back-compat ────
      // The relay accepts these camelCase keys and persists the Plane model.
      // `labels` are label NAMES (auto-created in the project's catalog), NOT
      // ids; `moduleIds` are module ids.
      projectId?: string;
      parentId?: string | null;
      stateId?: string;
      priority?: TaskPriority | string;
      // Planned start (epoch ms); null clears it.
      startDate?: number | null;
      labels?: string[];
      cycleId?: string | null;
      moduleIds?: string[];
      // The chat message this task is seeded from (CYBORG-1085). The relay refuses
      // a private channel or DM source; absent = an ordinary task.
      sourceMessageId?: string;
    },
  ): Promise<Task> {
    const resp = await this.request<{ task: RawTask }>("cyborg:create_task", {
      workspaceId,
      title,
      ...opts,
    });
    return mapRawTask(resp.task);
  }

  async updateTask(
    workspaceId: string,
    taskId: string,
    updates: {
      status?: string;
      title?: string;
      description?: string;
      assigneeId?: string | null;
      // null clears the due date; a number is epoch-ms (local end-of-day).
      dueAt?: number | null;
      result?: string;
      // ─── Tasks Redesign (Plane-shaped) — all optional, back-compat ────
      // Pass null to clear a scalar (projectId/parentId/stateId/startDate/
      // cycleId); an empty `labels`/`moduleIds` array clears that set. `labels`
      // are label NAMES (auto-created), NOT ids.
      priority?: TaskPriority | string | null;
      projectId?: string | null;
      parentId?: string | null;
      stateId?: string | null;
      startDate?: number | null;
      labels?: string[];
      cycleId?: string | null;
      moduleIds?: string[];
    },
  ): Promise<Task> {
    const resp = await this.request<{ task: RawTask }>("cyborg:update_task", {
      workspaceId,
      taskId,
      ...updates,
    });
    return mapRawTask(resp.task);
  }

  // Bulk-edit many tasks in one round-trip (multi-select → e.g. "Move to status").
  // The relay reads `updates` NESTED (not spread like update_task), applies each
  // allowed field (status / priority / assigneeId / dueAt / archivedAt) per task it
  // can see, and echoes back the updated rows (+ a tasks_changed broadcast each).
  async bulkUpdateTasks(
    workspaceId: string,
    taskIds: string[],
    updates: {
      status?: string;
      priority?: TaskPriority | string | null;
      assigneeId?: string | null;
      dueAt?: number | null;
      archivedAt?: number | null;
    },
  ): Promise<Task[]> {
    const resp = await this.request<{ tasks: RawTask[] }>("cyborg:bulk_update_tasks", {
      workspaceId,
      taskIds,
      updates,
    });
    return resp.tasks.map(mapRawTask);
  }

  async deleteTask(workspaceId: string, taskId: string): Promise<void> {
    await this.request("cyborg:delete_task", { workspaceId, taskId });
  }

  // Drag-reorder a task within its (workspace) lane. The caller names the
  // neighbours the task was dropped BETWEEN — `beforeId` is its new upper
  // neighbour (the task it now sits AFTER) and `afterId` its lower neighbour
  // (the task it now sits BEFORE); either, both, or neither (lane edges) may be
  // set. The relay computes a fractional sort_order between them
  // (computeReorderSort / task-ordering.ts) and broadcasts cyborg:tasks_changed.
  // Mirrors the create/update task RPC shape exactly (request → response payload
  // { task } → mapRawTask). No client path bypasses workspace/visibility: the
  // relay re-asserts the caller's role + project visibility before mutating.
  async reorderTask(
    workspaceId: string,
    taskId: string,
    anchors: { beforeId?: string; afterId?: string },
  ): Promise<Task> {
    const resp = await this.request<{ task: RawTask }>("cyborg:reorder_task", {
      workspaceId,
      taskId,
      beforeId: anchors.beforeId,
      afterId: anchors.afterId,
    });
    return mapRawTask(resp.task);
  }

  // A single BOUNDED page of the task board (default 100 rows). Returns
  // `nextCursor` when more rows remain — the load-more seam for a scroll/paged
  // board. The relay (cyborg:fetch_tasks) has always accepted limit+cursor and
  // returned nextCursor; this exposes it client-side so the first request is
  // bounded instead of the server serializing every workspace task into one fat
  // payload. Omitting `limit` still works server-side (unbounded), but this
  // client always sends a default so the bounded path is the default.
  async fetchTasksPage(
    workspaceId: string,
    opts?: {
      status?: string;
      assigneeId?: string;
      limit?: number;
      cursor?: string;
      // Trim the body: the relay drops `description`/`result` (its inner.titlesOnly)
      // so the board ships ~362KB instead of ~5MB. Spread onto the wire body below,
      // so it reaches the relay unchanged; the detail refills the body per-task.
      titlesOnly?: boolean;
      // CYBORG-1176: only rows updated at/after this epoch ms (the board's delta).
      updatedSince?: number;
      // CYBORG-1198: taskIdsDigest of the ids the caller holds (delta only).
      idsDigest?: string;
    },
  ): Promise<TaskPage> {
    const resp = await this.request<{
      tasks: RawTask[];
      nextCursor?: string | null;
      asOf?: number;
      ids?: string[];
      idsUnchanged?: boolean;
    }>("cyborg:fetch_tasks", { workspaceId, limit: 100, ...opts });
    return {
      tasks: resp.tasks.map(mapRawTask),
      nextCursor: resp.nextCursor ?? null,
      asOf: typeof resp.asOf === "number" ? resp.asOf : null,
      ids: Array.isArray(resp.ids) ? resp.ids : null,
      idsUnchanged: resp.idsUnchanged === true,
    };
  }

  // CYBORG-1206: the board drains in SMALL pages. One 2,000-row page was ~1 MB on the
  // Jex board: on Slow 4G it took 17.4s, blew the 15s response budget, was retried whole
  // (another 16s), and every answer queued behind it on the one socket waited with it
  // (agenda 18.4s, threads 17.7s). The first page is small so the board paints fast;
  // the rest follow one bounded page at a time, so other answers interleave between
  // them and a page that times out re-asks only itself.
  static readonly BOARD_FIRST_PAGE_LIMIT = 100;
  static readonly BOARD_PAGE_LIMIT = 250;
  // CYBORG-1176: a delta is one call; past this many changed rows it reports overflow
  // (null) and the caller reloads the board in pages.
  static readonly BOARD_DELTA_LIMIT = 2000;

  // How long a drained page waits for the connection to come back before it gives
  // up and fails the load. The reconnect backoff caps at 30s (reconnectBackoffMs),
  // so this covers a first attempt and then some; past it the board really is
  // offline and the caller should hear about it.
  private static readonly DRAIN_RECONNECT_WAIT_MS = 30_000;

  // ONE page of the board drain, retried ONCE across a reconnect (CYBORG-1171).
  //
  // The drain is sequential and as long as the board is big — 1,196 visible tasks is
  // 12 round trips at 100 rows a page — so on a large workspace the odds that some
  // page meets a drop are 12x those of a single request, and aborting the whole load
  // for it threw away the 11 pages that DID arrive and painted "Couldn't load work
  // items" over a board the relay had answered in ~1.5s. A retry keeps every page
  // already drained (the caller's dedupe map and cursor are untouched) and re-asks
  // only the page that died, on the connection that came back.
  //
  // Only a TRANSPORT failure is retried (isTransportFailure): a relay `cyborg:error`
  // is a real answer and re-asking it just gets refused twice.
  //
  // ONE retry for the WHOLE drain, not one per page — `budget` is shared by every
  // page of a single fetchTasks(). Per page it would be 12 independent budgets on
  // the Jex board: 12 x (15s response + 10s probe + 30s wait) is ~11 minutes of a
  // "load" that used to fail at the first drop, behind a pull-to-refresh on mobile.
  // A link that drops twice inside one drain is not a blip, and the board saying so
  // is the honest outcome.
  private async fetchTasksPageAcrossReconnect(
    workspaceId: string,
    opts: {
      status?: string;
      assigneeId?: string;
      limit?: number;
      cursor?: string;
      titlesOnly?: boolean;
      updatedSince?: number;
      idsDigest?: string;
    },
    budget: { retriesLeft: number },
  ): Promise<TaskPage> {
    // The identity this page belongs to. connect() bumps it on every logout /
    // re-login / endpoint switch, and sendRequest's own checks read it at the
    // RETRY's start — after the bump — so they cannot see a session that changed
    // during the wait. Without this, logging out and back in inside the 30s would
    // put the old session's read on the new session's socket.
    const sessionRevision = this.sessionRevision;
    try {
      return await this.fetchTasksPage(workspaceId, opts);
    } catch (err) {
      if (
        budget.retriesLeft <= 0 ||
        !isTransportFailure(err) ||
        err.transport === "session_changed"
      )
        throw err;
      budget.retriesLeft -= 1;
      await this.waitForUsableConnection(SlackClient.DRAIN_RECONNECT_WAIT_MS);
      // Giving up on the retry, so the drop we were retrying from is the cause —
      // without it the caller sees "Client session changed" with no trace of the
      // transport failure that started the wait.
      if (sessionRevision !== this.sessionRevision)
        throw new TransportError("session_changed", "Client session changed", { cause: err });
      return await this.fetchTasksPage(workspaceId, opts);
    }
  }

  // Full task board in the Task[] shape every caller expects (backward-compat),
  // but fetched as bounded pages that follow `nextCursor` until drained — so no
  // single request serializes the whole workspace. Callers that want true
  // lazy-on-scroll load-more should use `fetchTasksPage` directly and drive the
  // cursor themselves.
  async fetchTasks(
    workspaceId: string,
    opts?: { status?: string; assigneeId?: string },
  ): Promise<Task[]> {
    return (await this.fetchTaskBoard(workspaceId, opts)).tasks;
  }

  // The whole board plus `asOf`, the relay time the first page's read started: the
  // base for the next fetchTaskBoardChanges. null from a relay that predates it.
  // `onPage` hears the board so far after each page (CYBORG-1206), so the first rows
  // render while the rest are still arriving.
  async fetchTaskBoard(
    workspaceId: string,
    opts?: { status?: string; assigneeId?: string },
    onPage?: (tasksSoFar: Task[]) => void,
  ): Promise<{ tasks: Task[]; asOf: number | null }> {
    // Dedupe by id while draining. A duplicate id in the board's keyed
    // `{#each group.tasks (task.id)}` is a FATAL Svelte `each_key_duplicate`
    // that crashes the whole Tasks tab — so this must NEVER return two rows with
    // the same id. Keyset pages can repeat a boundary row (or a concurrent create
    // can land between page requests), so a Map (last page wins) is the guard.
    const byId = new Map<string, Task>();
    // Also guard against a cursor that fails to advance: if the server ever hands
    // back a cursor we've already requested, the drain would loop forever and the
    // tab would never finish loading. Stop the moment a cursor repeats.
    const seenCursors = new Set<string>();
    // Shared by every page below: the drain gets ONE retry across a reconnect, not
    // one per page. See fetchTasksPageAcrossReconnect.
    const retryBudget = { retriesLeft: 1 };
    let cursor: string | undefined;
    let asOf: number | null | undefined;
    do {
      // titlesOnly: the board list + filters never read the body, so drain trimmed
      // rows (~14x smaller). The detail view lazy-loads the full body on open via
      // fetchTask. Relay defaults titlesOnly off → other callers are unaffected.
      const limit = cursor ? SlackClient.BOARD_PAGE_LIMIT : SlackClient.BOARD_FIRST_PAGE_LIMIT;
      const page = await this.fetchTasksPageAcrossReconnect(
        workspaceId,
        { ...opts, cursor, titlesOnly: true, limit },
        retryBudget,
      );
      asOf ??= page.asOf;
      for (const t of page.tasks) byId.set(t.id, t);
      const next = page.nextCursor ?? undefined;
      if (next && onPage) onPage([...byId.values()]);
      if (next && seenCursors.has(next)) break;
      if (next) seenCursors.add(next);
      cursor = next;
    } while (cursor);
    return { tasks: [...byId.values()], asOf: asOf ?? null };
  }

  // CYBORG-1176: what changed on the board since `since` (epoch ms, relay clock), in
  // ONE call: the changed rows plus every id still visible. null when the relay
  // cannot answer a delta (older relay) or the change set overflows one call; the
  // caller then reloads the whole board.
  // CYBORG-1198: `heldIds` (the ids the board holds) go as a digest; a relay whose
  // visible set matches answers `idsUnchanged` and the held ids stand in for `ids`.
  async fetchTaskBoardChanges(
    workspaceId: string,
    since: number,
    heldIds?: readonly string[],
  ): Promise<{ tasks: Task[]; ids: string[]; asOf: number } | null> {
    const page = await this.fetchTasksPageAcrossReconnect(
      workspaceId,
      {
        titlesOnly: true,
        updatedSince: since,
        limit: SlackClient.BOARD_DELTA_LIMIT,
        ...(heldIds ? { idsDigest: taskIdsDigest(heldIds) } : {}),
      },
      { retriesLeft: 1 },
    );
    const ids = page.ids ?? (page.idsUnchanged && heldIds ? [...heldIds] : null);
    if (ids === null || page.asOf === null || page.nextCursor !== null) return null;
    return { tasks: page.tasks, ids, asOf: page.asOf };
  }

  // A single task WITH its full body (description + result). The board drains
  // `fetchTasks` with `titlesOnly`, so its shared rows are body-trimmed; the detail
  // view refills the body on open via this — the relay's existing `taskIds` filter
  // returns the untrimmed row for the given ids (no titlesOnly here). Returns null
  // when the id isn't found (deleted / not visible).
  async fetchTask(workspaceId: string, taskId: string): Promise<Task | null> {
    const resp = await this.request<{ tasks: RawTask[] }>("cyborg:fetch_tasks", {
      workspaceId,
      taskIds: [taskId],
    });
    const raw = resp.tasks?.[0];
    return raw ? mapRawTask(raw) : null;
  }

  // Workspace-wide task search (title OR description, ILIKE, enriched rows). Short
  // (<2 char) or empty queries short-circuit to []. Mirrors searchMessages.
  async searchTasks(workspaceId: string, query: string, limit = 50): Promise<TaskSearchResult[]> {
    const q = query.trim();
    if (q.length < 2) return [];
    const resp = await this.request<{ results: TaskSearchResult[] }>("cyborg:search_tasks", {
      workspaceId,
      query: q,
      limit,
    });
    return resp.results;
  }

  // Workspace-wide, paginated page search (title ILIKE, metadata-only rows unless
  // `mode: "detail"`) — the `/pages` slash command picker (CYBORG-309). An
  // empty/short (<2 char) query is NOT short-circuited here: the server
  // returns the workspace's most-recently-updated (visibility-gated) pages
  // for it instead of [], so the picker can show an instant candidate list
  // the moment "/pages" is typed — the same @-mention parity the composer's
  // live-search effect relies on. Follow `nextCursor` for more results
  // (mirrors fetchTasksPage's cursor contract).
  async searchPages(
    workspaceId: string,
    query: string,
    opts: PageSearchOptions,
  ): Promise<{ pages: PageSearchResult[]; nextCursor: string | null }> {
    const { cursor, limit = 20, mode } = opts;
    const q = query.trim();
    const resp = await this.request<{ pages: PageSearchResult[]; nextCursor?: string | null }>(
      "cyborg:search_pages",
      {
        workspaceId,
        query: q,
        limit,
        ...(cursor ? { cursor } : {}),
        ...(mode === "detail" ? { detail: true } : {}),
      },
    );
    return { pages: resp.pages, nextCursor: resp.nextCursor ?? null };
  }

  // ─── Tasks Redesign read-only catalog (board/detail) ─────────────
  // PENDING-SERVER: the relay handlers for these five RPCs land in a later phase.
  // They model the fetchTasks pattern (request type → await this.request → map the
  // response array). `fetchProjects` (the tasks-projects list) already exists on the
  // Cyborg subclass (ws-client.ts), so it is intentionally NOT duplicated here.

  // A project's workflow states (board columns), ordered by `sequence` server-side.
  async fetchProjectStates(projectId: string): Promise<TaskState[]> {
    const resp = await this.request<{ states: TaskState[] }>("cyborg:fetch_project_states", {
      projectId,
    });
    return resp.states;
  }

  // A project's label catalog (tags), ordered by `sortOrder` server-side.
  async fetchProjectLabels(projectId: string): Promise<TaskLabel[]> {
    const resp = await this.request<{ labels: TaskLabel[] }>("cyborg:fetch_project_labels", {
      projectId,
    });
    return resp.labels;
  }

  // A project's cycles (sprints).
  async fetchCycles(projectId: string): Promise<Cycle[]> {
    const resp = await this.request<{ cycles: Cycle[] }>("cyborg:fetch_cycles", { projectId });
    return resp.cycles;
  }

  // A project's modules (feature groupings).
  async fetchModules(projectId: string): Promise<Module[]> {
    const resp = await this.request<{ modules: Module[] }>("cyborg:fetch_modules", { projectId });
    return resp.modules;
  }

  // ─── Saved views catalog CRUD ────────────────────────────────────
  // A project's saved views (Plane's project views), newest-updated first
  // (server-ordered). `filters` + `display` are OPAQUE JSON strings — the views
  // route serializes / parses its own TaskFilters + display record around these.
  // The write methods (create/update/delete) live on CyborgClient (ws-client.ts),
  // matching the cycles/modules split (reads here, writes there).

  async fetchTaskViews(projectId: string): Promise<TaskView[]> {
    const resp = await this.request<{ views: TaskView[] }>("cyborg:fetch_task_views", {
      projectId,
    });
    return resp.views;
  }

  // ─── Project pages catalog CRUD ──────────────────────────────────
  // A project's pages (wiki/docs). Returns ALL pages (the UI filters archived
  // itself), non-archived first then newest-updated first (server-ordered).
  async fetchPages(projectId: string): Promise<Page[]> {
    const resp = await this.request<{ pages: Page[] }>("cyborg:fetch_pages", { projectId });
    return resp.pages;
  }

  // A single page by id, or null when missing / not visible.
  async fetchPage(pageId: string): Promise<Page | null> {
    const resp = await this.request<{ page: Page | null }>("cyborg:fetch_page", { pageId });
    return resp.page;
  }

  // Create a blank page in a project. ownedBy is set server-side (creator).
  // `opts.title` seeds the title; `opts.parentId` nests the new page directly
  // under that parent (same project) — null/absent = a root page. The server
  // persists parent_id atomically (no follow-up reparent). `opts.visibility`
  // sets Public/Private at creation (absent = server default "private").
  async createPage(
    projectId: string,
    opts: {
      title?: string;
      parentId?: string | null;
      visibility?: "private" | "public";
      mentions?: string[];
    } = {},
  ): Promise<Page> {
    const resp = await this.request<{ page: Page }>("cyborg:create_page", {
      projectId,
      title: opts.title,
      parentId: opts.parentId,
      visibility: opts.visibility,
      mentions: opts.mentions,
    });
    return resp.page;
  }

  // Update a page's editable fields. Only present keys are written. `parentId`
  // re-parents the page in the hierarchy (null = move to root); `sortOrder`
  // repositions it among its siblings. `mentions` is the FULL set of mentioned
  // userIds extracted from editor.getJSON() on a content save (CYBORG-303/304)
  // — the server diffs it against the persisted previous value to notify
  // newly-added mentions.
  async updatePage(
    pageId: string,
    patch: {
      title?: string;
      content?: string;
      visibility?: "private" | "public";
      icon?: string | null;
      parentId?: string | null;
      sortOrder?: number;
      mentions?: string[];
      // The page's updatedAt this edit was made against; a stale title/content save fails
      // with code "page_stale" instead of overwriting someone else's edit.
      baseUpdatedAt?: number;
    },
  ): Promise<Page> {
    const resp = await this.request<{ page: Page }>("cyborg:update_page", { pageId, ...patch });
    return resp.page;
  }

  // ─── Agents in pages (knowledge/135) ─────────────────────────────
  // Ask an agent to write into its own block at a top-level index of the SAVED doc.
  // The caller flushes its pending save first; the returned page carries the queued block.
  async pageInvokeCybo(args: {
    workspaceId: string;
    pageId: string;
    cyboId: string;
    instruction: string;
    anchor: { index: number };
    selection?: { text: string };
  }): Promise<{ runId: string; page: Page }> {
    return this.request<{ runId: string; page: Page }>("cyborg:page_invoke_cybo", args);
  }

  // `page` is not in the stop response schema today; adopted when a relay sends it.
  async pageAgentStop(
    workspaceId: string,
    runId: string,
  ): Promise<{ run: PageAgentRun; page?: Page }> {
    return this.request<{ run: PageAgentRun; page?: Page }>("cyborg:page_agent_stop", {
      workspaceId,
      runId,
    });
  }

  async pageAgentResolve(
    workspaceId: string,
    runId: string,
    action: "keep" | "discard" | "retry",
  ): Promise<{ run: PageAgentRun; page: Page; newRunId?: string }> {
    return this.request<{ run: PageAgentRun; page: Page; newRunId?: string }>(
      "cyborg:page_agent_resolve",
      { workspaceId, runId, action },
    );
  }

  // Toggle a page's soft-archive. Reversible — pass archived=false to restore.
  async setPageArchived(pageId: string, archived: boolean): Promise<Page> {
    const resp = await this.request<{ page: Page }>("cyborg:set_page_archived", {
      pageId,
      archived,
    });
    return resp.page;
  }

  // Hard-delete a page.
  async deletePage(pageId: string): Promise<void> {
    await this.request("cyborg:delete_page", { pageId });
  }

  // A single task's activity feed (history), ordered by `epoch` server-side.
  async fetchTaskActivity(taskId: string): Promise<TaskActivity[]> {
    const resp = await this.request<{ activity: TaskActivity[] }>("cyborg:fetch_task_activity", {
      taskId,
    });
    return resp.activity;
  }

  // Post a persistent comment on a task. `text` is the raw markdown body; the
  // server escapes it, resolves the actor + workspace from the session (never the
  // client), stores it as a verb='commented' activity row, and broadcasts
  // task_activity_changed so other open viewers refresh. Returns the created row
  // (with the real actorId + epoch) so the caller can splice it into the feed.
  /**
   * Post a comment on a task. `replyToId` threads it under an existing comment on
   * the SAME task — the server rejects an id from another task's feed.
   */
  async createTaskComment(
    taskId: string,
    text: string,
    replyToId?: string | null,
  ): Promise<TaskActivity> {
    const resp = await this.request<{ activity: TaskActivity }>("cyborg:create_task_comment", {
      taskId,
      text,
      replyToId: replyToId ?? null,
    });
    return resp.activity;
  }

  /** Edit your own task comment. Returns the server's edit time for the "edited" marker. */
  async updateTaskComment(activityId: string, text: string): Promise<number> {
    const resp = await this.request<{ editedAt: number }>("cyborg:update_task_comment", {
      activityId,
      text,
    });
    return resp.editedAt;
  }

  /** Delete a task comment (yours, or any as a workspace owner/admin). */
  async deleteTaskComment(activityId: string): Promise<void> {
    await this.request<{ deleted: boolean }>("cyborg:delete_task_comment", { activityId });
  }

  // ─── Extension point for subclasses ─────────────────────────────

  protected handleExtensionMessage(
    _type: string,
    _payload: Record<string, unknown> | undefined,
  ): boolean {
    return false;
  }

  // ─── Internal ───────────────────────────────────────────────────

  private sendRaw(data: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new TransportError("not_connected", "Not connected");
    }
    this.ws.send(JSON.stringify(data));
  }

  protected send(data: Record<string, unknown>): void {
    // A fire-and-forget frame (active_view, draft_set, watch_agent…) the floor would refuse.
    if (this.floorRefuses(data.type, data.workspaceId)) return;
    // The relay reads the INNER frame's traceparent (user-intent types only).
    // Every prompt frame gets its one prompt id here (prompt-id.ts): no send path can skip it.
    this.sendRaw({ type: "session", message: withTraceparent(withPromptId(data)) });
  }

  // Resolve once the socket is OPEN, or reject after `timeoutMs`. Used to gate
  // RPCs so a fetch issued during a reconnect blip (or a navigation that races
  // the socket) waits for the connection instead of throwing "Not connected"
  // synchronously and leaving the view permanently empty until a manual reload.
  private waitForOpen(timeoutMs: number): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) return Promise.resolve();
    // Event-driven (no polling): the open handler emits "connection" once the
    // socket is ready. The early return above closes the race where it's already
    // OPEN — there's no await between that check and attaching the listener, so
    // the event can't slip through in between.
    return new Promise((resolve, reject) => {
      let unsubscribe: (() => void) | undefined;
      const timer = setTimeout(() => {
        unsubscribe?.();
        reject(new TransportError("not_connected", "Not connected"));
      }, timeoutMs);
      unsubscribe = this.on("connection", (data) => {
        if (data.status === "connected") {
          clearTimeout(timer);
          unsubscribe?.();
          resolve();
        }
      });
    });
  }

  // Resolve once the socket is AUTHENTICATED (cyborg:auth has succeeded on the
  // current connection), or reject after `timeoutMs`. Gates non-auth RPCs so a
  // fetch issued in the window between socket-open and auth-complete (the
  // post-reconnect race) waits for auth instead of being silently dropped by
  // the relay and hanging until it times out — which left the view empty until
  // a manual reload.
  private waitForAuth(timeoutMs: number): Promise<void> {
    if (this.authenticated) return Promise.resolve();
    return new Promise((resolve, reject) => {
      let waiter: { resolve: () => void; reject: (err: Error) => void } | undefined;
      const timer = setTimeout(() => {
        if (waiter) this.authWaiters.delete(waiter);
        reject(new TransportError("not_authenticated", "Not authenticated"));
      }, timeoutMs);
      waiter = {
        resolve: () => {
          clearTimeout(timer);
          resolve();
        },
        reject: (err: Error) => {
          clearTimeout(timer);
          reject(err);
        },
      };
      this.authWaiters.add(waiter);
    });
  }

  // Resolve once the connection is usable again — OPEN *and* authenticated — or
  // reject after `timeoutMs`. The retry seam for a read that died with its socket.
  //
  // Unlike waitForOpen this does NOT trust readyState on its own: a zombie socket
  // reads OPEN forever and answers nothing (CYBORG-1171 — 73 of 268 reconnects in
  // 24h delivered no `close` event at all), so waitForOpen would return instantly
  // and the retry would burn exactly the same timeout the first attempt did. Probe
  // it first with the heartbeat's own liveness standard; a dead probe tears the
  // socket down, which runs the `close` handler, and the wait below then rides its
  // reconnect. A live probe costs one ping and falls straight through.
  private async waitForUsableConnection(timeoutMs: number): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    // Capture the socket this probe is judging, exactly as heartbeatTick does. The
    // verdict arrives a microtask or ten seconds later, and by then it can belong
    // to a socket that is no longer ours — acting on it then is not a no-op:
    //   * a `4003` close fails this ping synchronously while handleAuthRejection's
    //     refresh is already in flight. An unguarded forceReconnect() would reset
    //     the backoff to ~0 and reconnect on the REJECTED token, race the refresh,
    //     collect a second 4003 and turn a recoverable expiry into a forced
    //     re-login (auth_expired → /login).
    //   * connect() (re-login / endpoint switch) nulls this.ws before the old
    //     socket's close, so that close early-returns on the stale-socket guard
    //     and never fails this ping. It times out on its own 10s timer, by which
    //     point the current socket is a NEW, healthy one — and closing that is a
    //     drop we caused ourselves.
    const probedWs = this.ws;
    if (probedWs && this.connected && !(await this.ping(SlackClient.HEARTBEAT_TIMEOUT_MS))) {
      // Only the socket we actually probed, and only if it is still the live one.
      // forceReconnect() additionally no-ops during an intentional teardown.
      if (this.ws === probedWs) this.forceReconnect();
    }
    await this.waitForOpen(Math.max(0, deadline - Date.now()));
    await this.waitForAuth(Math.max(0, deadline - Date.now()));
  }

  // `timeoutMs` overrides the default per-request RESPONSE timeout for RPCs whose
  // server-side work legitimately exceeds it. The prime case is
  // cyborg:restore_session / cyborg:import_session: the daemon SPAWNS the provider
  // (Claude SDK process boot) and replays the full transcript
  // (hydrateTimelineFromProvider) BEFORE emitting restore_session_response, which
  // for a real, day-old Claude session routinely takes longer than the 15s
  // default — so a slow-but-successful resume tripped the default timeout and
  // surfaced "Request cyborg:restore_session timed out" while the daemon was still
  // (successfully) resuming. The socket-readiness gates keep the default (a stalled
  // connection should fail fast) — ONE requestTimeoutMs shared across both of them,
  // see sendRequest's readyDeadline; only the response wait is extended.
  protected request<T>(
    type: string,
    params?: Record<string, unknown>,
    timeoutMs?: number,
  ): Promise<T> {
    // In-flight read coalescing: concurrent IDENTICAL idempotent reads share ONE
    // wire request and one result, so the default channel's messages fetched
    // twice on workspace entry (bootstrap + route selectChannel) and a reconnect
    // that re-fires fetch_unread/fetch_channels/etc. collapse to a single request
    // each. Gated on COALESCEABLE_READ_TYPES so a mutation can NEVER be coalesced;
    // everything else (all mutations, ping, subscribe) goes straight through. The
    // map get/set runs synchronously here, BEFORE sendRequest()'s first await, so a
    // second caller in the same tick sees the entry and shares the promise. A read
    // that arrives AFTER the first settles fires fresh (the entry is deleted in
    // finally, on both resolve and reject).
    if (this.floorRefuses(type, params?.workspaceId)) {
      // The relay's workspace_scope floor would refuse it (rpc_denied): answer that refusal here.
      return Promise.reject(
        buildRpcError({ message: "not a member of this workspace", code: "not_a_member" }),
      );
    }
    if (ETAG_READ_TYPES.has(type)) return this.requestWithEtag<T>(type, params, timeoutMs);
    return this.coalescedRequest<T>(type, params, timeoutMs);
  }

  private isOutsiderSeat: (workspaceId: string) => boolean = () => false;

  private floorRefuses(type: unknown, workspaceId: unknown): boolean {
    return (
      typeof type === "string" &&
      typeof workspaceId === "string" &&
      !OUTSIDER_REACHABLE_RPCS.has(type) &&
      this.isOutsiderSeat(workspaceId)
    );
  }

  /** Which workspaces this account reaches only as a Connect external (from the workspace list). */
  setOutsiderScope(isOutsiderSeat: (workspaceId: string) => boolean): void {
    this.isOutsiderSeat = isOutsiderSeat;
  }

  // CYBORG-1210: a list read we already hold asks `ifNoneMatch`; `notModified` means the
  // held copy is byte-identical to what the relay would send. An old relay never answers it.
  private async requestWithEtag<T>(
    type: string,
    params: Record<string, unknown> | undefined,
    timeoutMs: number | undefined,
  ): Promise<T> {
    const key = `${type}:${stableStringify(params)}`;
    const held = this.etagHeld.get(key);
    const res = await this.coalescedRequest<Record<string, unknown>>(
      type,
      held ? { ...params, ifNoneMatch: held.etag } : params,
      timeoutMs,
    );
    if (res.notModified === true) {
      // cast: the held body is this same read's earlier answer, byte-identical per the etag
      if (held && res.etag === held.etag) return JSON.parse(held.body) as T;
      this.etagHeld.delete(key);
      return this.coalescedRequest<T>(type, params, timeoutMs);
    }
    if (typeof res.etag === "string") {
      const { requestId: _requestId, etag, ...body } = res;
      this.etagHeld.delete(key);
      if (this.etagHeld.size >= MAX_ETAG_HELD) {
        const oldest = this.etagHeld.keys().next();
        if (!oldest.done) this.etagHeld.delete(oldest.value);
      }
      this.etagHeld.set(key, { etag, body: JSON.stringify(body) });
    }
    return res as T; // cast: the same answer request<T> hands back unchecked
  }

  private coalescedRequest<T>(
    type: string,
    params: Record<string, unknown> | undefined,
    timeoutMs: number | undefined,
  ): Promise<T> {
    if (!COALESCEABLE_READ_TYPES.has(type)) {
      return this.sendRequest<T>(type, params, timeoutMs);
    }
    const key = `${type}:${stableStringify(params)}`;
    const existing = this.inflightReads.get(key) as Promise<T> | undefined;
    if (existing) return existing;
    const inflight = this.sendRequest<T>(type, params, timeoutMs).finally(() => {
      // Evict OUR entry only. A socket replacement clears the whole map
      // (CYBORG-991), so by the time a read sent on the OLD socket settles — it
      // can only time out, nothing can answer it now — the key may already hold
      // the NEW socket's live promise, and a blind delete would drop that one
      // instead, silently un-coalescing the callers still sharing it.
      if (this.inflightReads.get(key) === inflight) this.inflightReads.delete(key);
    });
    this.inflightReads.set(key, inflight);
    return inflight;
  }

  private async sendRequest<T>(
    type: string,
    params?: Record<string, unknown>,
    timeoutMs?: number,
  ): Promise<T> {
    const responseTimeoutMs = timeoutMs ?? this.requestTimeoutMs;
    // ONE readiness budget shared by BOTH gates, not one each. They are
    // sequential, so passing the full requestTimeoutMs to each made a single
    // RPC cost up to 15s (open) + 15s (auth) + 15s (response) = 45s — three
    // times what request()'s own doc comment promises ("the socket-readiness
    // gates keep the default (a stalled connection should fail fast)"). Nothing
    // failed fast: selectDm's 3-attempt retry loop (app.svelte.ts) multiplied
    // that to 45s x 3 + 600ms + 1200ms = 2m17s of spinner before it gave up and
    // latched the DM pane empty — the "my messages take minutes to render"
    // report. Socket-open and socket-auth are one condition from a caller's
    // point of view (is this connection usable), so they get one deadline;
    // the RESPONSE budget stays separate and untouched, since real server work
    // is a different question from a stalled connection.
    const readyDeadline = Date.now() + this.requestTimeoutMs;
    const sessionRevision = this.sessionRevision;
    try {
      await this.waitForOpen(this.requestTimeoutMs);
      if (sessionRevision !== this.sessionRevision) throw sessionChanged();
      // Every RPC except the auth handshake itself must wait until the socket is
      // authenticated — otherwise the relay drops it on the floor (see authWaiters).
      if (type !== "cyborg:auth") {
        await this.waitForAuth(Math.max(0, readyDeadline - Date.now()));
      }
      if (sessionRevision !== this.sessionRevision) throw sessionChanged();
    } catch (error) {
      throw type === "cyborg:send_agent_prompt" ? new PromptNotSentError(error) : error;
    }
    const sentOnGen = this.socketGen;
    const traceparent = isTracedFrame(type) ? newTraceparent() : undefined;
    const traceId = traceIdOf(traceparent);
    return new Promise((resolve, reject) => {
      const requestId = `req_${++this.requestCounter}_${Date.now()}`;
      const timer = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        // A response budget that lapses inside a planned restart is the restart.
        const timedOut = new TransportError("request_timed_out", `Request ${type} timed out`);
        const planned = this.plannedRestart;
        if (planned && planned.socketGen === sentOnGen && Date.now() < planned.until) {
          Object.assign(timedOut, { code: CONNECTION_LOST_CODE });
        }
        reject(withTraceId(timedOut, traceId));
      }, responseTimeoutMs);

      // An error reply to a send is a refusal BEFORE dispatch: the relay's branches
      // all break before forwarding, and the owning daemon emits cyborg:error only
      // ahead of its "routed" ack. So it is retryable, unlike the timeout above —
      // which rejects through the promise directly and stays unknown on purpose.
      this.pendingRequests.set(requestId, {
        resolve: resolve as (data: unknown) => void,
        reject:
          type === "cyborg:send_agent_prompt"
            ? (error: unknown) => reject(markPromptNotSent(error))
            : reject,
        // No "not sent" verdict on this one — see PendingRequest.rejectRaw. Only a
        // dropped socket uses it.
        rejectRaw: reject,
        timer,
        traceId,
      });

      try {
        this.send({ type, requestId, ...params, ...(traceparent ? { traceparent } : {}) });
      } catch (error) {
        clearTimeout(timer);
        this.pendingRequests.delete(requestId);
        reject(type === "cyborg:send_agent_prompt" ? new PromptNotSentError(error) : error);
      }
    });
  }

  private doConnect(): Promise<void> {
    return new Promise((resolve, reject) => {
      // A NEW socket inherits none of the old one's in-flight reads (CYBORG-991).
      // The close handler already clears them, but connect() (endpoint switch /
      // re-login) nulls `this.ws` synchronously before landing here, so the old
      // socket's close early-returns on the stale-socket guard and never runs —
      // this is the seam EVERY socket creation passes through, whatever replaced
      // the previous one (drop + reconnect, 4003 silent re-auth, connect()).
      this.inflightReads.clear();
      this.socketGen += 1;
      const ws = new WebSocket(this.url);
      this.ws = ws;

      // Settle the connect promise exactly once. A stalled handshake fires
      // neither `open` nor `error`, so without this watchdog doConnect() never
      // resolves OR rejects and every awaiter hangs forever. On timeout we close
      // the half-open socket (its `close` handler runs scheduleReconnect, exactly
      // like a failed connect) and reject so connectToServer surfaces a
      // retryable NetworkError instead of a permanent spinner.
      let settled = false;
      const connectTimer = setTimeout(() => {
        if (settled) return;
        settled = true;
        try {
          ws.close();
        } catch {
          // already closing/closed — the close handler covers reconnect.
        }
        reject(new Error("WebSocket connection timed out"));
      }, this.connectTimeoutMs);

      ws.addEventListener("open", () => {
        // Stale-socket guard: events are async, so a discarded socket (one a newer
        // connect() already replaced) can fire `open` late. Acting here would
        // clobber the live socket's hello/auth/heartbeat. Only the current socket
        // proceeds; a normal connect leaves this.ws === ws.
        if (this.ws && this.ws !== ws) return;
        if (settled) return; // a timeout already rejected — ignore a late open
        settled = true;
        clearTimeout(connectTimer);
        // MEASUREMENT ONLY, and it MUST be read here: the very next statement
        // resets the counter, so a report built later would always say 0 retries.
        const attemptsBeforeReset = this.reconnectAttempts;
        this.reconnectAttempts = 0;
        // Fresh socket: nothing is authenticated yet. Block gated RPCs until
        // cyborg:auth succeeds again on THIS connection.
        this.authenticated = false;
        this.sendRaw({
          type: "hello",
          clientId: `cyborg-ui-${Date.now()}`,
          clientType: "browser",
          protocolVersion: 1,
          // knowledge/85 §C.5 Phase 3 (THE FLIP): advertise that this client wants its
          // terminal frames narrowed per-attachId. When the relay AND the source daemon
          // are also per-attach-capable, the relay delivers a terminal_output/exit/
          // snapshot frame ONLY to the socket whose (terminalId, attachId) matches —
          // so opening the SAME terminal in two tabs no longer doubles the output on
          // this socket (the client filters by terminalId, not attachId, so the relay
          // narrowing is what prevents the same-terminal double once the daemon drops
          // its #807 per-user collapse). Additive + optional: an old relay ignores it
          // and falls back to per-user fan-out. Value MUST equal
          // CLIENT_CAPS.terminalPerAttachRouting ("terminal_per_attach_routing") in
          // @getpaseo/protocol — kept as a local literal because the UI is shell-
          // agnostic and does not depend on the server protocol package.
          // `timelineCoverage` (knowledge/125): this client reads mirror pages' `verified`,
          // `coverage` and `reset`, so the relay may serve it a page that is not whole.
          // `agentNotDelivered`: this client renders agent_not_run's `not_delivered` reason.
          capabilities: { terminal_per_attach_routing: true, timelineCoverage: true, agentNotDelivered: true }, // prettier-ignore
          // Desktop: the daemon this app bundles, so the relay can reach THIS machine's app.
          ...(this.localDaemonId ? { localDaemonId: this.localDaemonId } : {}),
        });
        this.emit("connection", { status: "connected" } as EventMap["connection"]);
        // Start the transport-level liveness heartbeat for THIS connection (#503).
        // Tied to the socket lifecycle (not foreground-gated), so a
        // backgrounded-but-open tab still detects a half-open socket.
        this.startHeartbeat();
        resolve();
        // Re-authenticate the socket immediately so parked RPCs unblock even if
        // the app layer is slow to call authenticate() after a reconnect. The
        // relay's re-auth is idempotent, so a redundant call from the app is
        // harmless. (cyborg:auth bypasses the auth gate in request().)
        // intentional: opportunistic socket re-auth; a real auth failure surfaces via the app's own authenticate() + connection events.
        if (this.token) this.authenticate().catch(() => {});
        // MEASUREMENT ONLY, placed after everything the reconnect needs. Builds the
        // report for the outage that just ended (null on the first-ever connect)
        // from the tracker BEFORE the reset, and schedules its send; the seed tick
        // gives this new connection a baseline for its first 30s interval.
        if (this.lastCloseAt !== null) {
          const downMs = Date.now() - this.lastCloseAt;
          const asleepMs = this.suspension.asleepMs(Date.now(), monotonicNow());
          this.scheduleReconnectReport(downMs, asleepMs, attemptsBeforeReset);
          this.clearReconnectTelemetry();
          this.driftTracker.reset();
        }
        this.driftTracker.tick(Date.now());
      });

      ws.addEventListener("error", () => {
        if (this.ws && this.ws !== ws) return; // stale socket — not the live connection
        if (settled) return;
        settled = true;
        clearTimeout(connectTimer);
        reject(new Error("WebSocket connection failed"));
      });

      ws.addEventListener("close", (event) => {
        // Stale-socket guard: a discarded socket's `close` can arrive AFTER a
        // newer connect() set this.ws to a different live socket. Without this it
        // would null out the live socket and schedule a spurious reconnect. A
        // genuine teardown leaves this.ws === ws (normal close) or null
        // (disconnect already nulled it) — both fall through.
        if (this.ws && this.ws !== ws) return;
        // MEASUREMENT ONLY. Stamp the FIRST close of an outage: a failed retry's
        // close would shrink downMs to the last leg, and its 1006 hides the cause.
        // The drift tick runs here too because stopHeartbeat() below can cancel the
        // overdue tick of a client waking from a freeze. Nothing below reads these.
        const stamped = this.lastCloseAt === null;
        if (stamped) {
          const closedAt = Date.now();
          this.lastCloseAt = closedAt;
          const observedCloseCode = (event as { code?: number })?.code;
          this.lastCloseCode = typeof observedCloseCode === "number" ? observedCloseCode : null;
          this.sawCloseEvent = !this.selfInitiatedClose;
          this.driftTracker.tick(closedAt);
        }
        this.selfInitiatedClose = false; // consumed by exactly one close
        if (this.userInitiatedClose) {
          this.userInitiatedClose = false;
          if (stamped) this.clearReconnectTelemetry();
        }
        if (this.lastCloseAt !== null) this.startSuspensionMeter();
        this.ws = null;
        this.authenticated = false;
        // The heartbeat is per-connection: stop it here so a torn-down socket
        // leaves no orphan timer; the next open() starts a fresh one.
        this.stopHeartbeat();
        // Reset the per-workspace seq baselines (#499): the next connection
        // re-seeds from its first live event, by which point handleReconnect's
        // drainSync has already caught history up — so the reconnect boundary
        // never reads as a spurious gap.
        this.broadcastSeqs.clear();
        // Same lifetime rule for the read coalescer (CYBORG-991): every entry in
        // there was sent on THIS socket, so none of them can ever be answered
        // now — they will only time out. Dropping them here (not just when the
        // next socket opens) means a caller arriving during the reconnect wait
        // gets a fresh request that parks in waitForOpen, instead of inheriting a
        // doomed promise that rejects on the dead socket's original deadline.
        this.inflightReads.clear();
        if (this.intentionalClose) {
          // MEASUREMENT ONLY — a logout/shutdown/endpoint-switch is not an
          // outage. Drop the stamp here or the NEXT connect() (a re-login hours
          // later) would open, see a non-null lastCloseAt and report the whole
          // idle time as downtime. Clearing state this handler just wrote;
          // touches nothing the teardown below depends on.
          this.clearReconnectTelemetry();
          this.cancelPendingReconnectReports();
          // Intentional close (logout/shutdown): fail parked RPCs now instead of
          // letting them hang for the full auth timeout.
          for (const w of this.authWaiters) w.reject(new Error(CLIENT_DISCONNECTED_ERROR));
          this.authWaiters.clear();
          return;
        }
        // The socket died under whatever was on the wire (CYBORG-1171). Nothing in
        // `pendingRequests` can be answered now — the same lifetime rule the read
        // coalescer above just applied — so fail them HERE, exactly as disconnect()
        // does, instead of letting each one sit out its full 15s budget and reject
        // with a timeout that blames the server for a dropped link. Before the
        // branches below, because all three of them (4003 silent re-auth, terminal
        // identity close, scheduleReconnect) leave the same dead requests behind.
        if ((event as { code?: number })?.code === WS_CLOSE_GOING_AWAY) {
          this.notePlannedRestart(0);
        }
        // Only a lost socket carries the connection_lost code; a deliberate disconnect does not.
        this.failPendingRequests(
          () =>
            Object.assign(new TransportError("connection_lost", CONNECTION_LOST_ERROR), {
              code: CONNECTION_LOST_CODE,
            }),
          true,
        );
        // A `4003` close is the relay rejecting our token (expired, or its JWT
        // secret rotated). Blindly reconnecting would loop forever against the
        // dead token (silent session death). Trade the stored refresh token for a
        // fresh one ONCE, then reconnect + re-auth transparently (CYBORG-87 — the
        // web/desktop mirror of the CLI's authenticateWithRefresh single retry).
        const code = (event as { code?: number })?.code;
        const closeReason =
          typeof (event as { reason?: unknown })?.reason === "string"
            ? (event as { reason?: string }).reason
            : undefined;
        if (code === 4003) {
          this.handleAuthRejection(closeReason);
          return;
        }
        // Terminal identity closes. The relay uses a NORMAL 1000 for these
        // (relay-standalone.ts: "account deleted" / "account deactivated"), so
        // without matching the REASON they are indistinguishable from a clean
        // disconnect and fall into the reconnect loop below — the app then spins on
        // "Reconnecting… this is taking longer than usual" forever against an
        // account that will never come back. Unlike a 4003 these are NOT
        // refreshable: a freshly minted token for a deleted account is rejected
        // just the same, so go straight to the terminal event and skip the
        // pointless refresh round-trip.
        if (code === 1000 && closeReason && TERMINAL_CLOSE_REASONS.has(closeReason)) {
          this.emit("auth_expired", {
            reason: closeReason,
            cause: "terminal_close",
          } as EventMap["auth_expired"]);
          return;
        }
        this.scheduleReconnect();
      });

      ws.addEventListener("message", (event) => {
        if (this.ws && this.ws !== ws) return; // ignore frames from a discarded socket
        this.handleMessage(event.data as string);
      });
    });
  }

  private handleMessage(raw: string): void {
    this.lastInboundAt = Date.now();
    let outer: { type?: string; message?: Record<string, unknown> };
    try {
      outer = JSON.parse(raw);
    } catch {
      return;
    }

    const msg =
      outer.type === "session" && outer.message
        ? outer.message
        : (outer as Record<string, unknown>);
    const type = msg.type as string | undefined;
    if (!type) return;

    const payload = (msg as { payload?: Record<string, unknown> }).payload;

    // guest_error is a top-level (non-session-wrapped) message with no
    // requestId — it can't reject a pending RPC, so route the HARD-PAUSE gate
    // to an event the app handles (open the activate modal).
    if (type === "guest_error") {
      this.handleGuestError(msg as { error?: string; message?: string; license?: LicensePayload });
      return;
    }

    normalizeFrameMessageText(type, payload);
    if (this.handleExtensionMessage(type, payload)) return;

    // Resolve pending RPC requests regardless of message type prefix
    if (payload && "requestId" in payload) {
      const pending = this.pendingRequests.get(payload.requestId as string);
      if (pending) {
        this.pendingRequests.delete(payload.requestId as string);
        clearTimeout(pending.timer);

        if (type === "cyborg:error" || type === "rpc_error") {
          // Double cast: the raw wire payload is Record<string, unknown>, which does
          // not structurally overlap the RpcErrorPayload interface, so go via unknown.
          const rpcErr = buildRpcError(payload as unknown as RpcErrorPayload);
          // Pricing v2: a gated RPC (create_schedule, enable_recipe, create_cybo, …)
          // rejects with UpgradeRequiredError. Emit globally so the upgrade modal
          // opens for EVERY gated op — most call sites have no local handler. The
          // reject still propagates for the few that do catch it (invite/create cybo).
          if (rpcErr instanceof UpgradeRequiredError) {
            this.emit("upgrade_required", { message: rpcErr.message, code: rpcErr.code });
          }
          pending.reject(withTraceId(rpcErr, pending.traceId));
        } else {
          pending.resolve(payload);
        }
        return;
      }
    }

    if (!type.startsWith("cyborg:")) return;

    this.dispatchBroadcast(type, payload);
  }

  // The gates the relay fires on a FIRE-AND-FORGET frame. Such a frame carries no
  // requestId, so unlike an RPC failure none of these can reject a pending promise
  // — each has to become an event the app listens for, or the frame is simply
  // swallowed. Split out of handleMessage (which was at its complexity ceiling)
  // when the rate-limit case was added; the three billing branches are unchanged.
  private handleGuestError(ge: {
    error?: string;
    message?: string;
    license?: LicensePayload;
  }): void {
    if (ge.error === "license_required") {
      this.emit("license_required", {
        message: ge.message ?? "Trial ended — activate your license.",
        license: ge.license,
      } as EventMap["license_required"]);
    } else if (ge.error === "pro_required" || ge.error === "seat_limit_reached") {
      // Pricing v2 upgrade gate on the fire-and-forget path — same routing as
      // license_required, but opens the upgrade prompt (not the paused paywall).
      this.emit("upgrade_required", {
        message: ge.message ?? "Upgrade to Pro to continue.",
        code: ge.error,
      } as EventMap["upgrade_required"]);
    } else if (ge.error === "rate limited") {
      // The relay's per-guest frame budget (CYBORG-866). Terminal input, typing,
      // drafts and mark_read all ride this path, and it used to match the three
      // billing codes above and return — so a throttled frame vanished with no
      // toast, no log, nothing, and the user's input was dropped in silence. Route
      // it to the generic `error` event, which both app shells already listen to.
      this.emit("error", {
        code: "rate_limited",
        message: ge.message ?? "Rate limited by the relay — some input was dropped.",
      } as EventMap["error"]);
    }
  }

  // Typed against the concrete base SlackEventMap: statics cannot reference the
  // class's EventMap type parameter, and every broadcast here is a base event
  // that any EventMap (which extends SlackEventMap) is guaranteed to carry.
  //
  // EXHAUSTIVENESS GAP (found live, review of #1655): the key type is
  // `Record<string, …>`, not a closed union — TypeScript enforces that every
  // VALUE here is a real SlackEventMap key (a typo in the value is a compile
  // error), but nothing enforces that every server-emitted `cyborg:*` wire
  // type gets a KEY here at all. `cyborg:page_access_revoked` was declared in
  // SlackEventMap, emitted by both the relay and dispatcher, and handled in
  // app.svelte.ts — but never added here, so it hit `event === undefined` at
  // dispatchBroadcast's map lookup below and was silently dropped, 100% of
  // the time, with `tsc` exit 0. See broadcast-map-coverage.test.ts for the
  // regression lock (a real inbound frame proving delivery) AND the
  // exhaustiveness check (every SlackEventMap key not on that file's curated
  // "not routed through this map" allowlist must appear as a value here).
  private static readonly BROADCAST_MAP: Record<string, keyof SlackEventMap & string> = {
    "cyborg:channel_message_broadcast": "channel_message",
    "cyborg:cybo_mention_notice": "cybo_mention_notice",
    "cyborg:cybo_mention_queued": "cybo_mention_queued",
    "cyborg:catchup_result": "catchup_result",
    "cyborg:dm_broadcast": "dm",
    "cyborg:typing_broadcast": "typing",
    // Both generations of the reaction frame; app.svelte.ts normalizes either shape.
    "cyborg:reaction_broadcast": "reaction",
    "cyborg:reaction_changed": "reaction",
    "cyborg:delete_message_broadcast": "delete_message",
    "cyborg:edit_message_broadcast": "edit_message",
    "cyborg:pin_message_broadcast": "pin_message",
    "cyborg:read_broadcast": "read",
    "cyborg:activity_new": "activity_new",
    "cyborg:activity_badge": "activity_badge",
    "cyborg:daemon_access_request_changed": "daemon_access_request_changed",
    "cyborg:cybo_transfer_request_changed": "cybo_transfer_request_changed",
    "cyborg:worker_invite_changed": "worker_invite_changed",
    "cyborg:workspace_membership_changed": "workspace_membership_changed",
    "cyborg:workspace_machines_changed": "workspace_machines_changed",
    "cyborg:external_agent_status_changed": "external_agent_status_changed",
    "cyborg:slack_import_progress": "slack_import_progress",
    "cyborg:page_access_request_changed": "page_access_request_changed",
    "cyborg:page_access_revoked": "page_access_revoked",
    "cyborg:join_request_changed": "join_request_changed",
    "cyborg:invite_request_changed": "invite_request_changed",
    "cyborg:notification_pref_changed": "notification_pref_changed",
    "cyborg:channel_archived_for_me_changed": "channel_archived_for_me_changed",
    "cyborg:dm_hidden_changed": "dm_hidden_changed",
    "cyborg:draft_changed": "draft_changed",
    "cyborg:members_updated": "members_updated",
    "cyborg:workspace_updated": "workspace_updated",
    "cyborg:tasks_changed": "tasks_changed",
    "cyborg:task_activity_changed": "task_activity_changed",
    "cyborg:pages_changed": "pages_changed",
    "cyborg:page_agent_run": "page_agent_run",
    "cyborg:daemon_status_broadcast": "daemon_status",
    "cyborg:channel_created_broadcast": "channel_created",
    "cyborg:group_dm_created_broadcast": "group_dm_created",
    "cyborg:channel_updated_broadcast": "channel_updated",
    "cyborg:channel_project_changed_broadcast": "channel_project_changed",
    "cyborg:channel_deleted_broadcast": "channel_deleted",
    "cyborg:channel_member_added_broadcast": "channel_member_added",
    "cyborg:channel_member_removed_broadcast": "channel_member_removed",
    "cyborg:channel_cybo_added_broadcast": "channel_cybo_added",
    "cyborg:channel_cybo_removed_broadcast": "channel_cybo_removed",
    "cyborg:cybo_updated_broadcast": "cybo_updated",
    "cyborg:cybo_created_broadcast": "cybo_created",
    "cyborg:cybo_deleted_broadcast": "cybo_deleted",
    "cyborg:thread_updated": "thread_updated",
    "cyborg:thread_read_changed": "thread_read_changed",
    "cyborg:thread_follow_changed": "thread_follow_changed",
    // #607: the payload IS the updated ScheduledMessage row, so the default
    // dispatch path (emit payload as-is) carries it straight to subscribers.
    "cyborg:schedule_message_failed": "schedule_message_failed",
    // #619: a schedule mutation fanned out to all clients (no requestId on the
    // broadcast copy; the requesting client's ack is resolved by requestId first).
    "cyborg:schedule_mutated": "schedule_mutated",
    "cyborg:server_shutdown": "server_shutdown",
    // Browser live view (knowledge/95 Phase 4). Frames ride the ordinary
    // broadcast path rather than the terminal fan-out: the ack-delayed
    // screencast is 2-5 fps, not the per-keystroke firehose terminal_output is,
    // so it does not need its own untyped channel.
    "cyborg:browse_frame": "browse_frame",
    "cyborg:browse_session_ended": "browse_session_ended",
    "cyborg:browser_sessions_changed": "browser_sessions_changed",
    "cyborg:browser_session_state": "browser_session_state",
    "cyborg:error": "error",
    "cyborg:message_queued": "message_queued",
  };

  // Track the highest broadcast seq per workspace and emit `seq_gap` on a forward
  // jump (#499). A real loss (Redis pub/sub hiccup, flaky link on an open socket)
  // and a benign hole (a DM between OTHER users consuming a seq we never receive)
  // are indistinguishable here, so this only SIGNALS; the app's handler coalesces
  // the recovery. seq < expected is a duplicate/stale event (already have it).
  private observeBroadcastSeq(payload: Record<string, unknown>): void {
    const seq = payload.seq;
    const workspaceId = payload.workspaceId;
    if (typeof seq !== "number" || typeof workspaceId !== "string") return;
    const prev = this.broadcastSeqs.get(workspaceId);
    if (prev === undefined) {
      // First seq-carrying event for this workspace on this connection: seed the
      // baseline (history was already established by the initial fetch / sync).
      this.broadcastSeqs.set(workspaceId, seq);
      this.emit("broadcast_seq", { workspaceId, seq });
      return;
    }
    if (seq <= prev) return; // duplicate / out-of-order — already accounted for
    if (seq > prev + 1) {
      // Hole: we expected prev+1. Hand the app the cursor to catch up FROM.
      this.emit(
        "seq_gap" as keyof EventMap & string,
        {
          workspaceId,
          fromSeq: prev,
          toSeq: seq,
        } as EventMap[keyof EventMap & string],
      );
    }
    this.broadcastSeqs.set(workspaceId, seq);
    this.emit("broadcast_seq", { workspaceId, seq });
  }

  private notePlannedRestart(etaMs: number): void {
    this.plannedRestart = {
      socketGen: this.socketGen,
      etaMs,
      until: Date.now() + etaMs + PLANNED_RESTART_GRACE_MS,
    };
  }

  private dispatchBroadcast(type: string, payload: Record<string, unknown> | undefined): void {
    // Every broadcast carries its data in `payload`. A broadcast that arrives
    // without it is malformed (e.g. a server type not yet migrated to the
    // payload envelope) — drop it instead of dereferencing undefined, which
    // used to throw an uncaught TypeError in the message handler.
    if (!payload) return;
    // Seq-gap detection (#499) runs FIRST so it sees EVERY seq-carrying broadcast
    // uniformly (channel messages + DMs share one per-workspace counter), before
    // any type-specific early-return below. Backward-compatible: a payload with no
    // numeric seq (older relay, or a non-sequenced event) is simply not tracked.
    this.observeBroadcastSeq(payload);
    if (type === "cyborg:server_shutdown" && payload.reason === "deploy") {
      const eta = typeof payload.etaMs === "number" ? payload.etaMs : 0;
      this.notePlannedRestart(eta);
      this.hintedRedialMs = hintedReconnectDelayMs(payload);
      const mark = payload.restartMark;
      this.restartMark =
        typeof mark === "string" && mark.length <= MAX_RESTART_MARK_LEN
          ? { mark, socketGen: this.socketGen }
          : undefined;
    }
    if (type === "cyborg:desktop_update_available") {
      this.triggerDesktopUpdateCheck();
      return;
    }
    if (type === "cyborg:tasks_changed") {
      // The broadcast `task` carries the same relay `mapTask` readback as the
      // RPC responses — Plane fields in snake_case. Normalize it to the camelCase
      // `Task` model so live-update subscribers match the fetch path. A "deleted"
      // op (or a recurrence-spawned partial) sends only `{ id }`; mapRawTask is
      // tolerant of the missing fields (they default to null/[]).
      const tp = payload as { workspaceId: string; op: string; task: RawTask };
      this.emit(
        "tasks_changed" as keyof EventMap & string,
        {
          ...tp,
          task: mapRawTask(tp.task),
        } as EventMap[keyof EventMap & string],
      );
      return;
    }

    const event = SlackClient.BROADCAST_MAP[type];
    if (event && payload) {
      // EventMap extends SlackEventMap, so every base broadcast key is a key of
      // EventMap — TS can't prove that for a generic parameter, hence the cast.
      this.emit(event as keyof EventMap & string, payload as EventMap[keyof EventMap & string]);
    }
  }

  protected emit<K extends keyof EventMap & string>(event: K, data: EventMap[K]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const handler of set) {
      try {
        handler(data);
      } catch (err) {
        // Listener errors still don't crash the client — but they are not free.
        // Every one of these is a frame the client RECEIVED and then dropped on
        // the floor, which on screen is indistinguishable from the server never
        // having sent it (the "it notified me but the reply never appeared"
        // class). Swallowing it silently is what made that unfindable.
        reportUiFailure("client.listener_threw", err, { event: String(event) });
      }
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit("connection", { status: "disconnected" } as EventMap["connection"]);
      return;
    }

    // Full jitter over the capped-exponential envelope — de-synchronizes the
    // mass reconnect after a relay deploy (#504). The banner reads `delayMs`
    // below, so it shows the actual (jittered) wait, not the envelope.
    const delay = this.hintedRedialMs ?? reconnectBackoffMs(this.reconnectAttempts);
    this.hintedRedialMs = undefined;
    // 1-based, human-facing attempt counter for the reconnect banner.
    const attempt = this.reconnectAttempts + 1;
    this.emit("connection", {
      status: "reconnecting",
      attempt,
      delayMs: delay,
    } as EventMap["connection"]);
    this.reconnectAttempts++;
    // A second look at twice the threshold files a fault when a "planned" restart never ended.
    if (
      this.reconnectAttempts === SlackClient.RECONNECT_STORM_ATTEMPTS ||
      this.reconnectAttempts === SlackClient.RECONNECT_STORM_ATTEMPTS * 2
    )
      this.reportReconnectStorm();

    this.reconnectTimer = setTimeout(() => {
      // intentional: a failed reconnect re-schedules via the backoff loop + emits "reconnecting"/"disconnected" events the UI shows.
      this.doConnect().catch(() => {});
    }, delay);
  }

  // The `/client-reconnect` beacon only fires on success: a storm that never heals is
  // reported once, at the threshold (attempts reset only on success), throttled across
  // episodes. A device that knows it is offline is skipped: not a fault, and no beacon.
  private static readonly RECONNECT_STORM_ATTEMPTS = 8;
  private static readonly RECONNECT_STORM_REPORT_INTERVAL_MS = 30 * 60_000;

  // Planned only while the ACTUAL downtime is within the announced ETA plus grace.
  private stormIsPlanned(downMs: number | undefined): boolean {
    const planned = this.plannedRestart;
    return planned !== null && (downMs ?? 0) <= planned.etaMs + PLANNED_RESTART_GRACE_MS;
  }

  private reportReconnectStorm(): void {
    try {
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      const downMs = this.lastCloseAt === null ? undefined : Date.now() - this.lastCloseAt;
      // A hidden tab's sockets and timers are throttled by the browser: its storm is the
      // environment, as a planned restart's is; a visible page's is the fault.
      const hidden = typeof document !== "undefined" && document.hidden === true;
      const kind = this.stormIsPlanned(downMs) || hidden ? "transient" : "fault";
      // One throttle slot per kind, so a later fault is not hidden by an earlier transient.
      if (
        shouldReport(
          `client.reconnect_storm.${kind}`,
          SlackClient.RECONNECT_STORM_REPORT_INTERVAL_MS,
        )
      ) {
        reportUiFailure(
          "client.reconnect_storm",
          new Error("reconnect not recovering"),
          {
            attempts: this.reconnectAttempts,
            closeCode: this.lastCloseCode,
            downMs,
            hidden,
            surface: this.clientSurface(),
          },
          {
            failureKind: kind,
            code: "reconnect_storm",
          },
        );
      }
    } catch {
      // intentional: telemetry must never break the reconnect loop it describes
    }
  }

  // Single-shot silent re-auth after a `4003` rejection (CYBORG-87). Trades the
  // stored refresh token for a fresh access token, then reconnects with it. Never
  // loops: `reauthing` gates a second attempt within the same episode, so if the
  // freshly-minted token is ALSO rejected we surface `auth_expired` (re-login)
  // instead of refreshing again — mirroring the CLI's exactly-once retry.
  private handleAuthRejection(reason?: string): void {
    if (this.intentionalClose) return;
    const reauth = this.reauth;
    // No refresh capability, or we already refreshed once this episode and the
    // NEW token was rejected too → a real problem the user must resolve.
    if (!reauth || this.reauthing) {
      this.emit("auth_expired", { reason, cause: "rejected" } as EventMap["auth_expired"]);
      return;
    }
    this.reauthing = true;
    void this.runReauth(reauth, reason);
  }

  private async runReauth(
    reauth: (wsUrl: string) => Promise<RefreshOutcome>,
    reason?: string,
  ): Promise<void> {
    let outcome: RefreshOutcome;
    try {
      outcome = await reauth(this.url);
    } catch {
      // Defensive: a throwing reauth hook is treated as transient, and is held to
      // the same budget as one that reports the failure properly.
      outcome = { status: "error" };
    }
    if (this.intentionalClose) return;
    if (outcome.status === "refreshed") {
      // Reconnect with the fresh token. `reauthing` stays true through the
      // reconnect (reset only once auth SUCCEEDS), so a re-rejection of THIS
      // token can't trigger a second refresh — exactly one per episode.
      this.token = outcome.token;
      this.authTokenRevision += 1;
      this.reconnectAttempts = 0;
      // intentional: a failed reconnect re-schedules via the backoff loop + emits "reconnecting"/"disconnected" events the UI shows.
      this.doConnect().catch(() => {});
      return;
    }
    if (outcome.status === "error") {
      // Transient (network / timeout / 5xx / 429): keep the token and let the
      // normal backoff retry — a later 4003 re-triggers a fresh attempt. Bounded,
      // because "transient" that never ends is indistinguishable from a dead
      // session to the person watching the spinner: once the run has failed for
      // longer than the budget, fall through to auth_expired below.
      this.reauthFailingSince ??= Date.now();
      if (Date.now() - this.reauthFailingSince < SlackClient.REAUTH_TRANSIENT_BUDGET_MS) {
        this.reauthing = false;
        this.scheduleReconnect();
        return;
      }
    }
    // expired: no / dead refresh token → actionable re-login, stop the loop.
    // Also the exhausted-budget case above: the token is NOT cleared here (that is
    // refreshAccessToken's call on a 401/403), the session just stops pretending
    // it can heal itself and hands the user the login screen.
    this.emit("auth_expired", {
      reason,
      cause: outcome.status === "error" ? "reauth_budget_exhausted" : "refresh_expired",
    } as EventMap["auth_expired"]);
  }
}
